import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Calculator,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Users,
  FileText,
  Settings,
  Download,
  Upload,
  Eye,
  Edit,
  Trash2,
  Clock,
  Shield,
  TrendingUp,
  BarChart3,
  Filter,
  Plus,
  RefreshCw,
  CreditCard,
  Timer,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  ExternalLink,
  Lock,
  Building2,
  Search,
  X,
  Save,
  Edit2,
  UserMinus,
  MinusCircle,
  Award,
  FileSpreadsheet,
  ChevronsUp,
  ChevronsDown,
  Landmark
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api, { confidentialAPI, configAPI } from '../services/api';
import { useSearchParams } from 'react-router-dom';
import { useTabPersistence, usePersistence } from '../hooks/usePersistence';
import PayrollPeriodForm from '../components/Forms/PayrollPeriodForm';
import EmployeePayrollBreakdown from '../components/Payroll/EmployeePayrollBreakdown';
import PayrollLockButton from '../components/Payroll/PayrollLockButton';
import PayrollAdjustmentModal from '../components/Payroll/PayrollAdjustmentModal';
import PayrollPaymentStep from '../components/Payroll/PayrollPaymentStep';
import ReviewFlagsModal from '../components/Payroll/ReviewFlagsModal';
import { AdjustAndLockTable } from '../components/Payroll/AdjustAndLockTable';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import { StatusBadge } from '../components/UI/StatusBadge';
import { CountBadge } from '../components/UI/CountBadge';
import ProgressModal from '../components/UI/ProgressModal';
import PillToggle from '../components/UI/PillToggle';
import Portal from '../components/UI/Portal';
import { 
  exportPayrollSummaryPDF,
  exportMOMComplianceReportPDF,
  exportEmployeePayrollExcel,
  exportPayrollStatisticsExcel,
  exportCPFSubmissionFile
} from '../utils/exportUtils';
import { exportMasterListPDF } from '../utils/masterListExport';
import { exportStaffRegisterPDF } from '../utils/staffPayrollRegisterExport';
import { exportFinalSettlementPDF } from '../utils/finalSettlementExport';
import toast from '../utils/toast';
import { formatDate, formatDateRange, toLocalISODate, parseLocalDate, getTodayInSingaporeTimezone, formatDateForInput } from '../utils/dateUtils';
import { DaysUntilBadge } from '../components/UI/DaysUntilBadge';
import { pluralizeDays, formatLeaveDays } from '../utils/numberUtils';
import { 
  usePayrollPeriods, 
  usePayrollRuns, 
  usePayrollStatistics, 
  useComplianceReports, 
  useCreatePayrollPeriod,
  useProcessPayroll,
  useRunPayrollCalculation,
  usePayrollCalculationStatus,
  useAdjustPayroll,
  usePreviewAdjustment,
  DEDUCTION_ADJUSTMENT_TYPES,
  useEmployeePayrolls,
  useEmployeePayrollDetail,
  useDepartmentProgress,
  usePayrollFlags,
  usePayrollFlagSummary,
  useStep2Reconciliation,
  useReviewFlag,
  useExcludeFromRun,
  useBulkReviewFlags,
  useAutoVerifyFlags,
  useFinalizePayroll,
  useBulkFinalizePayrolls,
  useBulkFinalizeRun,
  useUnfinalizePayroll
} from '../hooks';
import { useAllowanceTypes, useDepartments, useTradeCategories } from '../hooks/useConfigData';
import { useClickOutside } from '../hooks/useClickOutside';
import { usePayrollReadiness, useScanExceptions } from '../hooks/usePayrollReadiness';
import ConfirmModal from '../components/UI/ConfirmModal';
import { ClassificationBadge } from '../components/UI/ClassificationBadge';
import PillSelect from '../components/UI/PillSelect';
import FormSelect from '../components/UI/FormSelect';
import MultiPillSelect from '../components/UI/MultiPillSelect';
import DatePickerField from '../components/UI/DatePickerField';
import { useAdjustAndLock, isEmployeeFullyLocked, canLockEmployee } from '../hooks/useAdjustAndLock';
import DeductionManagementTab from '../components/Payroll/DeductionManagementTab';
import { FilterButton } from '../components/UI/FilterButton';
import { InfoBadge } from '../components/UI/InfoBadge';
import { TradeBadge } from '../components/UI/TradeBadge';
import { DepartmentTradeChips } from '../components/UI/DepartmentTradeChips';
import AccordionSection from '../components/UI/AccordionSection';
import {
  getDepartmentIcon,
  getDepartmentColor,
} from '../config/departmentConfig';
import { isTradeRedundantWithDepartment } from '../config/tradeConfig';
import { useStaffList } from '../hooks/useStaffData';



const PayrollManagement = () => {
  const { user } = useAuth();
  
  // Tab persistence
  const validTabs = ['processing', 'final-settlements', 'overtime', 'allowances', 'deductions', 'compliance', 'payment-compliance', 'reports', 'settings'];
  const [activeTab, setActiveTab] = useTabPersistence('payroll-management', 'processing', validTabs);
  const [searchParams, setSearchParams] = useSearchParams();

  // Clear stale 'step' param when on tabs that don't use steppers
  useEffect(() => {
    const tabsWithSteppers = ['processing', 'final-settlements'];
    if (!tabsWithSteppers.includes(activeTab) && searchParams.has('step')) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('step');
          return next;
        },
        { replace: true }
      );
    }
  }, [activeTab, searchParams, setSearchParams]);
  
  // React Query hooks — only fetch data needed at the parent level.
  // Tab-specific hooks (statistics, complianceReports) are fetched inside their
  // respective tab components so they only fire when the user navigates there.
  const { data: payrollPeriods = [], isLoading: periodsLoading } = usePayrollPeriods();
  const { data: payrollRuns = [], isLoading: runsLoading } = usePayrollRuns();
  const { data: allowanceTypes = [], isLoading: allowancesLoading } = useAllowanceTypes();
  const { data: departments = [], isLoading: departmentsLoading } = useDepartments();

  const loading = periodsLoading || runsLoading || allowancesLoading || departmentsLoading;
  
  // Other component state
  const [employeePayrolls, setEmployeePayrolls] = useState([]);
  const [paymentDeadlines, setPaymentDeadlines] = useState([]);
  const [complianceDashboard, setComplianceDashboard] = useState({});
  const [fsBadgeCount, setFsBadgeCount] = useState(null);
  
  // Form states
  const [showNewPeriodForm, setShowNewPeriodForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState([]); // [] = all departments
  const [processingStatus, setProcessingStatus] = useState('idle');

  const tabs = [
    // Only regular runs awaiting review belong on this tab. Final-settlement
    // runs have their own tab + badge (fsBadgeCount), so counting them here
    // double-surfaces them and inflates the count.
    { id: 'processing', name: 'Payroll Processing', icon: Calculator, badge: payrollRuns.filter(r => r.status === 'review' && r.run_type === 'regular').length || null },
    { id: 'final-settlements', name: 'Final Settlements', icon: UserMinus, badge: fsBadgeCount },
    { id: 'overtime', name: 'Overtime Management', icon: Clock, badge: null },
    { id: 'allowances', name: 'Allowance Management', icon: DollarSign, badge: null },
    { id: 'deductions', name: 'Deduction Management', icon: MinusCircle, badge: null },
    { id: 'compliance', name: 'MOM Compliance', icon: Shield, badge: null },
    { id: 'payment-compliance', name: 'Payment Compliance', icon: Timer, badge: complianceDashboard.overdue_payments || null },
    { id: 'reports', name: 'Reports & Analytics', icon: BarChart3, badge: null },
    { id: 'settings', name: 'Payroll Settings', icon: Settings, badge: null }
  ];

  // React Query mutations
  const createPayrollPeriodMutation = useCreatePayrollPeriod();
  const processPayrollMutation = useProcessPayroll();
  
  // Data loading handled automatically by React Query hooks











  const fetchPaymentComplianceData = async () => {
    try {
      // Fetch payment compliance dashboard data
      const dashboardResponse = await api.get('/payment-deadlines/dashboard/');
      setComplianceDashboard(dashboardResponse.data || {});
      
      // Fetch recent payment deadlines
      const deadlinesResponse = await api.get('/payment-deadlines/?ordering=payment_due_date&limit=50');
      setPaymentDeadlines(deadlinesResponse.data.results || []);
    } catch (error) {
      console.error('Error fetching payment compliance data:', error);
      // Set default values if API fails
      setComplianceDashboard({
        total_pending_payments: 0,
        overdue_payments: 0,
        approaching_deadlines: 0,
        compliance_rate: 100,
        recent_overdue: [],
        upcoming_deadlines: []
      });
      setPaymentDeadlines([]);
    }
  };

  const handleCreatePayrollPeriod = (formData) => {
    createPayrollPeriodMutation.mutate(formData, {
      onSuccess: () => {
        setShowNewPeriodForm(false);
      }
    });
  };

  const handleRunPayroll = (payrollRunId) => {
    setProcessingStatus('running');
    // Note: The backend expects payroll_run_id for processing payroll
    // This should be called with the first_run_id from the period object
    processPayrollMutation.mutate({ payroll_run_id: payrollRunId }, {
      onSuccess: () => {
        setProcessingStatus('completed');
      },
      onError: () => {
        setProcessingStatus('error');
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: 'SGD'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      processing: 'bg-blue-100 text-blue-800',
      review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      paid: 'bg-indigo-100 text-indigo-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getComplianceScoreColor = (score) => {
    if (score >= 10) return 'text-green-600';
    if (score >= 7) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Header Skeleton */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded animate-pulse"></div>
            <div className="space-y-2">
              <div className="h-9 bg-gray-200 rounded w-64 animate-pulse"></div>
              <div className="h-5 bg-gray-200 rounded w-96 animate-pulse"></div>
          </div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="flex items-center gap-2 flex-wrap mb-8">
          {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
          ))}
        </div>

        {/* Content Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg p-6 space-y-6">
          <div className="space-y-4">
            <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 space-y-3 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-32"></div>
                  <div className="h-3 bg-gray-200 rounded w-20"></div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="h-6 bg-gray-200 rounded w-40 animate-pulse"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
        <div>
        <div className="flex items-center gap-3 mb-1 min-w-0">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Payroll Management</h1>
          </div>
        <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Singapore MOM compliant payroll processing and management</p>
      </div>

      {/* Navigation Tabs - Pill Style */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${isActive ? 'active' : ''}`}
              title={tab.name}
            >
                    <Icon className="w-4 h-4" />
                  {tab.name}
                  {!!tab.badge && tab.badge > 0 && (
                <CountBadge count={tab.badge} theme="admin" active={isActive} className="ml-2" />
                  )}
                </button>
              );
            })}
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {renderTabContent()}
      </div>

      {/* New Payroll Period Modal */}
      <PayrollPeriodForm
        isOpen={showNewPeriodForm}
        onClose={() => setShowNewPeriodForm(false)}
        onSuccess={(newPeriod) => {
          // Period will be added automatically by React Query refetch
        }}
      />
    </div>
  );

  function renderTabContent() {
    switch (activeTab) {
      case 'processing':
        return <PayrollProcessingTab
          payrollPeriods={payrollPeriods}
          payrollRuns={payrollRuns}
          onRunPayroll={handleRunPayroll}
          processingStatus={processingStatus}
          formatCurrency={formatCurrency}
          getStatusColor={getStatusColor}
          setShowNewPeriodForm={setShowNewPeriodForm}
          selectedDepartments={selectedDepartments}
          setSelectedDepartments={setSelectedDepartments}
          departments={departments}
          setActiveTab={setActiveTab}
        />;
      
      case 'final-settlements':
        return <FinalSettlementsTab 
          formatCurrency={formatCurrency}
          onCountChange={setFsBadgeCount}
        />;
      
      case 'overtime':
        return <OvertimeManagementTab />;
      
      case 'allowances':
        return <AllowanceManagementTab />;
      
      case 'deductions':
        return <DeductionManagementTab />;
      
      case 'compliance':
        return <MOMComplianceTab
          getComplianceScoreColor={getComplianceScoreColor}
        />;
      
      case 'payment-compliance':
        return <PaymentComplianceTab 
          complianceDashboard={complianceDashboard}
          paymentDeadlines={paymentDeadlines}
          onRefresh={fetchPaymentComplianceData}
          formatCurrency={formatCurrency}
        />;
      
      case 'reports':
        return <ReportsAnalyticsTab
          formatCurrency={formatCurrency}
          payrollRuns={payrollRuns}
        />;
      
      case 'settings':
        return <PayrollSettingsTab />;
      
      default:
        return null;
    }
  }
};

// Tab Components
const PayrollProcessingTab = ({ payrollPeriods = [], payrollRuns, onRunPayroll, processingStatus, formatCurrency, getStatusColor, setShowNewPeriodForm, selectedDepartments, setSelectedDepartments, departments, setActiveTab }) => {
  const { user } = useAuth();
  const departmentOptions = useMemo(() =>
    departments.map(d => ({ value: d.id, label: d.department_name })),
    [departments]
  );
  
  // === Payroll Calculation Hooks ===
  const runPayrollMutation = useRunPayrollCalculation();
  const createPeriodMutation = useCreatePayrollPeriod();
  const [calculationTaskId, setCalculationTaskId] = useState(null);
  const [currentPayrollRunId, setCurrentPayrollRunId] = useState(null);
  const [showCalcModal, setShowCalcModal] = useState(false);

  const CALC_PHASES = useMemo(() => [
    { label: 'Loading employee data' },
    { label: 'Calculating payroll' },
    { label: 'Finalizing results' },
  ], []);
  
  // Poll for calculation status when task is running
  const { data: calculationStatus } = usePayrollCalculationStatus(
    currentPayrollRunId,
    calculationTaskId,
    !!calculationTaskId // Only poll when we have a task ID
  );

  // Derive calc modal props from calculationStatus
  const calcModalProps = useMemo(() => {
    if (!calculationStatus) return { currentPhase: 0, progress: null, statusMessage: 'Initializing…', error: null };
    const s = calculationStatus;
    if (s.state === 'FAILURE') {
      return { currentPhase: 1, progress: null, statusMessage: '', error: s.error || 'Payroll calculation failed' };
    }
    if (s.state === 'SUCCESS') {
      const r = s.result || {};
      return {
        currentPhase: 3,
        progress: null,
        statusMessage: `${r.success_count || 0} processed${r.error_count > 0 ? `, ${r.error_count} errors` : ''} — ${r.duration_seconds || 0}s`,
        error: null,
      };
    }
    const status = s.progress?.status || s.status || 'prefetching';
    const phase = status === 'prefetching' ? 0 : 1;
    return {
      currentPhase: phase,
      progress: s.progress?.total > 0 ? { current: s.progress.current, total: s.progress.total } : null,
      statusMessage: s.progress?.message || s.message || 'Processing…',
      error: null,
    };
  }, [calculationStatus]);

  // Auto-close modal on SUCCESS after 1.5s
  useEffect(() => {
    if (calculationStatus?.state === 'SUCCESS' && showCalcModal) {
      const timer = setTimeout(() => setShowCalcModal(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [calculationStatus?.state, showCalcModal]);

  // === Payroll Adjustment Hooks (Step 3) ===
  const adjustPayrollMutation = useAdjustPayroll();
  const previewAdjustmentMutation = usePreviewAdjustment();
  
  // Department progress tracking (must be declared before shouldFetchEmployeePayrolls)
  const { data: departmentProgress, refetch: refetchDepartmentProgress } = useDepartmentProgress(
    currentPayrollRunId,
    !!currentPayrollRunId
  );
  
  // Fetch employee payrolls whenever we have a payroll run ID.
  // The query itself is cheap (paginated), and gating on Celery task state
  // causes the payroll register to vanish on page refresh / navigation.
  const shouldFetchEmployeePayrolls = !!currentPayrollRunId;
  const { data: employeePayrolls = [], refetch: refetchEmployeePayrolls } = useEmployeePayrolls(
    currentPayrollRunId,
    shouldFetchEmployeePayrolls
  );
  
  // === Payroll Flags (Exception-Based Review) ===
  const {
    data: flagSummary,
    isLoading: flagSummaryLoading,
    isError: flagSummaryError,
    refetch: refetchFlagSummary,
  } = usePayrollFlagSummary(
    currentPayrollRunId,
    !!currentPayrollRunId && departmentProgress?.summary?.is_fully_calculated
  );
  const { data: flagsData, refetch: refetchFlags } = usePayrollFlags(
    currentPayrollRunId,
    { 
      groupBySeverity: true, 
      enabled: !!currentPayrollRunId && departmentProgress?.summary?.is_fully_calculated 
    }
  );
  // Live Step 2 ↔ Step 3 reconciliation. Surfaces drift between what
  // Step 2 (AR / leave sessions) shows and what Step 3 saved on each
  // EmployeePayroll row. The endpoint also syncs persistent
  // STEP2_DRIFT PayrollFlag rows so the existing can_proceed gate
  // already blocks Step 4 — the banner below is the affordance HR
  // uses to triage and resolve the drift.
  const { data: step2Reconciliation, refetch: refetchStep2Reconciliation } = useStep2Reconciliation(
    currentPayrollRunId,
    !!currentPayrollRunId && departmentProgress?.summary?.is_fully_calculated
  );
  const reviewFlagMutation = useReviewFlag();
  const excludeFromRunMutation = useExcludeFromRun();
  const bulkReviewMutation = useBulkReviewFlags();
  const autoVerifyMutation = useAutoVerifyFlags();
  
  // === Payroll Finalization Hooks (Step 3) ===
  const finalizePayrollMutation = useFinalizePayroll();
  const unfinalizePayrollMutation = useUnfinalizePayroll();
  const bulkFinalizeMutation = useBulkFinalizePayrolls();
  const bulkFinalizeRunMutation = useBulkFinalizeRun();
  const [showLockAllModal, setShowLockAllModal] = useState(false);
  const [showReviewAllModal, setShowReviewAllModal] = useState(false);
  const [showRegUnfinalizeModal, setShowRegUnfinalizeModal] = useState(false);
  const [regUnfinalizeReason, setRegUnfinalizeReason] = useState('');

  const [masterListExporting, setMasterListExporting] = useState(false);
  const handleMasterListExportXLSX = async () => {
    if (!currentPayrollRunId) return;
    setMasterListExporting(true);
    try {
      const res = await api.get(
        `/confidential/payroll-runs/${currentPayrollRunId}/master-list-export/`,
        { responseType: 'blob', timeout: 120000 }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : 'Worker_Payroll_Register.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Worker Payroll Register exported');
    } catch (err) {
      let msg = 'Failed to export Worker Payroll Register';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = 'Export timed out — the server is still generating the file. Please try again in a moment.';
      } else {
        const data = err.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            if (parsed?.error) msg = parsed.error;
          } catch (_) { /* blob not JSON */ }
        } else if (typeof data?.error === 'string') {
          msg = data.error;
        }
      }
      toast.error(msg);
    } finally {
      setMasterListExporting(false);
    }
  };

  const [masterListPdfExporting, setMasterListPdfExporting] = useState(false);
  const handleMasterListExportPDF = async () => {
    if (!currentPayrollRunId) return;
    setMasterListPdfExporting(true);
    try {
      const res = await api.get(
        `/confidential/payroll-runs/${currentPayrollRunId}/worker-register-data/`,
        { timeout: 120000 }
      );
      await exportMasterListPDF(
        res.data.rows,
        {
          period_name: res.data.period_name,
          period_start_date: res.data.period_start_date,
          period_end_date: res.data.period_end_date,
          status: res.data.status,
        },
        res.data.columns,
        res.data.cover_summary,
      );
    } catch (err) {
      let msg = 'Failed to generate PDF';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = 'PDF data timed out — please try again in a moment.';
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      toast.error(msg);
      console.error('Worker Payroll Register PDF export error:', err);
    } finally {
      setMasterListPdfExporting(false);
    }
  };

  const [staffRegisterExporting, setStaffRegisterExporting] = useState(false);
  const handleStaffRegisterExportXLSX = async () => {
    if (!currentPayrollRunId) return;
    setStaffRegisterExporting(true);
    try {
      const res = await api.get(
        `/confidential/payroll-runs/${currentPayrollRunId}/staff-register-export/`,
        { responseType: 'blob', timeout: 120000 }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : 'Staff_Payroll_Register.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Staff Payroll Register exported');
    } catch (err) {
      let msg = 'Failed to export Staff Payroll Register';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = 'Export timed out — the server is still generating the file. Please try again in a moment.';
      } else {
        const data = err.response?.data;
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            if (parsed?.error) msg = parsed.error;
          } catch (_) { /* blob not JSON */ }
        } else if (typeof data?.error === 'string') {
          msg = data.error;
        }
      }
      toast.error(msg);
    } finally {
      setStaffRegisterExporting(false);
    }
  };

  const [staffRegisterPdfExporting, setStaffRegisterPdfExporting] = useState(false);
  const handleStaffRegisterExportPDF = async () => {
    if (!currentPayrollRunId) return;
    setStaffRegisterPdfExporting(true);
    try {
      const res = await api.get(
        `/confidential/payroll-runs/${currentPayrollRunId}/staff-register-data/`,
        { timeout: 120000 }
      );
      await exportStaffRegisterPDF(
        res.data.rows,
        {
          period_name: res.data.period_name,
          period_start_date: res.data.period_start_date,
          period_end_date: res.data.period_end_date,
          status: res.data.status,
        },
        res.data.columns,
        res.data.cover_summary,
      );
    } catch (err) {
      let msg = 'Failed to generate Staff PDF';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = 'PDF data timed out — please try again in a moment.';
      } else if (err.response?.data?.error) {
        msg = err.response.data.error;
      }
      toast.error(msg);
      console.error('Staff Payroll Register PDF export error:', err);
    } finally {
      setStaffRegisterPdfExporting(false);
    }
  };

  // === Step 3 Master-Detail State (must be declared before useMemo hooks that use them) ===
  const [selectedPayrollDepartmentId, setSelectedPayrollDepartmentId] = useState(null);
  const [selectedEmployeePayrollId, setSelectedEmployeePayrollId] = useState(null);
  const [expandedPayrollDepartments, setExpandedPayrollDepartments] = useState(new Set());
  const [payrollFilter, setPayrollFilter] = useState('all'); // 'all' | 'staff' | 'worker'
  const [payrollDeptFilter, setPayrollDeptFilter] = useState(null); // null = all departments, dept_id = specific
  const [payrollSearchTerm, setPayrollSearchTerm] = useState('');
  const [selectedPayrollIds, setSelectedPayrollIds] = useState(new Set()); // For bulk selection
  const [recalculatingStaffId, setRecalculatingStaffId] = useState(null); // Step 3 detail-panel Recalc spinner
  const [detailAccordions, setDetailAccordions] = useState({ rates: false, deductions: false, ytd: false, awCeiling: false });

  // === Step 3 Master-Detail Computed Data ===
  // Group employee payrolls by department.
  // Includes pending departments (expected_count > 0) even when no payrolls have
  // been calculated yet, so the register table can surface them with a per-dept
  // Run button (single canonical surface — replaces the standalone Department
  // Progress widget).
  const payrollsByDepartment = useMemo(() => {
    if (!departmentProgress?.departments) return {};

    const payrolls = employeePayrolls || [];
    const grouped = {};
    const assignedEmployeeIds = new Set();
    
    // First pass: assign employees to their departments
    departmentProgress.departments.forEach(dept => {
      const deptEmployees = payrolls.filter(p => {
        // Try multiple fields for department matching
        const empDeptId = p.employee_department_id || p.department_at_calculation || p.department_id;
        return empDeptId === dept.department_id;
      });
      
      // Track which employees have been assigned
      deptEmployees.forEach(p => assignedEmployeeIds.add(p.id));
      
      if (dept.expected_count > 0 || deptEmployees.length > 0) {
        grouped[dept.department_id] = {
          ...dept,
          employees: deptEmployees
        };
      }
    });
    
    // Second pass: place unassigned employees into their actual department
    // by matching employee_department_name against departmentProgress names
    const unassignedEmployees = payrolls.filter(p => !assignedEmployeeIds.has(p.id));
    if (unassignedEmployees.length > 0) {
      unassignedEmployees.forEach(emp => {
        const matchedDept = emp.employee_department_name
          ? departmentProgress.departments.find(d => d.department_name === emp.employee_department_name)
          : null;

        if (matchedDept) {
          if (grouped[matchedDept.department_id]) {
            grouped[matchedDept.department_id].employees = [
              ...grouped[matchedDept.department_id].employees,
              emp
            ];
          } else {
            grouped[matchedDept.department_id] = {
              ...matchedDept,
              employees: [emp]
            };
          }
        }
      });
    }
    
    return grouped;
  }, [employeePayrolls, departmentProgress]);

  // Get flags indexed by employee for quick lookup
  const flagsByEmployeeId = useMemo(() => {
    if (!flagsData) return {};
    const indexed = {};
    const allFlags = [
      ...(flagsData.errors || []),
      ...(flagsData.needs_review || []),
      ...(flagsData.ready || [])
    ];
    allFlags.forEach(flag => {
      if (!indexed[flag.employee_id]) {
        indexed[flag.employee_id] = [];
      }
      indexed[flag.employee_id].push(flag);
    });
    return indexed;
  }, [flagsData]);

  // Filter employees based on search and filter
  const filteredPayrollsByDepartment = useMemo(() => {
    if (!Object.keys(payrollsByDepartment).length) return {};

    const filtered = {};
    Object.entries(payrollsByDepartment).forEach(([deptId, dept]) => {
      if (payrollDeptFilter != null && String(deptId) !== String(payrollDeptFilter)) return;

      let filteredEmployees = [...dept.employees];

      if (payrollSearchTerm) {
        const searchLower = payrollSearchTerm.toLowerCase();
        filteredEmployees = filteredEmployees.filter(emp => {
          const name = `${emp.employee_first_name || emp.employee?.first_name || ''} ${emp.employee_last_name || emp.employee?.last_name || ''}`.toLowerCase();
          const staffId = (emp.employee_staff_id || emp.employee?.staff_id || '').toLowerCase();
          return name.includes(searchLower) || staffId.includes(searchLower);
        });
      }

      if (payrollFilter !== 'all') {
        filteredEmployees = filteredEmployees.filter(emp => emp.employee_classification === payrollFilter);
      }

      if (filteredEmployees.length > 0 || !payrollSearchTerm) {
        filtered[deptId] = {
          ...dept,
          employees: filteredEmployees,
          filteredCount: filteredEmployees.length
        };
      }
    });
    return filtered;
  }, [payrollsByDepartment, payrollSearchTerm, payrollFilter, payrollDeptFilter]);

  // Fetch full detail for selected employee (includes ytd_summary, aw_ceiling_info, shg_contributions_display)
  const { data: employeePayrollDetail } = useEmployeePayrollDetail(selectedEmployeePayrollId);

  // Merge list data with detail data — detail enriches with fields the list serializer drops
  const selectedEmployeePayroll = useMemo(() => {
    const listRecord = employeePayrolls?.find(p => p.id === selectedEmployeePayrollId);
    if (!listRecord) return null;
    if (employeePayrollDetail && employeePayrollDetail.id === selectedEmployeePayrollId) {
      return { ...listRecord, ...employeePayrollDetail };
    }
    return listRecord;
  }, [employeePayrolls, selectedEmployeePayrollId, employeePayrollDetail]);

  const isCpfApplicable = ['Singapore Citizen', 'Permanent Resident'].includes(
    selectedEmployeePayroll?.employee_employment_pass_type
  );

  // Get classification counts for the currently scoped Step 3 register view
  const payrollFilterCountBase = useMemo(() => {
    let list = employeePayrolls || [];

    if (payrollDeptFilter != null) {
      const dept = payrollsByDepartment[payrollDeptFilter];
      list = dept?.employees || [];
    }

    if (payrollSearchTerm) {
      const searchLower = payrollSearchTerm.toLowerCase();
      list = list.filter(emp => {
        const name = `${emp.employee_first_name || emp.employee?.first_name || ''} ${emp.employee_last_name || emp.employee?.last_name || ''}`.toLowerCase();
        const staffId = (emp.employee_staff_id || emp.employee?.staff_id || '').toLowerCase();
        return name.includes(searchLower) || staffId.includes(searchLower);
      });
    }

    return list;
  }, [employeePayrolls, payrollsByDepartment, payrollDeptFilter, payrollSearchTerm]);

  const payrollFilterCounts = useMemo(() => {
    const all = payrollFilterCountBase.length;
    const staffCount = payrollFilterCountBase.filter(p => p.employee_classification === 'staff').length;
    const workerCount = payrollFilterCountBase.filter(p => p.employee_classification === 'worker').length;
    return { all, staff: staffCount, worker: workerCount };
  }, [payrollFilterCountBase]);

  // Unified department list for Step 3 pill bar
  const payrollDepartments = useMemo(() => {
    const depts = [];
    if (Object.keys(payrollsByDepartment).length > 0) {
      Object.values(payrollsByDepartment).forEach(dept => {
        depts.push({
          id: dept.department_id,
          name: dept.department_name,
          count: dept.employees.length,
          finalizedCount: dept.employees.filter(e => e.is_finalized).length,
        });
      });
    } else if (departmentProgress?.departments) {
      departmentProgress.departments.forEach(dept => {
        if (dept.expected_count > 0) {
          depts.push({
            id: dept.department_id,
            name: dept.department_name,
            count: dept.expected_count,
            finalizedCount: 0,
          });
        }
      });
    }
    return depts;
  }, [payrollsByDepartment, departmentProgress]);

  // Department + classification scoped payrolls for summary stats (ignores search term)
  const departmentScopedPayrolls = useMemo(() => {
    let list = employeePayrolls;
    if (payrollDeptFilter != null) {
      const dept = payrollsByDepartment[payrollDeptFilter];
      list = dept?.employees || [];
    }
    if (payrollFilter !== 'all') {
      list = list.filter(p => p.employee_classification === payrollFilter);
    }
    return list;
  }, [employeePayrolls, payrollsByDepartment, payrollDeptFilter, payrollFilter]);

  // Labour-cost-eligible allowance type names — mirrors the
  // `AllowanceType.allowance_type_is_labour_cost = True` rows in the
  // configuration DB (currently all 3 types per migration 0072 default).
  // See `.cursor/rules/plc-salary-cost-basis.mdc`.  Kept inline rather
  // than fetched so the Step 3 cards can render without an extra round
  // trip; if HR ever flips a type to FALSE the regression test
  // `test_full_apr2026_regular_cohort_total` will surface the drift.
  const LABOUR_COST_ALLOWANCE_TYPES = useMemo(() => new Set([
    'Director Fee',
    'Mobile Phone Allowance',
    'Car Allowance',
  ]), []);

  // Per-record labour-cost (PLC Salary Cost) basis.
  //   basic_for_period (= basic_salary_earned for FS runs,
  //                       basic_salary_monthly for regular runs)
  //   - leave_deduction_amount
  //   + overtime_pay
  //   + rest_day_pay
  //   + public_holiday_pay
  //   + qualifying allowances
  // EXCLUDES: leave_encashment, reimbursements, other_deductions,
  // statutory employee deductions.  Mirrors the backend helper
  // `_build_payroll_cost_basis` in
  // `staff_core/services/project_salary_service.py`.
  const buildSalaryCost = useCallback((p, runType) => {
    if (!p) return 0;
    const num = (k) => parseFloat(p[k]) || 0;
    const basicForPeriod = runType === 'final_settlement'
      ? num('basic_salary_earned')
      : num('basic_salary_monthly');
    const leaveDed = num('leave_deduction_amount');
    const ph = num('public_holiday_pay');
    const ot = num('overtime_pay');
    const rd = num('rest_day_pay');

    let qualifyingAllowances = 0;
    const breakdown = p.allowance_breakdown;
    if (breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown)) {
      for (const [name, amount] of Object.entries(breakdown)) {
        if (LABOUR_COST_ALLOWANCE_TYPES.has(name)) {
          qualifyingAllowances += parseFloat(amount) || 0;
        }
      }
    } else {
      // Fallback: today every configured allowance type has
      // is_labour_cost=True so total_allowances == qualifying total.
      qualifyingAllowances = num('total_allowances');
    }

    const basic = Math.max(0, basicForPeriod - leaveDed + ph + qualifyingAllowances);
    return basic + ot + rd;
  }, [LABOUR_COST_ALLOWANCE_TYPES]);

  const payrollAggregates = useMemo(() => {
    const list = departmentScopedPayrolls;
    const sum = (key) => list.reduce((s, p) => s + (parseFloat(p[key]) || 0), 0);
    // Compensation ladder breakdown (used by the summary-card hover tooltips
    // so HR can see exactly how Gross / Deductions / Net / Final tie out
    // without leaving the screen).
    return {
      // Earnings (build up to gross_pay)
      basicTotal: sum('basic_salary_monthly'),
      basicEarnedTotal: sum('basic_salary_earned'),
      allowancesTotal: sum('total_allowances'),
      overtimeTotal: sum('overtime_pay'),
      restDayTotal: sum('rest_day_pay'),
      publicHolidayTotal: sum('public_holiday_pay'),
      bonusTotal: sum('bonus_payments'),
      commissionTotal: sum('commission'),
      otherPaymentsTotal: sum('other_payments'),
      grossTotal: sum('gross_pay'),
      // Deduction lines (sum to total_deductions)
      cpfTotal: sum('cpf_employee_contribution'),
      shgTotal: sum('shg_total_contribution'),
      incomeTaxTotal: sum('income_tax_deduction'),
      leaveDeductionTotal: sum('leave_deduction_amount'),
      nsDeductionTotal: sum('ns_deduction_amount'),
      ir21DeductionTotal: sum('ir21_deduction_amount'),
      workPermitFeeTotal: sum('work_permit_replacement_fee'),
      sponsorshipBondTotal: sum('sponsorship_bond_deduction'),
      otherDeductionsTotal: sum('other_deductions'),
      deductionsTotal: sum('total_deductions'),
      // Bottom of the ladder
      netTotal: sum('net_pay'),
      reimbursementTotal: sum('total_reimbursements'),
      encashmentTotal: sum('leave_encashment_amount'),
      finalTotal: list.reduce((s, p) => s + (parseFloat(p.final_payment || p.net_pay) || 0), 0),
      // Labour-cost basis (Salary Cost) — ties to the PLC Salary Cost
      // report.  Regular-run cohort only.
      salaryCostTotal: list.reduce((s, p) => s + buildSalaryCost(p, 'regular'), 0),
    };
  }, [departmentScopedPayrolls, buildSalaryCost]);

  // === Companion: parallel Final Settlement run for the same period ===
  // Step 3 itself stays scoped to the regular run so "Finalize" only acts
  // on the rows HR is reviewing here. But the Worker Payroll Register, the
  // PLC Salary Cost report, and the GL all sum regular + final_settlement
  // for the period total -- so we surface the FS delta (count + dollars)
  // as a non-actionable "Period Total Labour Cash Outflow" strip below
  // the cards. Same number HR will see on PLC and on the bank file.
  const finalSettlementRunForPeriod = useMemo(() => {
    if (!currentPayrollRunId || !Array.isArray(payrollRuns)) return null;
    const regularRun = payrollRuns.find(r => r.id === currentPayrollRunId);
    if (!regularRun) return null;
    return payrollRuns.find(r =>
      r.run_type === 'final_settlement'
      && r.status !== 'cancelled'
      && r.period_start_date === regularRun.period_start_date
      && r.period_end_date === regularRun.period_end_date
    ) || null;
  }, [currentPayrollRunId, payrollRuns]);

  const fsRunId = finalSettlementRunForPeriod?.id || null;
  const { data: fsEmployeePayrolls = [] } = useEmployeePayrolls(fsRunId, !!fsRunId);

  const fsAggregates = useMemo(() => {
    let list = (fsEmployeePayrolls || []).filter(p => !p.is_excluded);
    if (payrollFilter !== 'all') {
      list = list.filter(p => p.employee_classification === payrollFilter);
    }
    return {
      count: list.length,
      grossTotal: list.reduce((s, p) => s + (parseFloat(p.gross_pay) || 0), 0),
      netTotal: list.reduce((s, p) => s + (parseFloat(p.net_pay) || 0), 0),
      finalTotal: list.reduce((s, p) => s + (parseFloat(p.final_payment || p.net_pay) || 0), 0),
      // Labour-cost basis for the FS cohort — uses basic_salary_earned
      // (NOT basic_salary_monthly) since absence pro-ration is built
      // into the earned amount for final-settlement runs.
      salaryCostTotal: list.reduce((s, p) => s + buildSalaryCost(p, 'final_settlement'), 0),
      employees: list.map(p => ({
        staffId: p.employee_staff_id,
        name: `${p.employee_first_name || ''} ${p.employee_last_name || ''}`.trim() || p.employee_staff_id,
        finalPayment: parseFloat(p.final_payment || p.net_pay) || 0,
        salaryCost: buildSalaryCost(p, 'final_settlement'),
      })),
      list,
    };
  }, [fsEmployeePayrolls, payrollFilter, buildSalaryCost]);

  // === Cash Outflow ↔ Salary Cost reconciliation ====================
  // Mirrors `scripts/_diag_apr2026_salary_cost_vs_cash_outflow.py` so
  // the dashboard tooltip and the audit script tell the same story.
  //
  // The Δ between the two headline numbers is a NET of two opposing
  // cash flows -- some items hit cash but NOT labour cost, others
  // hit labour cost but were retained from cash:
  //
  //   Adds (in cash, NOT in labour cost)        Subs (in labour cost, NOT in cash)
  //   ──────────────────────────────────        ──────────────────────────────────
  //   Bonus / commission / other earnings       Employee CPF
  //   Leave encashment                          SHG (CDAC/MBMF/SINDA/ECF)
  //   Reimbursements                            Income tax
  //   Non-qualifying allowances                 IR21 (tax clearance)
  //                                             NS deduction
  //                                             Work permit replacement fee
  //                                             Sponsorship bond
  //                                             Other deductions (phone bill etc.)
  //
  //   Cash Outflow = Salary Cost + Σ(Adds) − Σ(Subs)
  //   ⇒ Salary Cost = Cash Outflow − Σ(Adds) + Σ(Subs)
  //
  // Backend parity: see `staff_core/services/project_salary_service
  // .py :: build_cash_outflow_bridge` for the same logic in Python.
  const cashOutflowBridge = useMemo(() => {
    const allEPs = [
      ...departmentScopedPayrolls,
      ...((fsAggregates.list) || []),
    ];
    const num = (p, k) => parseFloat(p[k]) || 0;

    // Qualifying allowances per EP — the slice that IS labour cost.
    // Uses the same allowance_breakdown filter as buildSalaryCost so
    // the two calculations cannot drift.
    const qualifyingAllow = (p) => {
      const bd = p.allowance_breakdown;
      if (bd && typeof bd === 'object' && !Array.isArray(bd)) {
        let q = 0;
        for (const [name, amt] of Object.entries(bd)) {
          if (LABOUR_COST_ALLOWANCE_TYPES.has(name)) {
            q += parseFloat(amt) || 0;
          }
        }
        return q;
      }
      return num(p, 'total_allowances');
    };

    const totalOf = (key) => allEPs.reduce((s, p) => s + num(p, key), 0);

    const allAllowances = totalOf('total_allowances');
    const qualAllowances = allEPs.reduce((s, p) => s + qualifyingAllow(p), 0);

    const adds = {
      nonQualifyingAllowances: Math.max(0, allAllowances - qualAllowances),
      bonus: totalOf('bonus_payments'),
      commission: totalOf('commission'),
      otherEarnings: totalOf('other_payments'),
      leaveEncashment: totalOf('leave_encashment_amount'),
      reimbursements: totalOf('total_reimbursements'),
    };
    const subs = {
      employeeCPF: totalOf('cpf_employee_contribution'),
      shg: totalOf('shg_total_contribution'),
      incomeTax: totalOf('income_tax_deduction'),
      ir21: totalOf('ir21_deduction_amount'),
      nsDeduction: totalOf('ns_deduction_amount'),
      workPermitFee: totalOf('work_permit_replacement_fee'),
      sponsorshipBond: totalOf('sponsorship_bond_deduction'),
      otherDeductions: totalOf('other_deductions'),
    };
    const addsTotal = Object.values(adds).reduce((s, v) => s + v, 0);
    const subsTotal = Object.values(subs).reduce((s, v) => s + v, 0);

    const salaryCost = (payrollAggregates.salaryCostTotal || 0)
      + (fsAggregates.salaryCostTotal || 0);
    const cashOutflow = (payrollAggregates.finalTotal || 0)
      + (fsAggregates.finalTotal || 0);

    // Bridge: Salary Cost = Cash Outflow − adds + subs.
    // Residual quantifies any unclassified earning/deduction type --
    // should be 0 in steady state; non-zero is a regression flag.
    const bridgedSalaryCost = cashOutflow - addsTotal + subsTotal;
    const residual = bridgedSalaryCost - salaryCost;

    return {
      salaryCost, cashOutflow, adds, subs, addsTotal, subsTotal,
      bridgedSalaryCost, residual,
      netDelta: cashOutflow - salaryCost,
    };
  }, [
    departmentScopedPayrolls, fsAggregates,
    payrollAggregates.salaryCostTotal, payrollAggregates.finalTotal,
    LABOUR_COST_ALLOWANCE_TYPES,
  ]);

  // Flag review modal state
  const [showFlagReviewModal, setShowFlagReviewModal] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [exclusionReason, setExclusionReason] = useState('');
  
  // Adjustment modal state
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedPayrollForAdjustment, setSelectedPayrollForAdjustment] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    // Line item arrays
    backpayItems: [],
    overtimeItems: [],
    allowanceItems: [],
    bonusItems: [],
    deductionItems: [],
    reimbursementItems: [],
    // Audit trail
    adjustmentNotes: '',
    // Legacy fields (backward compatibility)
    bonusPayments: '',
    otherPayments: '',
    otherDeductions: ''
  });
  const [adjustmentPreview, setAdjustmentPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewTimeoutRef = useRef(null);
  
  // Ready Employees Modal state (for "View All" button)
  const [showReadyEmployeesModal, setShowReadyEmployeesModal] = useState(false);
  const [readyEmployeesSearch, setReadyEmployeesSearch] = useState('');
  
  // Expandable Payroll Detail Rows state (Step 3)
  const [expandedPayrollRows, setExpandedPayrollRows] = useState(new Set());
  
  // Toggle expand/collapse for a payroll row
  const togglePayrollRowExpansion = (payrollId) => {
    setExpandedPayrollRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(payrollId)) {
        newSet.delete(payrollId);
      } else {
        newSet.add(payrollId);
      }
      return newSet;
    });
  };
  
  // Expand/collapse all payroll rows
  const expandAllPayrollRows = () => {
    setExpandedPayrollRows(new Set(employeePayrolls.map(p => p.id)));
  };
  
  const collapseAllPayrollRows = () => {
    setExpandedPayrollRows(new Set());
  };
  
  // === PART A: Multi-Context State Persistence ===
  // Persist active step with URL sync
  const [activeStep, setActiveStep] = usePersistence('payroll-processing-step', 1, [1, 2, 3, 4]);
  
  // Separate contexts for Step 1 (normalization) and Step 2 (editing)
  const [step1Context, setStep1Context] = usePersistence('payroll-processing-step1-context', {
    period: '',
    departments: []
  });
  
  const [step2Context, setStep2Context] = usePersistence('payroll-processing-step2-context', {
    period: '',
    departments: [],
    employee: null,
    unlockedDates: {} // Map of staff_id -> array of unlocked dates
  });
  
  // Current working state — persisted so a hard refresh of step=3/4 keeps
  // the same payroll period selected without forcing the user to re-run.
  const [selectedPeriod, setSelectedPeriod] = usePersistence('payroll-processing-selected-period', '');
  
  // Track last normalized context for auto-sync detection
  const [lastNormalizedContext, setLastNormalizedContext] = useState(null);
  
  // Track if we just transitioned from Step 1 to prevent infinite loops
  const justTransitionedFromStep1 = useRef(false);

  // === Trade Sub-Filter (shown when exactly the Teams department is selected) ===
  const { data: tradeCategories = [] } = useTradeCategories();
  const [selectedTradeIds, setSelectedTradeIds] = useState([]); // [] = all trades (no filter)
  const [allTeamsStaff, setAllTeamsStaff] = useState(null);

  const teamsDept = departments.find(d => d.department_name?.toLowerCase() === 'teams');
  const teamsOnlySelected = teamsDept && selectedDepartments.length === 1 && selectedDepartments[0] === teamsDept.id;

  // Fetch Teams staff once when Teams is the sole selected department
  useEffect(() => {
    if (!teamsOnlySelected) {
      setAllTeamsStaff(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/staff/', {
          params: { department: teamsDept.id, page_size: 1000, employment_status: 'active' },
        });
        if (!cancelled) setAllTeamsStaff(res.data?.results || res.data || []);
      } catch {
        if (!cancelled) setAllTeamsStaff(null);
      }
    })();
    return () => { cancelled = true; };
  }, [teamsOnlySelected, teamsDept?.id]);

  // Derive filtered staff IDs from cached list + selected trades (no API call on toggle)
  const tradeStaffIds = useMemo(() => {
    if (!allTeamsStaff || selectedTradeIds.length === 0) return null;
    return allTeamsStaff
      .filter(s => {
        const tc = s.trade_category;
        if (!tc) return false;
        const tcId = tc.id ?? tc;
        return selectedTradeIds.some(id => id === tcId);
      })
      .map(s => s.staff_id)
      .filter(Boolean);
  }, [allTeamsStaff, selectedTradeIds]);

  // Clear trade selection when Teams is deselected
  useEffect(() => {
    if (!teamsOnlySelected) setSelectedTradeIds([]);
  }, [teamsOnlySelected]);

  // === Payroll Readiness Dashboard Hook ===
  const { data: readinessData, isLoading: readinessLoading, refetch: refetchReadiness } = usePayrollReadiness(
    selectedPeriod,
    selectedDepartments
  );
  // Global (unscoped) readiness for the Step 2 department tab bar and gate progress.
  // Auto-poll every 30s while on Step 2 so pill counts stay fresh after lock/unlock.
  const { data: globalReadinessData, refetch: refetchGlobalReadiness } = usePayrollReadiness(
    selectedPeriod,
    [],
    { staleTime: 30_000, refetchInterval: activeStep === 2 ? 30_000 : false }
  );
  const scanExceptionsMutation = useScanExceptions();

  // === Adjust & Lock Hook (shared with FinalSettlementsTab) ===
  const {
    exceptionsPending, locksSummary, adjusting, totalEmployees,
    recordsLoading, attendanceRecords, locksLoading, locksByStaffId,
    adjustedReady, setAdjustedReady,
    employeeList, selectedEmployee, setSelectedEmployee,
    employeeDailyRecords,
    rowLocking, employeeLocking,
    employeeSearchTerm, setEmployeeSearchTerm,
    filterClassification, setFilterClassification,
    expandedRows,
    leftPanelCollapsed, setLeftPanelCollapsed,
    selectedRows, setSelectedRows,
    lastSelectedIndex, setLastSelectedIndex,
    progressModal, setProgressModal, ADJUST_PHASES,
    hasUnsavedEdits,
    conflictWarning, setConflictWarning,
    editingCell, setEditingCell, cellValue, setCellValue, savingCell,
    editingSiteCell, setEditingSiteCell, siteSearchTerm, setSiteSearchTerm,
    siteFrequencyMap,
    confirmAction, setConfirmAction,
    unlockedDatesMap,
    lockedAll, globalLockedAll, globalLocksSummary, canProceedToCalculation, hasExceptionsWarning, processingReadiness, todayIso,
    recheckExceptions, fetchLocksSummary, fetchGlobalLocksSummary, fetchProcessingReadiness, fetchAttendanceLocks, fetchAttendanceRecords,
    handleApplyLock, handleRemoveLock, toggleRow, buildEmployeeList,
    loadEmployeeDailyRecords, selectEmployee,
    handleCellClick, handleCellSave, handleSessionCellSave,
    handleSiteClick, handleSiteSave,
    handleRowSelect, handleSelectAll,
    handleBulkLock, handleBulkUnlock,
    handleRowLockToggle, handleToggleEmployeeLock,
    handleDeleteSiteSession, handleConfirmDeleteSession,
    handleAdjust: _hookHandleAdjust,
    handleLoadOnly: _hookHandleLoadOnly,
    isRowEditable, getFilteredAndSortedSites,
    formatPeriodRange, formatDateDisplay, formatDateTimeDisplay, deriveFromSites,
    toLocalISODate, getMonthRange,
    getTableProps,
  } = useAdjustAndLock({
    period: selectedPeriod,
    departmentIds: selectedDepartments,
    enabled: activeStep === 2,
    persistencePrefix: 'payroll-processing',
    staffIdFilter: tradeStaffIds,
  });

  // Wrap handleAdjust (full normalize + load) with step-transition logic
  const handleAdjust = async () => {
    await _hookHandleAdjust(({ unlockedDatesMap: newUnlockedDatesMap }) => {
      justTransitionedFromStep1.current = true;
      setStep1Context({ period: selectedPeriod, departments: selectedDepartments });
      setStep2Context({
        period: selectedPeriod,
        departments: selectedDepartments,
        employee: null,
        unlockedDates: newUnlockedDatesMap,
      });
      setLastNormalizedContext({ period: selectedPeriod, departments: selectedDepartments });
      setActiveStep(2);
    });
    refetchGlobalReadiness();
  };

  // Wrap handleLoadOnly (load-only, no normalization) for dept switches within Step 2
  const handleLoadOnly = async () => {
    await _hookHandleLoadOnly();
    refetchGlobalReadiness();
  };

  // ── Step 2 Department Switcher ─────────────────────────────────────────────
  // Tabs switch instantly via handleLoadOnly (no normalization — data was
  // already normalized in Step 1). The unsaved-edits guard is the only gate.
  const [pendingDeptSwitch, setPendingDeptSwitch] = useState(null);
  const [deptSwitchTrigger, setDeptSwitchTrigger] = useState(0);

  const commitDepartmentSwitch = (newDepts) => {
    // Trade ids are a Teams-only lens. Clear them in this same update so the
    // roster load does not go out scoped to the previous trade's staff ids
    // and then get filtered down to an empty name list.
    const nextIsTeamsOnly = !!(teamsDept && newDepts.length === 1 && newDepts[0] === teamsDept.id);
    if (!nextIsTeamsOnly) setSelectedTradeIds([]);
    setSelectedDepartments(newDepts);
    setDeptSwitchTrigger(n => n + 1);
  };

  const handleDepartmentSwitch = (newDepts) => {
    if (JSON.stringify(newDepts) === JSON.stringify(selectedDepartments)) return;
    if (hasUnsavedEdits) {
      setPendingDeptSwitch(newDepts);
      return;
    }
    commitDepartmentSwitch(newDepts);
  };

  const confirmDepartmentSwitch = () => {
    if (pendingDeptSwitch !== null) {
      const next = pendingDeptSwitch;
      setPendingDeptSwitch(null);
      commitDepartmentSwitch(next);
    }
  };

  useEffect(() => {
    if (deptSwitchTrigger > 0 && activeStep === 2) {
      handleLoadOnly();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptSwitchTrigger]);

  // Define stepper steps with icons
  const steps = [
    { id: 1, label: 'Payroll Readiness', icon: Shield },
    { id: 2, label: 'Adjust & Lock', icon: Settings },
    { id: 3, label: 'Calculate Payroll', icon: DollarSign },
    { id: 4, label: 'Review & Payment', icon: Landmark }
  ];

  // Generate list of payroll months — includes the current in-progress month
  const getAvailableMonths = () => {
    const months = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-indexed

    for (let year = 2024; year <= currentYear; year++) {
      const startMonth = 0;
      const endMonth = year === currentYear ? currentMonth : 11; // Include current month

      for (let month = startMonth; month <= endMonth; month++) {
        const isCurrent = year === currentYear && month === currentMonth;
        const baseLabel = new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        months.push({
          value: `${year}-${String(month + 1).padStart(2, '0')}`,
          label: baseLabel,
          isCurrent,
        });
      }
    }

    return months.reverse(); // Most recent first
  };

  const completedMonths = getAvailableMonths();

  // === URL Sync for activeStep ===
  // URL step wins on first load so /admin/payroll?tab=processing&step=3
  // behaves like a real deep link instead of being overwritten by localStorage.
  const didHydrateStepFromUrl = useRef(false);
  useEffect(() => {
    const url = new URL(window.location.href);
    const stepFromUrl = Number(url.searchParams.get('step'));
    const hasValidUrlStep = [1, 2, 3, 4].includes(stepFromUrl);

    if (!didHydrateStepFromUrl.current) {
      didHydrateStepFromUrl.current = true;
      if (hasValidUrlStep && stepFromUrl !== activeStep) {
        setActiveStep(stepFromUrl);
        return;
      }
    }

    url.searchParams.set('step', activeStep);
    window.history.replaceState({}, '', url);
  }, [activeStep, setActiveStep]);

  // === One-time hydration of Step 2 selection (department + employee) on hard refresh ===
  // `selectedDepartments` lives in the parent as a plain useState([]) and
  // `selectedEmployee` lives in the useAdjustAndLock hook, so a hard refresh
  // resets both even though the step (?step=) and the period survive via
  // localStorage. The Step 2/Step 3 "restore context" effects below only
  // re-apply department/employee when `!selectedPeriod`, and on a refresh the
  // period IS restored — so the department tab HR was working on (e.g. Teams)
  // and the employee detail panel they had open were being silently dropped,
  // kicking them back to "All Departments" and hiding the Teams-only trade
  // sub-filter.
  //
  // Re-hydrate both once, from the persisted context matching the restored
  // step, so HR lands back exactly where they were. Guarded by a ref + an
  // empty-selection check so it runs exactly once on mount and can't fight
  // the period restore/auto-load effects (the ping-pong those effects'
  // comments warn about).
  const didHydrateDeptsFromStorage = useRef(false);
  useEffect(() => {
    if (didHydrateDeptsFromStorage.current) return;
    didHydrateDeptsFromStorage.current = true;

    const _nd = (val) => (Array.isArray(val) ? val : val && val !== 'all' ? [val] : []);

    // Department tab — only restore when there's no live selection (i.e. a
    // cold reload, not an in-session navigation). Prefer the context for the
    // step we're restoring onto; fall back to the other so a saved department
    // is honoured regardless of which step's context last captured it.
    if (selectedDepartments.length === 0) {
      const primary = activeStep === 1 ? step1Context : step2Context;
      const secondary = activeStep === 1 ? step2Context : step1Context;
      const restored = _nd(primary?.departments);
      const fallback = restored.length > 0 ? restored : _nd(secondary?.departments);
      if (fallback.length > 0) {
        setSelectedDepartments(fallback);
      }
    }

    // Step 2 master-detail also tracks the open employee row. Restore it from
    // the persisted Step 2 context so a refresh on an employee's detail panel
    // lands back on that same employee.
    if (!selectedEmployee && step2Context?.employee) {
      setSelectedEmployee(step2Context.employee);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === Restore Step 1 context on mount or when navigating to Step 1 ===
  useEffect(() => {
    if (activeStep === 1) {
      if (step1Context.period) {
        setSelectedPeriod(step1Context.period);
      } else if (completedMonths.length > 0) {
        // Fallback to most recent month
        setSelectedPeriod(completedMonths[0].value);
      }
      // Normalise persisted value: old format was a string, new format is an array
      const storedDepts = step1Context.departments;
      if (Array.isArray(storedDepts)) {
        setSelectedDepartments(storedDepts);
      } else if (storedDepts && storedDepts !== 'all') {
        setSelectedDepartments([storedDepts]);
      }
    }
  }, [activeStep === 1]);

  // === Restore Step 2 context when navigating to Step 2 ===
  useEffect(() => {
    if (activeStep === 2) {
      // If we just transitioned from Step 1, the Continue button already synced everything
      // Skip this effect to prevent infinite loops
      if (justTransitionedFromStep1.current) {
        justTransitionedFromStep1.current = false; // Reset the flag
        return;
      }
      
      // Check if we just normalized (lastNormalizedContext exists and matches current)
      if (lastNormalizedContext && 
          lastNormalizedContext.period === selectedPeriod) {
        // Just normalized - unlockedDates already populated by handleAdjust
        // Don't override with step2Context
        return;
      }

      const _normaliseDepts = (val) => {
        if (Array.isArray(val)) return val;
        if (val && val !== 'all') return [val];
        return [];
      };

      // selectedPeriod is the source of truth (persisted via usePersistence).
      // Only fall back to a saved context when there is no current
      // selection (true deep-link / fresh storage). Overriding an existing
      // selectedPeriod here used to ping-pong with the auto-load effect
      // below — Restore would push step1Context.period in, Auto-load would
      // push step2Context.period back, ad infinitum, hammering
      // /processing-readiness, /attendance-lock/summary, etc.
      if (!selectedPeriod) {
        if (step2Context.period) {
          setSelectedPeriod(step2Context.period);
          setSelectedDepartments(_normaliseDepts(step2Context.departments));
          if (step2Context.employee) {
            setSelectedEmployee(step2Context.employee);
          }
        } else if (step1Context.period) {
          setSelectedPeriod(step1Context.period);
          setSelectedDepartments(_normaliseDepts(step1Context.departments));
        }
      }
    }
  }, [activeStep, step1Context.period, step2Context.period, selectedPeriod, lastNormalizedContext]);

  // === Auto-load attendance data when returning to Step 2 ===
  useEffect(() => {
    // Skip if we just transitioned from Step 1 (handled by Continue button)
    if (justTransitionedFromStep1.current) return;

    // selectedPeriod is the source of truth. We only need to re-arm
    // `adjustedReady` so the main fetch effect in useAdjustAndLock
    // re-loads attendance / locks for the current selection.
    //
    // Do NOT mutate selectedPeriod / selectedDepartments here — that
    // used to fight the "Restore Step 2 context" effect above and
    // produced an endless period toggle (and therefore endless
    // /processing-readiness, /attendance-lock/summary, /exceptions/summary
    // calls visible in the backend log).
    if (
      activeStep === 2 &&
      selectedPeriod &&
      attendanceRecords.length === 0 &&
      !recordsLoading &&
      !adjustedReady
    ) {
      setAdjustedReady(true);
    }
  }, [activeStep, selectedPeriod, attendanceRecords.length, recordsLoading, adjustedReady]);

  // === Restore period context when navigating to Step 3 ===
  useEffect(() => {
    if (activeStep !== 3) return;

    // selectedPeriod is the source of truth. Only restore from a saved
    // context when there is no current selection (true deep-link /
    // fresh storage). Overriding an existing selectedPeriod here would
    // re-introduce the same kind of context ping-pong that caused the
    // endless processing-readiness loop on Step 2.
    if (selectedPeriod) return;

    const _nd = (val) => Array.isArray(val) ? val : (val && val !== 'all' ? [val] : []);
    if (step2Context.period) {
      setSelectedPeriod(step2Context.period);
      setSelectedDepartments(_nd(step2Context.departments));
    } else if (step1Context.period) {
      setSelectedPeriod(step1Context.period);
      setSelectedDepartments(_nd(step1Context.departments));
    }
  }, [activeStep, step1Context.period, step2Context.period, selectedPeriod]);

  // === Auto-restore payroll run ID when returning to Step 3/4 ===
  // currentPayrollRunId is volatile (useState) but derivable from the
  // persisted selectedPeriod. Without this, navigating away and back
  // wipes the run ID and forces a redundant recalculation.
  //
  // Two paths:
  //   A. User has an explicit selectedPeriod (the normal case after Step 1/2,
  //      and after a hard refresh because selectedPeriod is now persisted):
  //      try to match it in payrollPeriods. If it doesn't match (e.g. user
  //      picked Apr 2026 but no PayrollRun exists yet), do NOTHING -- the
  //      "Ready to Process Payroll" empty state is the correct answer and
  //      clicking Run Payroll will create the run for THEIR chosen period.
  //
  //   B. selectedPeriod is empty (true deep-link / fresh storage / different
  //      device): fall back to the most recent regular payroll period that
  //      already has a run. list_periods is sorted by period_start_date desc.
  //
  // Critical invariant: never overwrite an explicit user selection. If we
  // silently switched Apr -> Mar because Mar happened to have a run, the
  // user's chosen month would vanish from Step 3 without any indication.
  useEffect(() => {
    if (!(activeStep === 3 || activeStep === 4)) return;
    if (currentPayrollRunId) return;
    if (!Array.isArray(payrollPeriods) || payrollPeriods.length === 0) return;

    const isUsableRun = (runId) => {
      const matchingRun = payrollRuns?.find(r => r.id === runId);
      // list_periods already scopes to regular runs; if the paginated runs
      // query doesn't contain it, trust list_periods. Only block on a known
      // bad state (cancelled, or the rare case where it really is a final
      // settlement run that slipped into the list).
      if (!matchingRun) return true;
      return matchingRun.run_type !== 'final_settlement' && matchingRun.status !== 'cancelled';
    };

    if (selectedPeriod) {
      // Path A: respect the explicit selection. No fallback.
      const [year, month] = selectedPeriod.split('-');
      const periodStart = `${year}-${month}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const periodEnd = `${year}-${month}-${lastDay}`;
      const target = payrollPeriods.find(p =>
        p.period_start_date === periodStart && p.period_end_date === periodEnd
      );
      if (target?.first_run_id && isUsableRun(target.first_run_id)) {
        setCurrentPayrollRunId(target.first_run_id);
      }
      return;
    }

    // Path B: deep-link fallback. Only here may we choose a period for the user.
    const fallback = payrollPeriods.find(p => p?.first_run_id);
    if (fallback?.first_run_id && isUsableRun(fallback.first_run_id)) {
      setCurrentPayrollRunId(fallback.first_run_id);
      const restoredPeriod = String(fallback.period_start_date || '').slice(0, 7);
      if (restoredPeriod) setSelectedPeriod(restoredPeriod);
    }
  }, [activeStep, currentPayrollRunId, selectedPeriod, payrollPeriods, payrollRuns]);

  // === Save Step 1 context when period/department changes on Step 1 ===
  useEffect(() => {
    if (activeStep === 1 && selectedPeriod) {
      setStep1Context({
        period: selectedPeriod,
        departments: selectedDepartments,
      });
    }
  }, [activeStep, selectedPeriod, selectedDepartments]);

  // === Save Step 2 context when context changes on Step 2 ===
  useEffect(() => {
    if (activeStep === 2 && selectedPeriod) {
      setStep2Context(prev => ({
        period: selectedPeriod,
        departments: selectedDepartments,
        employee: selectedEmployee,
        unlockedDates: prev.unlockedDates || {} // Preserve unlocked dates
      }));
    }
  }, [activeStep, selectedPeriod, selectedDepartments, selectedEmployee]);

  // === PART D: Validation - Period validation ===
  useEffect(() => {
    if (selectedPeriod && completedMonths.length > 0) {
      const periodExists = completedMonths.some(p => p.value === selectedPeriod);
      if (!periodExists) {
        setSelectedPeriod('');
        setActiveStep(1);
        setStep1Context({ period: '', departments: [] });
        setStep2Context({ period: '', departments: [], employee: null, unlockedDates: {} });
        toast.error('Previously selected period no longer exists. Reset to Step 1.');
      }
    }
  }, [completedMonths, selectedPeriod]);


  // === Step 3: Auto-dismiss success banner after 5s ===
  useEffect(() => {
    if (calculationStatus?.state === 'SUCCESS' && calculationTaskId) {
      const timer = setTimeout(() => setCalculationTaskId(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [calculationStatus?.state, calculationTaskId]);

  // === Step 3: Auto-expand departments with employees or errors ===
  useEffect(() => {
    if (activeStep === 3 && departmentProgress?.departments) {
      const toExpand = new Set();
      departmentProgress.departments.forEach(dept => {
        // Expand if: has employees AND (is being processed OR has errors OR is calculated)
        if (dept.expected_count > 0 && (dept.status === 'pending' || dept.errors > 0 || dept.status === 'calculated')) {
          toExpand.add(dept.department_id);
        }
      });
      // If nothing to expand, expand first department with employees
      if (toExpand.size === 0) {
        const firstWithEmployees = departmentProgress.departments.find(d => d.expected_count > 0);
        if (firstWithEmployees) toExpand.add(firstWithEmployees.department_id);
      }
      setExpandedPayrollDepartments(toExpand);
    }
  }, [activeStep, departmentProgress?.departments]);

  // === Step 3: Clear employee selection when filter changes and employee doesn't match ===
  useEffect(() => {
    if (selectedEmployeePayrollId && payrollFilter !== 'all') {
      const selectedPayroll = employeePayrolls?.find(p => p.id === selectedEmployeePayrollId);
      if (selectedPayroll && selectedPayroll.employee_classification !== payrollFilter) {
        setSelectedEmployeePayrollId(null);
      }
    }
  }, [payrollFilter, selectedEmployeePayrollId, employeePayrolls]);

  // === Payroll Adjustment Preview (Debounced) ===
  useEffect(() => {
    // Only trigger preview if modal is open and there are adjustments
    if (!showAdjustmentModal || !selectedPayrollForAdjustment) return;
    
    // Check if there are any adjustment items
    const hasAdjustments = 
      adjustmentForm.backpayItems.length > 0 ||
      adjustmentForm.overtimeItems.length > 0 ||
      adjustmentForm.allowanceItems.length > 0 ||
      adjustmentForm.bonusItems.length > 0 ||
      adjustmentForm.deductionItems.length > 0 ||
      adjustmentForm.reimbursementItems.length > 0;
    
    if (!hasAdjustments) {
      setAdjustmentPreview(null);
      return;
    }
    
    // Clear any existing timeout
    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }
    
    // Set loading state immediately for feedback
    setIsPreviewLoading(true);
    
    // Debounce the API call (800ms)
    previewTimeoutRef.current = setTimeout(() => {
      previewAdjustmentMutation.mutate({
        employeePayrollId: selectedPayrollForAdjustment.id,
        adjustmentData: {
          backpay_items: adjustmentForm.backpayItems.filter(i => i.amount).map(i => ({
            description: i.description || 'Backpay',
            amount: parseFloat(i.amount) || 0
          })),
          overtime_items: adjustmentForm.overtimeItems.filter(i => i.amount).map(i => ({
            description: i.description || 'Overtime',
            amount: parseFloat(i.amount) || 0
          })),
          allowance_items: adjustmentForm.allowanceItems.filter(i => i.amount).map(i => ({
            description: i.description || 'Allowance',
            amount: parseFloat(i.amount) || 0
          })),
          bonus_items: adjustmentForm.bonusItems.filter(i => i.amount).map(i => ({
            description: i.description || 'Bonus',
            amount: parseFloat(i.amount) || 0
          })),
          deduction_items: adjustmentForm.deductionItems.filter(i => i.amount).map(i => ({
            description: i.description || 'Deduction',
            amount: parseFloat(i.amount) || 0
          })),
          reimbursement_items: adjustmentForm.reimbursementItems.filter(i => i.amount).map(i => ({
            description: i.description || 'Reimbursement',
            amount: parseFloat(i.amount) || 0
          })),
        }
      }, {
        onSuccess: (data) => {
          setAdjustmentPreview(data);
          setIsPreviewLoading(false);
        },
        onError: () => {
          setIsPreviewLoading(false);
        }
      });
    }, 800);
    
    // Cleanup timeout on unmount or re-run
    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
    };
  }, [
    showAdjustmentModal,
    selectedPayrollForAdjustment?.id,
    adjustmentForm.backpayItems,
    adjustmentForm.overtimeItems,
    adjustmentForm.allowanceItems,
    adjustmentForm.bonusItems,
    adjustmentForm.deductionItems,
    adjustmentForm.reimbursementItems
  ]);

  // Legacy effect: Auto-select most recent month if nothing selected (fallback)
  useEffect(() => {
    if (!selectedPeriod && completedMonths.length > 0 && activeStep === 1) {
      setSelectedPeriod(completedMonths[0].value);
    }
  }, []);

  // === Step 3: Bulk Selection Handlers ===
  const togglePayrollSelection = (payrollId, checked) => {
    setSelectedPayrollIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(payrollId);
      } else {
        newSet.delete(payrollId);
      }
      return newSet;
    });
  };

  const toggleDepartmentPayrollSelection = (deptId, checked) => {
    const dept = filteredPayrollsByDepartment[deptId];
    if (!dept) return;
    
    setSelectedPayrollIds(prev => {
      const newSet = new Set(prev);
      dept.employees.forEach(emp => {
        if (checked) {
          newSet.add(emp.id);
        } else {
          newSet.delete(emp.id);
        }
      });
      return newSet;
    });
  };

  const toggleAllPayrollSelection = (checked) => {
    if (checked) {
      const allIds = new Set();
      Object.values(filteredPayrollsByDepartment).forEach(dept => {
        dept.employees.forEach(emp => allIds.add(emp.id));
      });
      setSelectedPayrollIds(allIds);
    } else {
      setSelectedPayrollIds(new Set());
    }
  };

  const handleBulkFinalizeSelected = () => {
    if (selectedPayrollIds.size === 0) {
      toast.error('No employees selected');
      return;
    }
    
    // Filter to only unfinalized payrolls
    const unfinalizedIds = Array.from(selectedPayrollIds).filter(id => {
      const payroll = employeePayrolls.find(p => p.id === id);
      return payroll && !payroll.is_finalized;
    });
    
    if (unfinalizedIds.length === 0) {
      toast.success('All selected payrolls are already finalized');
      return;
    }
    
    bulkFinalizeMutation.mutate(unfinalizedIds, {
      onSuccess: () => {
        setSelectedPayrollIds(new Set());
        refetchEmployeePayrolls();
      }
    });
  };

  // Step 3 "Lock All" — finalize every record in the run in one call
  const unfinalizedPayrollCount = useMemo(
    () => employeePayrolls.filter(p => !p.is_finalized && !p.is_excluded).length,
    [employeePayrolls]
  );

  const handleLockAllPayrolls = () => {
    if (!currentPayrollRunId) return;
    bulkFinalizeRunMutation.mutate(currentPayrollRunId, {
      onSuccess: () => {
        setShowLockAllModal(false);
        setSelectedPayrollIds(new Set());
        refetchEmployeePayrolls();
      },
      onError: () => setShowLockAllModal(false),
    });
  };

  // Step 3 flag triage — acknowledge a group of review flags (or all of them)
  // from the ReviewFlagsModal. The modal stays open after a partial (group)
  // acknowledgement so HR can keep triaging; it closes once everything is done.
  const handleAcknowledgeFlags = (flagIds) => {
    if (!flagIds || flagIds.length === 0) return;
    const isFullSet = flagIds.length === (flagSummary?.needs_review?.length || 0);
    bulkReviewMutation.mutate(
      { flagIds, reviewNotes: 'Acknowledged via Step 3 flag review' },
      {
        onSuccess: () => {
          if (isFullSet) setShowReviewAllModal(false);
          refetchFlagSummary();
          refetchFlags();
        },
      }
    );
  };

  // System cross-check of review flags against source data; modal stays
  // open showing whatever still needs human eyes.
  const handleAutoVerifyFlags = () => {
    if (!currentPayrollRunId) return;
    autoVerifyMutation.mutate(currentPayrollRunId, {
      onSuccess: () => {
        refetchFlagSummary();
        refetchFlags();
      },
    });
  };

  const pollSingleEmployeeCalcTask = async (taskId) => {
    const MAX_POLLS = 60; // ~30s ceiling for one employee
    for (let i = 0; i < MAX_POLLS; i++) {
      const sres = await api.get(
        `/confidential/payroll-runs/${currentPayrollRunId}/calculation_status/`,
        { params: { task_id: taskId } },
      );
      const st = sres.data?.state;
      if (st === 'SUCCESS') {
        const result = sres.data?.result;
        if (result?.status === 'error') {
          throw new Error(result.message || result.error || 'Calculation failed');
        }
        return result;
      }
      if (st === 'FAILURE') {
        throw new Error(sres.data?.error || 'Calculation failed');
      }
      await new Promise((r) => setTimeout(r, 500));
    }
    throw new Error('Calculation timed out — refresh to check the latest figures');
  };

  // Detail-panel Recalc — same Celery path as selection-toolbar Recalc,
  // scoped to one employee. Does NOT flip the run into org-wide
  // "calculating"; HR stays in the payslip review loop.
  const handleRecalculateEmployee = async (payroll) => {
    if (!payroll || !currentPayrollRunId) return;
    if (payroll.is_finalized) {
      toast.error('Cannot recalculate a locked payroll. Unlock it first.');
      return;
    }
    const staffId = payroll.employee_staff_id || payroll.employee?.staff_id;
    if (!staffId) {
      toast.error('Missing employee id');
      return;
    }
    if (recalculatingStaffId) return;

    const displayName = payroll.employee_name
      || `${payroll.employee_first_name || ''} ${payroll.employee_last_name || ''}`.trim()
      || staffId;

    setRecalculatingStaffId(staffId);
    toast.loading(`Recalculating ${displayName}…`, { id: 'single-recalc' });
    try {
      const res = await api.post(
        `/confidential/payroll-runs/${currentPayrollRunId}/calculate_employee/`,
        { employee_id: staffId, force_recalculate: true },
      );
      if (res.data?.task_id) {
        await pollSingleEmployeeCalcTask(res.data.task_id);
      }
      await Promise.all([
        refetchEmployeePayrolls(),
        refetchDepartmentProgress(),
        refetchStep2Reconciliation(),
        refetchFlagSummary(),
        refetchFlags(),
      ]);
      toast.success(`Recalculated ${displayName}`, { id: 'single-recalc' });
    } catch (error) {
      toast.error(
        `Failed to recalculate: ${error.response?.data?.error || error.message}`,
        { id: 'single-recalc' },
      );
    } finally {
      setRecalculatingStaffId(null);
    }
  };

  const handleBulkRecalculateSelected = async () => {
    if (selectedPayrollIds.size === 0) {
      toast.error('No employees selected');
      return;
    }
    
    // Get staff IDs from selected payrolls
    const selectedPayrolls = Array.from(selectedPayrollIds)
      .map(id => employeePayrolls.find(p => p.id === id))
      .filter(Boolean);
    
    // Filter to only unfinalized payrolls (can't recalculate finalized)
    const recalculatable = selectedPayrolls.filter(p => !p.is_finalized);
    
    if (recalculatable.length === 0) {
      toast.error('Cannot recalculate locked payrolls. Unlock them first.');
      return;
    }
    
    toast.loading(`Recalculating ${recalculatable.length} employee(s)...`, { id: 'bulk-recalc' });
    
    try {
      // Recalculate each employee sequentially
      for (const payroll of recalculatable) {
        await api.post(`/confidential/payroll-runs/${currentPayrollRunId}/calculate_employee/`, {
          employee_id: payroll.employee_staff_id || payroll.employee?.staff_id,
          force_recalculate: true
        });
      }
      
      toast.success(`Recalculated ${recalculatable.length} employee(s)`, { id: 'bulk-recalc' });
      setSelectedPayrollIds(new Set());
      refetchEmployeePayrolls();
      refetchDepartmentProgress();
    } catch (error) {
      toast.error(`Failed to recalculate: ${error.response?.data?.error || error.message}`, { id: 'bulk-recalc' });
    }
  };

  // Recalculate every employee currently flagged with Step 2 ↔ Step 3
  // drift. The persistent STEP2_DRIFT flag will auto-resolve on the
  // next reconcile pass once the saved row matches the AR/session
  // truth — see ``payroll_step2_reconciliation_service.sync_drift_flags``.
  const handleRecalculateDriftedEmployees = async () => {
    const drifts = step2Reconciliation?.drifts || [];
    if (drifts.length === 0) {
      toast.error('No drifted employees to recalculate');
      return;
    }

    // Filter to only unfinalized rows (can't recalc finalized).
    const targets = drifts
      .map(d => {
        const ep = employeePayrolls.find(p => p.id === d.employee_payroll_id
          || (p.employee_staff_id || p.employee?.staff_id) === d.staff_id);
        return ep && !ep.is_finalized ? ep : null;
      })
      .filter(Boolean);

    if (targets.length === 0) {
      toast.error('All drifted employees are locked. Unlock them before recalculating.');
      return;
    }

    toast.loading(`Recalculating ${targets.length} drifted employee(s)…`, { id: 'drift-recalc' });

    try {
      for (const ep of targets) {
        await api.post(`/confidential/payroll-runs/${currentPayrollRunId}/calculate_employee/`, {
          employee_id: ep.employee_staff_id || ep.employee?.staff_id,
          force_recalculate: true,
        });
      }
      toast.success(`Recalculated ${targets.length} drifted employee(s)`, { id: 'drift-recalc' });
      refetchEmployeePayrolls();
      refetchDepartmentProgress();
      refetchStep2Reconciliation();
      refetchFlagSummary();
      refetchFlags();
    } catch (error) {
      toast.error(
        `Failed to recalculate: ${error.response?.data?.error || error.message}`,
        { id: 'drift-recalc' }
      );
    }
  };

  const togglePayrollDepartmentExpansion = (deptId) => {
    setExpandedPayrollDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptId)) {
        newSet.delete(deptId);
      } else {
        newSet.add(deptId);
      }
      return newSet;
    });
  };

  const visibleDeptIds = useMemo(
    () => Object.keys(filteredPayrollsByDepartment).filter(
      id => filteredPayrollsByDepartment[id].employees.length > 0
    ),
    [filteredPayrollsByDepartment]
  );
  const allPayrollDeptsExpanded = visibleDeptIds.length > 0 && visibleDeptIds.every(id => expandedPayrollDepartments.has(id));

  const toggleAllPayrollDepartments = () => {
    if (allPayrollDeptsExpanded) {
      setExpandedPayrollDepartments(new Set());
    } else {
      setExpandedPayrollDepartments(new Set(visibleDeptIds));
    }
  };

  // Calculate total net pay from payroll runs
  const totalNetPay = payrollRuns && payrollRuns.length > 0 
    ? payrollRuns.reduce((sum, run) => sum + (run.total_net_pay || 0), 0) 
    : 0;

  // Determine if ANY payroll data exists (enough to show the register).
  // Intentionally permissive — we show partial results as departments are
  // calculated. The only hard blocker is an actively-running calculation.
  const isCalculationComplete = () => {
    if (calculationStatus?.state === 'PROGRESS' || runPayrollMutation.isPending) {
      return false;
    }

    // Persistent source of truth: do we have calculated employee payrolls?
    if (employeePayrolls.length > 0) return true;

    // Check PayrollRun status from database
    if (selectedPeriod) {
      const [year, month] = selectedPeriod.split('-');
      const periodStart = `${year}-${month}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const periodEnd = `${year}-${month}-${lastDay}`;

      const matchingPeriod = payrollPeriods.find(p =>
        p.period_start_date === periodStart &&
        p.period_end_date === periodEnd
      );

      if (matchingPeriod?.first_run_id) {
        const matchingRun = payrollRuns?.find(r => r.id === matchingPeriod.first_run_id);
        if (matchingRun && matchingRun.run_type !== 'final_settlement') {
          if (['review', 'approved', 'paid'].includes(matchingRun.status)) return true;
          if (matchingRun.status === 'calculating') return false;
        }
      }
    }

    // Department progress check
    if (departmentProgress?.summary?.total_calculated > 0) return true;

    // Celery task state (ephemeral — only valid within the same session)
    return calculationStatus?.state === 'SUCCESS';
  };

  // Check if all flags are resolved (for proceeding to Step 4)
  const areFlagsResolved = () => {
    // If no flag summary yet, assume not resolved
    if (!flagSummary) return false;
    return flagSummary.can_proceed === true;
  };

  // Step 3 must be fully locked before approval (mirrors Step 2's all-rows-locked gate)
  const allPayrollsLocked = employeePayrolls.length > 0 && unfinalizedPayrollCount === 0;

  const canContinue = isCalculationComplete() && areFlagsResolved() && allPayrollsLocked;

  // Single source of truth for WHY Step 4 is still gated. Reused by both the
  // stepper's Step 4 circle and the "Continue" button so the icon and the
  // button can never disagree about whether HR may proceed to approval.
  const step4BlockedReason = (calculationStatus?.state === 'PROGRESS' || runPayrollMutation.isPending)
    ? 'Payroll calculation in progress...'
    : !isCalculationComplete()
      ? 'Run payroll calculation first'
      : flagSummary?.error_count > 0
        ? `${flagSummary.error_count} error(s) must be resolved or excluded`
        : !areFlagsResolved()
          ? `${flagSummary?.review_count || 0} item(s) need review — use Review All`
          : !allPayrollsLocked
            ? `${unfinalizedPayrollCount} payroll record(s) must be locked first — use Lock All`
            : 'Complete Step 3 first';

  // Render modern horizontal stepper
  const renderStepIndicator = () => {
    const handleStepClick = (stepId) => {
      // Steps 1 & 2 are always freely navigable (once a period is selected)
      if (stepId <= 2) {
        setActiveStep(stepId);
      }
      // Step 3: requires at least one category ready (staff or workmen)
      else if (stepId === 3 && canProceedToCalculation) {
        setActiveStep(stepId);
      }
      // Step 4: forward only once everything's ready (same gate as the
      // "Continue" button), OR freely if HR has already reached it.
      else if (stepId === 4 && (canContinue || activeStep >= 4)) {
        setActiveStep(stepId);
      }
      // Allow going back from any completed step
      else if (stepId < activeStep) {
        setActiveStep(stepId);
      }
    };

    return (
      <div className="mb-4">
        {/* Stepper - Full Width */}
        <div className="flex items-center justify-center space-x-4">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = activeStep >= step.id;
            const isCompleted = activeStep > step.id;
            const isCurrent = activeStep === step.id;
            // Steps 1-2: always clickable. Step 3: only when gate passes.
            // Step 4: when ready to proceed (canContinue) or already reached.
            const isClickable = step.id <= 2
              || (step.id === 3 && canProceedToCalculation)
              || (step.id === 4 && (canContinue || activeStep >= 4))
              || step.id < activeStep;

            return (
              <React.Fragment key={step.id}>
                {/* Step Container */}
                <div 
                  className={`flex flex-col items-center flex-1 ${
                    isClickable ? 'cursor-pointer group' : 'cursor-not-allowed'
                  }`}
                  onClick={() => isClickable && handleStepClick(step.id)}
                  title={isClickable 
                    ? (isCurrent ? 'Current step' : `Navigate to ${step.label}`)
                    : step.id === 3 ? 'Lock at least one employee with 0 pending exceptions'
                    : step.id === 4 ? step4BlockedReason
                    : 'Complete previous steps first'
                  }
                >
                  {/* Step Circle */}
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
                    isCompleted
                      ? 'bg-red-600 text-white shadow-sm'
                      : isActive
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'bg-gray-200 text-gray-500'
                  } ${isClickable && !isCurrent ? 'group-hover:ring-2 group-hover:ring-red-300 group-hover:ring-offset-2' : ''}`}>
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>

                  {/* Step Label */}
                  <div className="mt-1.5 text-center">
                    <div className={`text-xs font-medium transition-colors duration-200 ${
                      activeStep >= step.id ? 'text-red-600' : 'text-gray-500'
                    } ${isClickable && !isCurrent ? 'group-hover:text-red-700' : ''}`}>
                      {step.label}
                    </div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 rounded-full transition-all duration-200 self-start mt-4 ${
                    activeStep > step.id ? 'bg-red-600' : 'bg-gray-200'
                  }`} style={{ minWidth: '60px', maxWidth: '150px' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">

      {/* Stepper Container */}
      <div className="bg-white rounded-2xl shadow-sm border pt-4 px-6 pb-6">
        {renderStepIndicator()}


        {/* Step Content */}
        {activeStep === 1 && (
          <div className="space-y-6">
            {/* Header */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-red-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Payroll Readiness</h3>
                </div>
                {/* Right controls: filters + actions */}
                <div className="flex items-center gap-3">
                  {/* Department Filter */}
                  <MultiPillSelect
                    options={departmentOptions}
                    value={selectedDepartments}
                    onChange={setSelectedDepartments}
                    allLabel="All Departments"
                    icon={Building2}
                    iconColor="text-red-600"
                    theme="admin"
                  />

                  {/* Trade Sub-Filter — visible only when Teams is the sole selected department */}
                  {teamsOnlySelected && tradeCategories.length > 0 && (
                    <div className="flex items-center gap-1">
                      <MultiPillSelect
                        options={tradeCategories.map(tc => ({
                          value: tc.id,
                          label: tc.trade_code || tc.name,
                        }))}
                        value={selectedTradeIds}
                        onChange={setSelectedTradeIds}
                        allLabel="All Trades"
                        icon={Filter}
                        iconColor="text-amber-600"
                        theme="admin"
                        className="border-amber-300 hover:border-amber-400"
                      />
                      {selectedTradeIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedTradeIds([])}
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="Clear trade filter"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}

                  {/* Period selector */}
                  <PillSelect
                    options={completedMonths}
                    value={selectedPeriod}
                    onChange={setSelectedPeriod}
                    placeholder="Select Period..."
                    icon={Calendar}
                    iconColor="text-red-600"
                    theme="admin"
                  />

                  {/* Scan for Exceptions */}
                  <button
                    onClick={() => scanExceptionsMutation.mutate(selectedPeriod)}
                    disabled={!selectedPeriod || scanExceptionsMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                    title="Run exception detection for all unscanned days in this period"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${scanExceptionsMutation.isPending ? 'animate-spin' : ''}`} />
                    {scanExceptionsMutation.isPending ? 'Scanning…' : 'Scan for Exceptions'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {readinessData?.is_current_month
                  ? `Showing data completeness up to ${readinessData.effective_end} (today). Work progressively — lock employees as they're verified.`
                  : 'Review data completeness and resolve exceptions before proceeding to payroll calculation.'}
              </p>
            </div>

            {/* ── Mid-period leavers notice (read-only, informational) ────
               Surfaces here so HR sees the "shape of this run" before drilling
               into row-level data on Step 2. Scoped to the active department
               filter, so it disappears when irrelevant. */}
            {readinessData?.overall?.mid_period_leavers > 0 && (() => {
              const count = readinessData.overall.mid_period_leavers;
              const monthLabel = readinessData.period_start
                ? new Date(readinessData.period_start).toLocaleString('en-SG', { month: 'long', year: 'numeric' })
                : 'this period';
              return (
                <div
                  className="inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20 text-xs"
                  role="status"
                >
                  <UserMinus className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>
                      <span className="font-semibold">{count} employee{count === 1 ? '' : 's'}</span>
                      {' '}left mid-{monthLabel} — excluded from this run, handled via Final Settlements.
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveTab('final-settlements')}
                      className="font-medium underline underline-offset-2 hover:text-blue-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-sm"
                    >
                      Review Final Settlements →
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Section A: Data Completeness Cards */}
            {readinessData && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {/* Clock-In Coverage */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Clock-In</span>
                    <span className={`text-xs font-semibold ${readinessData.overall.clock_in_pct >= 95 ? 'text-green-600' : readinessData.overall.clock_in_pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                      {readinessData.overall.clock_in_pct}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${readinessData.overall.clock_in_pct >= 95 ? 'bg-green-500' : readinessData.overall.clock_in_pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(readinessData.overall.clock_in_pct, 100)}%` }} />
                  </div>
                </div>

                {/* Clock-Out Coverage */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Clock-Out</span>
                    <span className={`text-xs font-semibold ${readinessData.overall.clock_out_pct >= 95 ? 'text-green-600' : readinessData.overall.clock_out_pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                      {readinessData.overall.clock_out_pct}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${readinessData.overall.clock_out_pct >= 95 ? 'bg-green-500' : readinessData.overall.clock_out_pct >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${Math.min(readinessData.overall.clock_out_pct, 100)}%` }} />
                  </div>
                </div>

                {/* Exceptions */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Exceptions</span>
                    {readinessData.overall.pending_exceptions === 0 ? (
                      <span className="inline-flex items-center text-xs font-semibold text-green-600">
                        <CheckCircle className="w-3 h-3 mr-0.5" /> Clear
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-xs font-semibold text-amber-600">
                        <AlertTriangle className="w-3 h-3 mr-0.5" /> {readinessData.overall.pending_exceptions}
                      </span>
                    )}
                  </div>
                  <div className="text-xl font-bold text-gray-900">{readinessData.overall.pending_exceptions}</div>
                </div>

                {/* Lock Progress */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Locked</span>
                    <span className={`text-xs font-semibold ${readinessData.overall.readiness_pct === 100 ? 'text-green-600' : 'text-gray-600'}`}>
                      {readinessData.overall.locked_employees}/{readinessData.overall.total_employees}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${readinessData.overall.readiness_pct === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${readinessData.overall.readiness_pct}%` }} />
                  </div>
                </div>
              </div>
            )}

            {readinessLoading && !readinessData && (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-5 h-5 text-gray-400 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Loading readiness data…</span>
              </div>
            )}

            {/* Section B: Department Readiness Table */}
            {readinessData?.departments?.length > 0 && (
              <div className="bg-white rounded-xl border overflow-hidden">
                {/* Mobile card list */}
                <div className="md:hidden p-4 space-y-3">
                  {readinessData.departments.map(dept => (
                    <div key={dept.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{dept.name}</p>
                          <p className="text-xs text-gray-500">{dept.total_employees} employees</p>
                        </div>
                        <div className="flex-shrink-0">
                          {dept.pending_exceptions > 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              <AlertTriangle className="w-3 h-3 mr-1" /> {dept.pending_exceptions}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> 0
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 space-y-2 flex-1">
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Clock-In</span>
                          <span className={`text-right tabular-nums text-xs font-semibold ${dept.clock_in_coverage.pct >= 95 ? 'text-green-600' : dept.clock_in_coverage.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                            {dept.clock_in_coverage.pct}%
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Clock-Out</span>
                          <span className={`text-right tabular-nums text-xs font-semibold ${dept.clock_out_coverage.pct >= 95 ? 'text-green-600' : dept.clock_out_coverage.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                            {dept.clock_out_coverage.pct}%
                          </span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Locked</span>
                          <span className="text-gray-900 text-right tabular-nums">{dept.locked_employees}/{dept.total_employees}</span>
                        </div>
                        <div className="flex items-start justify-between gap-3 text-sm">
                          <span className="text-gray-500 flex-shrink-0">Readiness</span>
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="w-16 bg-gray-100 rounded-full h-1.5">
                              <span className={`block h-1.5 rounded-full ${dept.is_ready ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${dept.readiness_pct}%` }} />
                            </span>
                            <span className="text-xs font-medium text-gray-600 tabular-nums">{dept.readiness_pct}%</span>
                          </span>
                        </div>
                      </div>
                      <div className="card-action-footer">
                        <button
                          onClick={() => {
                            setSelectedDepartments([dept.id]);
                            handleAdjust();
                          }}
                          className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                          title={`Adjust & Lock ${dept.name}`}
                          aria-label={`Adjust and lock ${dept.name}`}
                        >
                          Adjust & Lock <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-4 py-3 font-medium text-gray-600">Department</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Employees</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Clock-In %</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Clock-Out %</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Exceptions</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Locked</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-600">Readiness</th>
                      <th className="text-right px-4 py-3 font-medium text-gray-600"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {readinessData.departments.map(dept => (
                      <tr key={dept.id} className="table-row-dense">
                        <td className="px-4 py-3 font-medium text-gray-900">{dept.name}</td>
                        <td className="text-center px-4 py-3 text-gray-700">{dept.total_employees}</td>
                        <td className="text-center px-4 py-3">
                          <span className={`text-xs font-semibold ${dept.clock_in_coverage.pct >= 95 ? 'text-green-600' : dept.clock_in_coverage.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                            {dept.clock_in_coverage.pct}%
                          </span>
                        </td>
                        <td className="text-center px-4 py-3">
                          <span className={`text-xs font-semibold ${dept.clock_out_coverage.pct >= 95 ? 'text-green-600' : dept.clock_out_coverage.pct >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
                            {dept.clock_out_coverage.pct}%
                          </span>
                        </td>
                        <td className="text-center px-4 py-3">
                          {dept.pending_exceptions > 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              <AlertTriangle className="w-3 h-3 mr-1" /> {dept.pending_exceptions}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" /> 0
                            </span>
                          )}
                        </td>
                        <td className="text-center px-4 py-3 text-gray-700">
                          {dept.locked_employees}/{dept.total_employees}
                        </td>
                        <td className="text-center px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 bg-gray-100 rounded-full h-1.5">
                              <div className={`h-1.5 rounded-full ${dept.is_ready ? 'bg-green-500' : 'bg-blue-500'}`}
                                style={{ width: `${dept.readiness_pct}%` }} />
                            </div>
                            <span className="text-xs font-medium text-gray-600">{dept.readiness_pct}%</span>
                          </div>
                        </td>
                        <td className="text-right px-4 py-3">
                          <button
                            onClick={() => {
                              // Deep-link to Step 2 filtered to this department
                              setSelectedDepartments([dept.id]);
                              handleAdjust();
                            }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                          >
                            Adjust & Lock <ChevronRight className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {!selectedPeriod && (
              <div className="text-center py-12">
                <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">Select a payroll period to view readiness data.</p>
              </div>
            )}

            {/* Footer — navigate to Step 2 */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  if (selectedPeriod) {
                    handleAdjust();
                  }
                }}
                disabled={!selectedPeriod || adjusting}
                className="btn btn-admin-primary"
              >
                {adjusting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Continue to Adjust & Lock
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

            {/* Step 2: Normalize & Lock (Master-Detail View) */}
            {activeStep === 2 && (
              <div className="space-y-6">
                {/* Mid-period leavers are surfaced inline on each department
                   pill below (and on Step 1) — they have no row-level action
                   on this page, so a global banner here is noise. */}

                {/* ── Department Tab Bar ──────────────────────────────────── */}
                {globalReadinessData?.departments?.length > 0 && (
                  <div className="bg-white rounded-xl border p-3">
                    <div className="flex items-center gap-2 overflow-x-auto scroll-smooth pb-1">
                      {/* "All Departments" pill — only when multiple departments */}
                      {globalReadinessData.departments.length > 1 && (() => {
                        const isActive = selectedDepartments.length === 0;
                        return (
                        <>
                          <button
                            onClick={() => handleDepartmentSwitch([])}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 border ${
                              isActive
                                ? 'bg-red-600 text-white border-red-600 shadow-sm'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:text-red-600'
                            }`}
                          >
                            <Building2 className="w-3 h-3" />
                            All Departments
                            <span className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              isActive
                                ? 'bg-white/20 text-white'
                                : globalReadinessData.overall.locked_employees === globalReadinessData.overall.total_employees
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-600'
                            }`}>
                              {globalReadinessData.overall.locked_employees}/{globalReadinessData.overall.total_employees}
                              {globalReadinessData.overall.locked_employees === globalReadinessData.overall.total_employees && ' ✓'}
                            </span>
                            {globalReadinessData.overall.mid_period_leavers > 0 && (
                              <span
                                className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${
                                  isActive ? 'text-white/80' : 'text-gray-400'
                                }`}
                                title={`${globalReadinessData.overall.mid_period_leavers} mid-period leaver${globalReadinessData.overall.mid_period_leavers === 1 ? '' : 's'} — handled via Final Settlements`}
                                aria-label={`${globalReadinessData.overall.mid_period_leavers} mid-period leaver${globalReadinessData.overall.mid_period_leavers === 1 ? '' : 's'} handled via Final Settlements`}
                              >
                                <UserMinus className="w-2.5 h-2.5" aria-hidden="true" />
                                {globalReadinessData.overall.mid_period_leavers}
                              </span>
                            )}
                          </button>
                          <div className="w-px h-5 bg-gray-200 mx-1" />
                        </>
                        );
                      })()}

                      {/* Per-department pills */}
                      {globalReadinessData.departments.map(dept => {
                        const isActive = selectedDepartments.length === 1 && selectedDepartments[0] === dept.id;
                        const allLocked = dept.locked_employees === dept.total_employees && dept.total_employees > 0;
                        return (
                          <button
                            key={dept.id}
                            onClick={() => handleDepartmentSwitch([dept.id])}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 border ${
                              isActive
                                ? 'bg-red-600 text-white border-red-600 shadow-sm'
                                : allLocked
                                  ? 'bg-green-50 text-green-700 border-green-200 hover:border-green-400'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:text-red-600'
                            }`}
                          >
                            {dept.name}
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              isActive
                                ? 'bg-white/20 text-white'
                                : allLocked
                                  ? 'bg-green-100 text-green-700'
                                  : dept.locked_employees > 0
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-500'
                            }`}>
                              {dept.locked_employees}/{dept.total_employees}
                              {allLocked && ' ✓'}
                            </span>
                            {dept.mid_period_leavers > 0 && (
                              <span
                                className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${
                                  isActive ? 'text-white/80' : 'text-gray-400'
                                }`}
                                title={`${dept.mid_period_leavers} mid-period leaver${dept.mid_period_leavers === 1 ? '' : 's'} — handled via Final Settlements`}
                                aria-label={`${dept.mid_period_leavers} mid-period leaver${dept.mid_period_leavers === 1 ? '' : 's'} handled via Final Settlements`}
                              >
                                <UserMinus className="w-2.5 h-2.5" aria-hidden="true" />
                                {dept.mid_period_leavers}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <AdjustAndLockTable {...getTableProps()} />

                {/* Unsaved edits guard when switching departments */}
                <ConfirmModal
                  isOpen={pendingDeptSwitch !== null}
                  title="Unsaved Changes"
                  message="You have unsaved attendance edits. Switching departments will discard them. Continue?"
                  confirmLabel="Discard & Switch"
                  variant="danger"
                  onConfirm={confirmDepartmentSwitch}
                  onCancel={() => setPendingDeptSwitch(null)}
                />

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <button
                onClick={() => setActiveStep(1)}
                className="btn btn-admin-outline"
              >
                <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
                Back
              </button>
              <div className="flex items-center gap-3">
                {processingReadiness ? (
                  <div className="flex items-center gap-3">
                    {processingReadiness.staff?.total_count > 0 && (
                      <span className={`text-xs font-medium ${processingReadiness.staff.can_proceed ? 'text-green-600' : 'text-gray-500'}`}>
                        <Lock className="w-3 h-3 inline mr-1" />
                        Staff: {processingReadiness.staff.locked_count}/{processingReadiness.staff.total_count}
                        {processingReadiness.staff.pending_exceptions > 0 && (
                          <span className="text-amber-600 ml-1">
                            ({processingReadiness.staff.pending_exceptions} exc.)
                          </span>
                        )}
                      </span>
                    )}
                    {processingReadiness.workmen?.total_count > 0 && (
                      <span className={`text-xs font-medium ${processingReadiness.workmen.can_proceed ? 'text-green-600' : 'text-gray-500'}`}>
                        <Lock className="w-3 h-3 inline mr-1" />
                        Workmen: {processingReadiness.workmen.locked_count}/{processingReadiness.workmen.total_count}
                        {processingReadiness.workmen.pending_exceptions > 0 && (
                          <span className="text-amber-600 ml-1">
                            ({processingReadiness.workmen.pending_exceptions} exc.)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                ) : globalLocksSummary.total > 0 && (
                  <span className="text-xs text-gray-500">
                    <Lock className="w-3 h-3 inline mr-1" />
                    {globalLocksSummary.locked}/{globalLocksSummary.total} employees locked globally
                  </span>
                )}
                <button
                  onClick={() => setActiveStep(3)}
                  disabled={!canProceedToCalculation}
                  className="btn btn-admin-primary"
                  title={
                    !canProceedToCalculation
                      ? 'Lock at least one category of employees before calculating'
                      : ''
                  }
                >
                  {(() => {
                    if (!processingReadiness) {
                      return globalLocksSummary.locked > 0 && globalLocksSummary.locked < globalLocksSummary.total
                        ? `Calculate ${globalLocksSummary.locked} Locked Employee${globalLocksSummary.locked !== 1 ? 's' : ''}`
                        : 'Continue to Calculate';
                    }
                    const parts = [];
                    if (processingReadiness.staff?.can_proceed) parts.push('Staff');
                    if (processingReadiness.workmen?.can_proceed) parts.push('Workmen');
                    return parts.length > 0
                      ? `Calculate ${parts.join(' & ')} Payroll`
                      : 'Continue to Calculate';
                  })()}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>

        </div>
        )}

            {/* Step 3: Calculate Payroll - HR Only */}
            {activeStep === 3 && (
      <div className="space-y-6">
                {/* HR Access Check */}
                {!(user?.is_superuser || user?.has_admin_access || user?.has_hr_access) ? (
                  <div className="bg-red-50 rounded-xl border border-red-200 p-6 text-center">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-red-900 mb-2">Access Restricted</h3>
                    <p className="text-sm text-red-700 mb-4">
                      Step 3 (Calculate Payroll) is restricted to HR users only. 
                      Finance users can access Step 4 (Review & Payment) after HR completes the calculations.
                    </p>
                    <button
                      onClick={() => setActiveStep(4)}
                      className="btn btn-admin-primary"
                    >
                      Go to Step 4: Review & Payment
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                {/* ── Department Tab Bar (same mental model as Step 2) ── */}
                {payrollDepartments.length > 0 && (
                  <div className="bg-white rounded-2xl border p-3">
                    <div className="flex items-center gap-2 overflow-x-auto scroll-smooth pb-1">
                      {payrollDepartments.length > 1 && (() => {
                        const isActive = payrollDeptFilter == null;
                        const totalCount = payrollDepartments.reduce((s, d) => s + d.count, 0);
                        const totalFinalized = payrollDepartments.reduce((s, d) => s + d.finalizedCount, 0);
                        const allFinalized = totalFinalized === totalCount && totalCount > 0;
                        return (
                          <>
                            <button
                              onClick={() => setPayrollDeptFilter(null)}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 border ${
                                isActive
                                  ? 'bg-red-600 text-white border-red-600 shadow-sm'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:text-red-600'
                              }`}
                            >
                              <Building2 className="w-3 h-3" />
                              All Departments
                              <span className={`ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                                isActive
                                  ? 'bg-white/20 text-white'
                                  : allFinalized
                                    ? 'bg-green-100 text-green-700'
                                    : totalFinalized > 0
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-600'
                              }`}>
                                {totalFinalized}/{totalCount}
                                {allFinalized && ' ✓'}
                              </span>
                            </button>
                            <div className="w-px h-5 bg-gray-200 mx-1" />
                          </>
                        );
                      })()}
                      {payrollDepartments.map(dept => {
                        const isActive = payrollDeptFilter === dept.id;
                        const allFinalized = dept.finalizedCount === dept.count && dept.count > 0;
                        return (
                          <button
                            key={dept.id}
                            onClick={() => setPayrollDeptFilter(isActive ? null : dept.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 border ${
                              isActive
                                ? 'bg-red-600 text-white border-red-600 shadow-sm'
                                : allFinalized
                                  ? 'bg-green-50 text-green-700 border-green-200 hover:border-green-400'
                                  : 'bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:text-red-600'
                            }`}
                          >
                            {dept.name}
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              isActive
                                ? 'bg-white/20 text-white'
                                : allFinalized
                                  ? 'bg-green-100 text-green-700'
                                  : dept.finalizedCount > 0
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-500'
                            }`}>
                              {dept.finalizedCount}/{dept.count}
                              {allFinalized && ' ✓'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Step 2 ↔ Step 3 reconciliation banner ──
                    Surfaces any saved EmployeePayroll rows whose values
                    disagree with the AR / leave-session truth that
                    Step 2 displays. Drift here is the W0218-class bug:
                    a stale row (calculated before a fix landed, or
                    before Step 2 data was edited). The corresponding
                    STEP2_DRIFT PayrollFlag is auto-created with
                    severity='error', so the existing "Continue to
                    Step 4" gate already blocks until resolved — this
                    banner is just the affordance to triage and recalc.
                */}
                {step2Reconciliation && step2Reconciliation.drift_rows > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h4 className="text-sm font-semibold text-red-900">
                            Step 2 ↔ Step 3 drift detected ({step2Reconciliation.drift_rows} employee
                            {step2Reconciliation.drift_rows !== 1 ? 's' : ''})
                          </h4>
                          <button
                            onClick={handleRecalculateDriftedEmployees}
                            className="btn btn-sm bg-red-600 text-white hover:bg-red-700 whitespace-nowrap"
                          >
                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                            Recalculate all drifted
                          </button>
                        </div>
                        <p className="text-xs text-red-700 mb-3">
                          The saved payroll values disagree with the Step 2 source of truth
                          (AR / leave sessions). This is usually a stale row from a calculation that
                          ran before a code fix landed, or Step 2 data that was edited after Step 3
                          last calculated. Step 4 is blocked until every drifted row is recalculated.
                        </p>
                        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                          {step2Reconciliation.drifts.map(rec => (
                            <div
                              key={rec.employee_payroll_id}
                              className="bg-white border border-red-200 rounded-lg px-3 py-2 text-xs"
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-semibold text-gray-900">
                                  {rec.staff_id} — {rec.staff_name}
                                </span>
                                <span className="text-[10px] font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                                  {rec.drift_count} field{rec.drift_count !== 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="space-y-0.5">
                                {rec.drifts.map(d => (
                                  <div key={d.field} className="flex items-center gap-2 text-[11px] text-gray-700">
                                    <span className="font-medium text-gray-800 min-w-[140px]">
                                      {d.label}:
                                    </span>
                                    <span className="text-gray-600">
                                      Step 2 = <span className="font-semibold text-gray-900">{d.step2_value}</span>,{' '}
                                      Step 3 = <span className="font-semibold text-gray-900">{d.step3_value}</span>
                                    </span>
                                    <span className={`font-semibold ${
                                      Number(d.delta) > 0 ? 'text-orange-700' : 'text-blue-700'
                                    }`}>
                                      (Δ {Number(d.delta) >= 0 ? '+' : ''}{d.delta} {d.unit})
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header */}
                <div className="space-y-2">
        <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <DollarSign className="w-5 h-5 text-red-600 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">Calculate Payroll</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleMasterListExportXLSX}
                        disabled={masterListExporting || !isCalculationComplete()}
                        className="btn btn-sm btn-admin-outline"
                        title={!isCalculationComplete() ? 'Run payroll first' : 'Export Worker Payroll Register (.xlsx)'}
                      >
                        {masterListExporting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4" />
                        )}
                        Worker Excel
                      </button>
                      <button
                        onClick={handleMasterListExportPDF}
                        disabled={masterListPdfExporting || !isCalculationComplete()}
                        className="btn btn-sm btn-admin-outline"
                        title={!isCalculationComplete() ? 'Run payroll first' : 'Export Worker Payroll Register (.pdf)'}
                      >
                        {masterListPdfExporting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                        Worker PDF
                      </button>
                      <span className="w-px h-5 bg-gray-300" aria-hidden="true" />
                      <button
                        onClick={handleStaffRegisterExportXLSX}
                        disabled={staffRegisterExporting || !isCalculationComplete()}
                        className="btn btn-sm btn-admin-outline"
                        title={!isCalculationComplete() ? 'Run payroll first' : 'Export Staff Payroll Register (.xlsx)'}
                      >
                        {staffRegisterExporting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4" />
                        )}
                        Staff Excel
                      </button>
                      <button
                        onClick={handleStaffRegisterExportPDF}
                        disabled={staffRegisterPdfExporting || !isCalculationComplete()}
                        className="btn btn-sm btn-admin-outline"
                        title={!isCalculationComplete() ? 'Run payroll first' : 'Export Staff Payroll Register (.pdf)'}
                      >
                        {staffRegisterPdfExporting ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                        Staff PDF
                      </button>
                    <button
                      onClick={async () => {
                        // Validate period is selected
                        if (!selectedPeriod) {
                          toast.error('Please select a period first.');
                          return;
                        }
                        
                        // Parse selectedPeriod (YYYY-MM format) to get date range
                        const [year, month] = selectedPeriod.split('-');
                        const periodStart = `${year}-${month}-01`;
                        const lastDay = new Date(year, month, 0).getDate();
                        const periodEnd = `${year}-${month}-${lastDay}`;
                        
                        // Calculate pay date (7 days after period end - MOM requirement)
                        const payDateObj = new Date(year, month - 1, lastDay + 7);
                        const payDate = formatDateForInput(payDateObj);
                        
                        // Generate period name
                        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];
                        const periodName = `${monthNames[parseInt(month) - 1]} ${year}`;
                        
                        // Check if PayrollRun already exists for this period
                        const matchingPeriod = payrollPeriods?.find(p => 
                          p.period_start_date === periodStart && 
                          p.period_end_date === periodEnd
                        );
                        
                        // Helper function to run payroll calculation
                        const runCalculation = (payrollRunId, forceRecalculate = false) => {
                          setCurrentPayrollRunId(payrollRunId);
                          runPayrollMutation.mutate(
                            { 
                              payrollRunId,
                              departmentId: null,
                              forceRecalculate
                            },
                            {
                              onSuccess: (data) => {
                                setCalculationTaskId(data.task_id);
                                setShowCalcModal(true);
                              },
                              onError: () => {
                                setCalculationTaskId(null);
                                setCurrentPayrollRunId(null);
                                setShowCalcModal(false);
                              }
                            }
                          );
                        };
                        
                        if (matchingPeriod?.first_run_id) {
                          // PayrollRun exists — recalculate (overwrite) the same run
                          runCalculation(matchingPeriod.first_run_id, true);
                        } else {
                          // PayrollRun doesn't exist - auto-create it first
                          toast.loading(`Creating payroll run for ${periodName}...`, { id: 'create-period' });
                          
                          createPeriodMutation.mutate(
                            {
                              period_start_date: periodStart,
                              period_end_date: periodEnd,
                              pay_date: payDate,
                              period_name: periodName
                            },
                            {
                              onSuccess: (newPeriod) => {
                                toast.success(`Payroll run created for ${periodName}`, { id: 'create-period' });
                                // Run calculation with the newly created PayrollRun
                                runCalculation(newPeriod.id);
                              },
                              onError: (error) => {
                                toast.error(`Failed to create payroll run: ${error.response?.data?.detail || error.message}`, { id: 'create-period' });
                              }
                            }
                          );
                        }
                      }}
                      disabled={!selectedPeriod || runPayrollMutation.isPending || createPeriodMutation.isPending || calculationTaskId}
                      className="btn btn-admin-primary"
                      title={employeePayrolls.length > 0 ? 'Re-run payroll calculations for the selected period' : 'Run payroll calculations for the selected period'}
                    >
                      {createPeriodMutation.isPending ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Creating Period…
                        </>
                      ) : (runPayrollMutation.isPending || (calculationTaskId && calculationStatus?.state === 'PROGRESS')) ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Processing…
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4 mr-2" />
                          {employeePayrolls.length > 0 ? 'Re-run Payroll' : 'Run Payroll'}
                        </>
                      )}
                    </button>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Prorates salary, applies allowances, overtime, leave, and deductions (including statutory) to derive net pay.
                  </p>
        </div>


              {/* Progress, success, and error states are now handled by the ProgressModal */}

              {/* ═══════════════════════════════════════════════════════════
                  PAYROLL REGISTER — Unified view
                  Shows summary stats, department progress, and payroll data
                  together. No binary gate — renders progressively.
                  ═══════════════════════════════════════════════════════════ */}

              {/* Summary Stats Cards — DS compact metrics (5-col payroll strip)
                  Each card hover-reveals the calculation ladder with actual
                  figures so HR can see exactly how the headline number was
                  built. Cards stay scoped to the regular run only — see the
                  period-total strip below for the regular + final-settlement
                  total that ties to PLC / Worker Payroll Register / GL. */}
              {currentPayrollRunId && departmentScopedPayrolls.length > 0 && (() => {
                const regularPeriodLabel = (() => {
                  const r = payrollRuns?.find(x => x.id === currentPayrollRunId);
                  if (!r?.period_start_date) return '';
                  return new Date(r.period_start_date).toLocaleString('en-SG', { month: 'long', year: 'numeric' });
                })();
                const scopeLabel = payrollFilter === 'staff' ? 'staff'
                  : payrollFilter === 'worker' ? 'workers'
                  : 'employees';
                const peopleNoun = payrollFilter === 'staff' ? 'Staff'
                  : payrollFilter === 'worker' ? 'Workers'
                  : 'Employees';
                const a = payrollAggregates;
                // Tiny renderer to keep tooltip rows visually consistent.
                const Row = ({ label, value, tone, indent }) => (
                  <div className={`flex justify-between gap-4 ${indent ? 'pl-2' : ''}`}>
                    <span className={tone === 'muted' ? 'text-gray-400' : 'text-gray-300'}>{label}</span>
                    <span className={`tabular-nums ${tone === 'subtract' ? 'text-red-300' : tone === 'add' ? 'text-green-300' : 'text-white'}`}>{value}</span>
                  </div>
                );
                const Divider = () => <div className="border-t border-gray-700 my-1.5" />;
                return (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* ── 1. Workers / Staff / Employees count ── */}
                  <div className="tooltip-container bg-white rounded-2xl shadow-sm border p-4 cursor-help" style={{ display: 'block' }}>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="text-lg font-bold text-gray-900 tabular-nums">{departmentScopedPayrolls.length}</p>
                        <p className="text-xs text-gray-600">{peopleNoun} <span className="text-gray-400">· regular run</span></p>
                      </div>
                    </div>
                    <div className="tooltip-content tooltip-bottom-start" style={{ minWidth: '280px' }}>
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="font-semibold text-white mb-1">{peopleNoun} in scope</div>
                        <Row label="Total" value={departmentScopedPayrolls.length} />
                        {payrollFilter === 'all' && (
                          <>
                            <Row label="• Staff" value={payrollFilterCounts.staff} indent />
                            <Row label="• Workers" value={payrollFilterCounts.worker} indent />
                          </>
                        )}
                        <Divider />
                        <p className="text-gray-400 leading-snug">
                          Regular {regularPeriodLabel || 'monthly'} run only. Mid-period leavers are
                          processed as Final Settlements on a separate run.
                        </p>
                        {fsAggregates.count > 0 && (
                          <p className="text-blue-300 leading-snug pt-1">
                            + {fsAggregates.count} on Final Settlement (see strip below).
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── 2. Gross Pay ── */}
                  <div className="tooltip-container bg-white rounded-2xl shadow-sm border p-4 cursor-help" style={{ display: 'block' }}>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="text-lg font-bold text-gray-900 tabular-nums truncate">{formatCurrency(a.grossTotal)}</p>
                        <p className="text-xs text-gray-600">Gross Pay <span className="text-gray-400">· before deductions</span></p>
                      </div>
                    </div>
                    <div className="tooltip-content tooltip-bottom" style={{ minWidth: '300px' }}>
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="font-semibold text-white mb-1">Gross Pay build-up</div>
                        <Row label="Basic Salary (monthly)" value={formatCurrency(a.basicTotal)} />
                        {a.allowancesTotal > 0 && <Row label="+ Allowances" value={formatCurrency(a.allowancesTotal)} tone="add" />}
                        {a.overtimeTotal > 0 && <Row label="+ Overtime Pay" value={formatCurrency(a.overtimeTotal)} tone="add" />}
                        {a.restDayTotal > 0 && <Row label="+ Rest Day Pay" value={formatCurrency(a.restDayTotal)} tone="add" />}
                        {a.publicHolidayTotal > 0 && <Row label="+ Public Holiday Pay" value={formatCurrency(a.publicHolidayTotal)} tone="add" />}
                        {a.bonusTotal > 0 && <Row label="+ Bonus" value={formatCurrency(a.bonusTotal)} tone="add" />}
                        {a.commissionTotal > 0 && <Row label="+ Commission" value={formatCurrency(a.commissionTotal)} tone="add" />}
                        {a.otherPaymentsTotal > 0 && <Row label="+ Other Payments" value={formatCurrency(a.otherPaymentsTotal)} tone="add" />}
                        <Divider />
                        <Row label="= Gross Pay" value={formatCurrency(a.grossTotal)} />
                        <p className="text-gray-400 leading-snug pt-1">
                          Gross is wage cost <em>before</em> any deductions. CPF, SDL and FWL are
                          computed off this base.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── 3. Deductions ── */}
                  <div className="tooltip-container bg-white rounded-2xl shadow-sm border p-4 cursor-help" style={{ display: 'block' }}>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-5 h-5 text-red-600 rotate-180" />
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="text-lg font-bold text-red-600 tabular-nums truncate">-{formatCurrency(a.deductionsTotal)}</p>
                        <p className="text-xs text-gray-600">Deductions <span className="text-gray-400">· employee-paid</span></p>
                      </div>
                    </div>
                    <div className="tooltip-content tooltip-bottom" style={{ minWidth: '300px' }}>
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="font-semibold text-white mb-1">Total Deductions breakdown</div>
                        {a.cpfTotal > 0 && <Row label="CPF (Employee)" value={formatCurrency(a.cpfTotal)} tone="subtract" />}
                        {a.shgTotal > 0 && <Row label="SHG (CDAC / SINDA / MBMF / ECF)" value={formatCurrency(a.shgTotal)} tone="subtract" />}
                        {a.incomeTaxTotal > 0 && <Row label="Income Tax" value={formatCurrency(a.incomeTaxTotal)} tone="subtract" />}
                        {a.leaveDeductionTotal > 0 && <Row label="Leave Deduction (UL / absence)" value={formatCurrency(a.leaveDeductionTotal)} tone="subtract" />}
                        {a.nsDeductionTotal > 0 && <Row label="NS Deduction (ICT)" value={formatCurrency(a.nsDeductionTotal)} tone="subtract" />}
                        {a.ir21DeductionTotal > 0 && <Row label="IR21 (IRAS Clearance)" value={formatCurrency(a.ir21DeductionTotal)} tone="subtract" />}
                        {a.workPermitFeeTotal > 0 && <Row label="Work Permit Replacement" value={formatCurrency(a.workPermitFeeTotal)} tone="subtract" />}
                        {a.sponsorshipBondTotal > 0 && <Row label="Sponsorship Bond" value={formatCurrency(a.sponsorshipBondTotal)} tone="subtract" />}
                        {a.otherDeductionsTotal > 0 && <Row label="Other Deductions (phone bill, etc.)" value={formatCurrency(a.otherDeductionsTotal)} tone="subtract" />}
                        <Divider />
                        <Row label="= Total Deductions" value={formatCurrency(a.deductionsTotal)} tone="subtract" />
                        <p className="text-gray-400 leading-snug pt-1">
                          SDL &amp; Foreign Worker Levy are <strong>employer-paid</strong> and tracked
                          separately for compliance — not in this total.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── 4. Net Pay ── */}
                  <div className="tooltip-container bg-white rounded-2xl shadow-sm border p-4 cursor-help" style={{ display: 'block' }}>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="text-lg font-bold text-gray-900 tabular-nums truncate">{formatCurrency(a.netTotal)}</p>
                        <p className="text-xs text-gray-600">Net Pay <span className="text-gray-400">· gross − deductions</span></p>
                      </div>
                    </div>
                    <div className="tooltip-content tooltip-bottom" style={{ minWidth: '300px' }}>
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="font-semibold text-white mb-1">Net Pay (take-home)</div>
                        <Row label="Gross Pay" value={formatCurrency(a.grossTotal)} />
                        <Row label="− Total Deductions" value={formatCurrency(a.deductionsTotal)} tone="subtract" />
                        <Divider />
                        <Row label="= Net Pay" value={formatCurrency(a.netTotal)} />
                        <p className="text-gray-400 leading-snug pt-1">
                          Net Pay is the wage component of take-home — the base for IR8A
                          employment income. Reimbursements &amp; AL encashment are <em>added on
                          top</em> in Final Payment.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ── 5. Final Payment ── */}
                  <div className="tooltip-container bg-white rounded-2xl shadow-sm border border-green-200 p-4 cursor-help" style={{ display: 'block' }}>
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="ml-3 min-w-0">
                        <p className="text-lg font-bold text-green-700 tabular-nums truncate">{formatCurrency(a.finalTotal)}</p>
                        <p className="text-xs text-green-600">Final Payment <span className="text-green-600/70">· cash to bank</span></p>
                      </div>
                    </div>
                    <div className="tooltip-content tooltip-bottom-end" style={{ minWidth: '300px' }}>
                      <div className="tooltip-arrow"></div>
                      <div className="text-xs space-y-1">
                        <div className="font-semibold text-white mb-1">Final Payment (bank file total)</div>
                        <Row label="Net Pay" value={formatCurrency(a.netTotal)} />
                        <Row label="+ Reimbursements" value={formatCurrency(a.reimbursementTotal)} tone={a.reimbursementTotal > 0 ? 'add' : 'muted'} />
                        <Row label="+ Leave Encashment" value={formatCurrency(a.encashmentTotal)} tone={a.encashmentTotal > 0 ? 'add' : 'muted'} />
                        <Divider />
                        <Row label="= Final Payment" value={formatCurrency(a.finalTotal)} />
                        <p className="text-gray-400 leading-snug pt-1">
                          Regular run only ({departmentScopedPayrolls.length} {scopeLabel}).
                          Encashment &amp; reimbursements are usually $0 here — they appear on
                          Final Settlements.
                        </p>
                        {fsAggregates.count > 0 && (
                          <p className="text-amber-300 leading-snug pt-1">
                            <strong>{regularPeriodLabel || 'Period'} period total</strong> (incl. {fsAggregates.count}
                            {' '}FS leaver{fsAggregates.count === 1 ? '' : 's'}):
                            {' '}{formatCurrency(a.finalTotal + fsAggregates.finalTotal)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* ── Period reconciliation strip ──
                  Compact amber strip surfacing the two PERIOD-level totals
                  senior Finance / HR reconcile against external books:
                    • Salary Cost  → ties to PLC · Salary Cost tab
                    • Cash Outflow → ties to Worker Payroll Register / bank file

                  These are deliberately SEPARATE from the 5 white cards above:
                    • White cards = regular-run only (e.g. 139 emp); the
                      Gross → Deductions → Net → Final ladder ties out
                      across that row by construction.
                    • Amber strip = period rollup (regular + final-settlement
                      leavers, e.g. 142 emp).  Salary Cost and Cash Outflow
                      are intentionally DIFFERENT bases (cost vs cash) and
                      are NOT supposed to tie to each other — see
                      `.cursor/rules/plc-salary-cost-basis.mdc`.
                  Putting them in the same row as the 5 white cards would
                  conflate scopes and invite a false reconciliation.

                  Cash-outflow column only renders when FS leavers exist
                  (otherwise it's identical to the regular run's Final
                  Payment card above and would be redundant). */}
              {currentPayrollRunId && departmentScopedPayrolls.length > 0 && (() => {
                const regularRun = payrollRuns?.find(r => r.id === currentPayrollRunId);
                const periodLabel = regularRun?.period_start_date
                  ? new Date(regularRun.period_start_date).toLocaleString('en-SG', { month: 'short', year: 'numeric' })
                  : 'this period';
                const regSC = payrollAggregates.salaryCostTotal || 0;
                const fsSC = fsAggregates.salaryCostTotal || 0;
                const periodSalaryCost = regSC + fsSC;
                const periodCashOutflow = payrollAggregates.finalTotal + fsAggregates.finalTotal;
                const totalHeadcount = departmentScopedPayrolls.length + fsAggregates.count;
                const showCashOutflow = fsAggregates.count > 0;
                const a = payrollAggregates;
                const scopeLabel = payrollFilter === 'staff' ? 'staff'
                  : payrollFilter === 'worker' ? 'workers'
                  : 'employees';
                const Row = ({ label, value, tone, indent }) => (
                  <div className={`flex justify-between gap-4 ${indent ? 'pl-2' : ''}`}>
                    <span className={tone === 'muted' ? 'text-gray-400' : 'text-gray-300'}>{label}</span>
                    <span className={`tabular-nums ${tone === 'subtract' ? 'text-red-300' : tone === 'add' ? 'text-green-300' : 'text-white'}`}>{value}</span>
                  </div>
                );
                const Divider = () => <div className="border-t border-gray-700 my-1.5" />;
                return (
                  <div className={`bg-slate-100 ring-1 ring-inset ring-slate-300 rounded-2xl px-4 py-2.5 grid gap-x-6 gap-y-2 ${showCashOutflow ? 'lg:grid-cols-2' : 'grid-cols-1'}`}>
                    {/* ── Salary Cost (PLC tie-out) ── */}
                    <div className="tooltip-container flex items-center gap-2.5 cursor-help min-w-0">
                      <div className="w-7 h-7 rounded-full bg-white ring-1 ring-slate-300 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-3.5 h-3.5 text-slate-700" />
                      </div>
                      <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">
                          {periodLabel} · Salary Cost
                        </span>
                        <span className="text-base font-bold text-slate-900 tabular-nums">
                          {formatCurrency(periodSalaryCost)}
                        </span>
                        <span className="text-[10px] text-slate-500 whitespace-nowrap">
                          · {totalHeadcount} {totalHeadcount === 1 ? 'emp' : 'emp'}
                        </span>
                      </div>
                      {showCashOutflow && (
                        <div className="hidden md:flex flex-shrink-0 items-center gap-1.5 text-[10px] text-slate-500 tabular-nums whitespace-nowrap">
                          <span>Reg {departmentScopedPayrolls.length} · <span className="font-semibold text-slate-900">{formatCurrency(regSC)}</span></span>
                          <span className="text-slate-400">+</span>
                          <span>FS {fsAggregates.count} · <span className="font-semibold text-slate-900">{formatCurrency(fsSC)}</span></span>
                        </div>
                      )}
                      <div className="tooltip-content tooltip-bottom-start" style={{ minWidth: '320px' }}>
                        <div className="tooltip-arrow"></div>
                        <div className="text-xs space-y-1">
                          <div className="font-semibold text-white mb-1">Salary Cost (PLC labour-cost basis)</div>
                          <div className="text-gray-400 leading-snug pb-1">Regular run ({departmentScopedPayrolls.length} {scopeLabel}):</div>
                          <Row label="Basic salary (monthly)" value={formatCurrency(a.basicTotal)} indent />
                          <Row label="− Leave Deduction" value={formatCurrency(a.leaveDeductionTotal)} tone="subtract" indent />
                          <Row label="+ Overtime Pay" value={formatCurrency(a.overtimeTotal)} tone="add" indent />
                          <Row label="+ Rest-Day Pay" value={formatCurrency(a.restDayTotal)} tone="add" indent />
                          <Row label="+ Public Holiday Pay" value={formatCurrency(a.publicHolidayTotal)} tone={a.publicHolidayTotal > 0 ? 'add' : 'muted'} indent />
                          <Row label="+ Qualifying Allowances" value={formatCurrency(a.allowancesTotal)} tone={a.allowancesTotal > 0 ? 'add' : 'muted'} indent />
                          <Divider />
                          <Row label="= Regular Salary Cost" value={formatCurrency(regSC)} />
                          {fsAggregates.count > 0 && (
                            <>
                              <div className="text-gray-400 leading-snug pt-2">
                                + Final Settlement leavers ({fsAggregates.count}, basic_salary_earned):
                              </div>
                              <Row label="+ FS Salary Cost" value={formatCurrency(fsSC)} tone="add" indent />
                              <Divider />
                            </>
                          )}
                          <Row label="= Period Salary Cost" value={formatCurrency(periodSalaryCost)} />
                          <p className="text-gray-400 leading-snug pt-1">
                            Ties to PLC report · <span className="font-medium">Salary Cost</span> tab.
                            EXCLUDES leave encashment, reimbursements, other deductions
                            (phone bill etc.), CPF/SHG/income tax, IR21 — those are
                            wages routed elsewhere on the GL.
                          </p>
                          {showCashOutflow && Math.abs(cashOutflowBridge.netDelta) > 0.005 && (
                            <p className="text-gray-500 leading-snug pt-1 italic">
                              Hover the Cash Outflow chip → for the
                              cash-to-labour-cost reconciliation
                              ({formatCurrency(cashOutflowBridge.netDelta)} net bridge).
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ── Total Labour Cash Outflow (bank file tie-out) ──
                          Reconciliation tooltip on the headline span shows the
                          dual-direction bridge back to PLC Salary Cost so HR
                          can audit "what each cash dollar maps to on the GL"
                          without leaving the screen.  Mirrors the diag script
                          `_diag_apr2026_salary_cost_vs_cash_outflow.py` and
                          the backend helper `build_cash_outflow_bridge`. */}
                    {showCashOutflow && (
                      <div className="flex items-center gap-2.5 min-w-0 lg:border-l lg:border-slate-300 lg:pl-6">
                        {/* Icon + headline grouped in a single
                            tooltip-container (mirrors the Salary Cost
                            chip layout above) so hover anywhere on the
                            cluster — icon, label, value, headcount —
                            surfaces the reconciliation tooltip with a
                            cursor-help affordance.  The right-side
                            Reg + FS badge is kept OUTSIDE this wrapper
                            because the FS button has its own
                            tooltip-container; nesting them would
                            double-trigger both tooltips on FS hover. */}
                        <div className="tooltip-container cursor-help flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-7 h-7 rounded-full bg-white ring-1 ring-slate-300 flex items-center justify-center flex-shrink-0">
                            <BarChart3 className="w-3.5 h-3.5 text-slate-700" />
                          </div>
                          <div className="min-w-0 flex-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 whitespace-nowrap">
                              {periodLabel} · Total Labour Cash Outflow
                            </span>
                            <span className="text-base font-bold text-slate-900 tabular-nums">
                              {formatCurrency(periodCashOutflow)}
                            </span>
                            <span className="text-[10px] text-slate-500 whitespace-nowrap">
                              · {totalHeadcount} emp
                            </span>
                          </div>

                          {/* Cash Outflow → Salary Cost reconciliation. */}
                          {(() => {
                            const b = cashOutflowBridge;
                            // Render only the rows that are non-zero so the
                            // panel stays compact on cohorts (e.g. workers)
                            // where most CPF/SHG/tax lines are $0.
                            const addRows = [
                              ['− Non-qualifying Allowances', b.adds.nonQualifyingAllowances],
                              ['− Bonus Payments', b.adds.bonus],
                              ['− Commission', b.adds.commission],
                              ['− Other Earnings', b.adds.otherEarnings],
                              ['− Leave Encashment', b.adds.leaveEncashment],
                              ['− Reimbursements', b.adds.reimbursements],
                            ].filter(([, v]) => Math.abs(v) > 0.005);
                            const subRows = [
                              ['+ Employee CPF', b.subs.employeeCPF],
                              ['+ SHG (CDAC/MBMF/SINDA/ECF)', b.subs.shg],
                              ['+ Income Tax', b.subs.incomeTax],
                              ['+ IR21 (tax clearance)', b.subs.ir21],
                              ['+ NS Deduction', b.subs.nsDeduction],
                              ['+ Work Permit Replacement Fee', b.subs.workPermitFee],
                              ['+ Sponsorship Bond', b.subs.sponsorshipBond],
                              ['+ Other Deductions (phone bill etc.)', b.subs.otherDeductions],
                            ].filter(([, v]) => Math.abs(v) > 0.005);
                            return (
                              <div className="tooltip-content tooltip-bottom-end" style={{ minWidth: '360px' }}>
                                <div className="tooltip-arrow"></div>
                                <div className="text-xs space-y-1">
                                  <div className="font-semibold text-white mb-1">
                                    Cash Outflow → PLC Salary Cost reconciliation
                                  </div>
                                  <Row label="Total Labour Cash Outflow" value={formatCurrency(b.cashOutflow)} />
                                  {addRows.length > 0 && (
                                    <>
                                      <div className="text-gray-400 leading-snug pt-1">
                                        Paid in cash, but NOT labour cost:
                                      </div>
                                      {addRows.map(([label, v]) => (
                                        <Row key={label} label={label} value={formatCurrency(v)} tone="subtract" indent />
                                      ))}
                                    </>
                                  )}
                                  {subRows.length > 0 && (
                                    <>
                                      <div className="text-gray-400 leading-snug pt-1">
                                        IS labour cost, but withheld from cash:
                                      </div>
                                      {subRows.map(([label, v]) => (
                                        <Row key={label} label={label} value={formatCurrency(v)} tone="add" indent />
                                      ))}
                                    </>
                                  )}
                                  <Divider />
                                  <Row label="= PLC Salary Cost" value={formatCurrency(b.bridgedSalaryCost)} />
                                  <div className="flex justify-between gap-4 text-[10px] text-gray-500 pt-0.5">
                                    <span>Net bridge (Cash − Salary Cost)</span>
                                    <span className="tabular-nums">{formatCurrency(b.netDelta)}</span>
                                  </div>
                                  {Math.abs(b.residual) > 0.05 && (
                                    <div className="mt-1.5 px-2 py-1 rounded bg-amber-900/40 ring-1 ring-amber-600/50 text-amber-200 text-[10px] leading-snug">
                                      ⚠ Bridge residual: {formatCurrency(b.residual)}.
                                      An earning or deduction type isn't classified
                                      into either bucket — investigate.
                                    </div>
                                  )}
                                  <p className="text-gray-400 leading-snug pt-1">
                                    Bank file ties to <span className="font-medium">Cash Outflow</span>.
                                    GL labour line ties to <span className="font-medium">Salary Cost</span>.
                                    Both numbers are correct — they measure
                                    different things.
                                  </p>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="hidden md:flex flex-shrink-0 items-center gap-1.5 text-[10px] text-slate-500 tabular-nums whitespace-nowrap">
                          <span>Reg {departmentScopedPayrolls.length} · <span className="font-semibold text-slate-900">{formatCurrency(payrollAggregates.finalTotal)}</span></span>
                          <span className="text-slate-400">+</span>
                          <button
                            type="button"
                            onClick={() => setActiveTab('final-settlements')}
                            className="tooltip-container inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 focus-visible:ring-offset-slate-100 transition-colors group"
                            title="Review the final-settlement leavers contributing to this total"
                          >
                            <span className="group-hover:text-slate-900">FS {fsAggregates.count} · <span className="font-semibold text-slate-900">{formatCurrency(fsAggregates.finalTotal)}</span></span>
                            <ExternalLink className="w-2.5 h-2.5" />
                            {fsAggregates.employees.length > 0 && (
                              <div className="tooltip-content tooltip-top-end" style={{ minWidth: '260px' }}>
                                <div className="tooltip-arrow"></div>
                                <div className="text-xs space-y-1">
                                  <div className="font-semibold text-white mb-1">Final-settlement leavers</div>
                                  {fsAggregates.employees.map(emp => (
                                    <div key={emp.staffId} className="flex justify-between gap-3">
                                      <span className="text-gray-300 truncate">
                                        <span className="text-gray-500">{emp.staffId}</span> {emp.name}
                                      </span>
                                      <span className="text-white tabular-nums">{formatCurrency(emp.finalPayment)}</span>
                                    </div>
                                  ))}
                                  <div className="border-t border-gray-700 my-1.5" />
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-400">Subtotal</span>
                                    <span className="text-white tabular-nums">{formatCurrency(fsAggregates.finalTotal)}</span>
                                  </div>
                                  <p className="text-gray-400 leading-snug pt-1">Matches Worker Payroll Register &amp; bank file. Click to review →</p>
                                </div>
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Payroll Register + Detail Panel
                  Per-department status icon + calculated/expected count + Run/Re-run
                  buttons live on each department group header (single canonical surface). */}
              {currentPayrollRunId && Object.keys(payrollsByDepartment).length > 0 && (
                <div className="flex gap-4" style={{ height: 'calc(100dvh - 240px)', minHeight: '400px' }}>
                  {/* LEFT: Payroll Register Table */}
                  <div className={`bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden transition-all duration-200 ${selectedEmployeePayrollId ? 'w-[55%] flex-shrink-0' : 'flex-1'}`}>
                    {/* Toolbar */}
                    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <DebouncedSearchInput
                          value={payrollSearchTerm}
                          onSearch={setPayrollSearchTerm}
                          placeholder="Search…"
                          theme="admin"
                          className="max-w-xs"
                        />
                        {/* Global calc-progress chip — replaces the standalone Department Progress widget */}
                        {departmentProgress?.summary && departmentProgress.summary.total_expected > 0 && (() => {
                          const { total_calculated = 0, total_expected = 0, total_errors = 0, completion_percentage = 0, is_fully_calculated } = departmentProgress.summary;
                          const isComplete = is_fully_calculated && total_errors === 0;
                          const hasErrors = total_errors > 0;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${
                                isComplete
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : hasErrors
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-gray-50 text-gray-600 border border-gray-200'
                              }`}
                              title={hasErrors ? `${total_errors} error${total_errors === 1 ? '' : 's'} need resolution` : `${total_calculated} of ${total_expected} employees calculated (${completion_percentage}%)`}
                            >
                              {isComplete ? <CheckCircle className="w-3 h-3" /> : hasErrors ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              <span className="tabular-nums">{total_calculated}/{total_expected}</span>
                              {!isComplete && <span className="text-gray-400">· {completion_percentage}%</span>}
                              {isComplete && <span>complete</span>}
                            </span>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2">
                        <PillToggle
                          value={payrollFilter}
                          onChange={setPayrollFilter}
                          options={[
                            { id: 'all', label: 'All', count: payrollFilterCounts.all },
                            { id: 'staff', label: 'Staff', count: payrollFilterCounts.staff },
                            { id: 'worker', label: 'Worker', count: payrollFilterCounts.worker },
                          ]}
                          variant="text"
                          theme="admin"
                          ariaLabel="Filter by employee classification"
                        />
                        {visibleDeptIds.length > 1 && (
                          <button
                            onClick={toggleAllPayrollDepartments}
                            className="icon-button-admin"
                            title={allPayrollDeptsExpanded ? 'Collapse All' : 'Expand All'}
                            aria-label={allPayrollDeptsExpanded ? 'Collapse All' : 'Expand All'}
                          >
                            {allPayrollDeptsExpanded ? <ChevronsUp className="w-4 h-4" /> : <ChevronsDown className="w-4 h-4" />}
                          </button>
                        )}
                        {employeePayrolls.length > 0 && (
                          unfinalizedPayrollCount > 0 ? (
                            <button
                              onClick={() => setShowLockAllModal(true)}
                              disabled={bulkFinalizeRunMutation.isPending}
                              className="btn btn-sm btn-admin-primary"
                              title="Lock every payroll record in this run"
                            >
                              <Lock className="w-3 h-3" />
                              <span>Lock All</span>
                              <CountBadge count={unfinalizedPayrollCount} theme="admin" active max={9999} />
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 whitespace-nowrap">
                              <Lock className="w-3 h-3" /> All locked
                            </span>
                          )
                        )}
                        {selectedPayrollIds.size > 0 && (
                          <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200">
                            <span className="text-xs text-gray-500">{selectedPayrollIds.size} selected</span>
                            <button onClick={handleBulkFinalizeSelected} disabled={bulkFinalizeMutation.isPending} className="btn btn-sm btn-admin-primary" title="Lock the selected payroll records">
                              <Lock className="w-3 h-3" /> Lock
                            </button>
                            <button onClick={handleBulkRecalculateSelected} className="btn btn-sm btn-outline">
                              <RefreshCw className="w-3 h-3" /> Recalc
                            </button>
                            <button onClick={() => setSelectedPayrollIds(new Set())} className="btn btn-sm btn-outline text-gray-400">Clear</button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Table — DS compact variant for dense payroll register */}
                    <div className="flex-1 overflow-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="table-header sticky top-0 z-10">
                          <tr>
                            <th className="table-header-cell-compact w-8">
                              <input
                                type="checkbox"
                                checked={selectedPayrollIds.size > 0 && selectedPayrollIds.size === Object.values(filteredPayrollsByDepartment).reduce((sum, d) => sum + d.employees.length, 0)}
                                onChange={(e) => toggleAllPayrollSelection(e.target.checked)}
                                className="rounded border-gray-300 text-red-600 accent-red-600"
                              />
                            </th>
                            <th className="table-header-cell-compact">Employee</th>
                            <th className="table-header-cell-compact text-right">Basic</th>
                            <th className="table-header-cell-compact text-right">Gross</th>
                            <th className="table-header-cell-compact text-right">CPF (Emp)</th>
                            <th className="table-header-cell-compact text-right">Deductions</th>
                            <th className="table-header-cell-compact text-right">Net Pay</th>
                            {payrollAggregates.reimbursementTotal > 0 && (
                              <th
                                className="table-header-cell-compact text-right"
                                title="Non-taxable repayments. Not included in gross or net pay."
                              >
                                Reimbursements
                              </th>
                            )}
                            <th className="table-header-cell-compact text-center">Lock</th>
                          </tr>
                        </thead>
                        <tbody className="table-body">
                          {Object.values(filteredPayrollsByDepartment).map(dept => {
                            const isExpanded = expandedPayrollDepartments.has(dept.department_id);
                            const deptGross = dept.employees.reduce((s, e) => s + (parseFloat(e.gross_pay) || 0), 0);
                            const deptNet = dept.employees.reduce((s, e) => s + (parseFloat(e.net_pay) || 0), 0);
                            const deptReimbursed = dept.employees.reduce((s, e) => s + (parseFloat(e.total_reimbursements) || 0), 0);

                            const expected = dept.expected_count || 0;
                            const calculated = dept.calculated_count || 0;
                            const errors = dept.errors || 0;
                            const isCalculated = dept.status === 'calculated';
                            const isErrored = dept.status === 'error' || (calculated > 0 && errors > 0);
                            const isPending = dept.status === 'pending' || (!isCalculated && !isErrored && calculated === 0);
                            const hasRows = dept.employees.length > 0;

                            // Drop departments that are entirely empty (no expected, no rows).
                            if (!hasRows && expected === 0) return null;

                            return (
                              <React.Fragment key={dept.department_id}>
                                {/* Department group header — also surfaces calc progress + per-dept Run/Re-run.
                                    This replaces the standalone Department Progress widget. */}
                                <tr
                                  className={`bg-gray-50/80 ${hasRows ? 'cursor-pointer' : ''} table-row-dense`}
                                  onClick={hasRows ? () => togglePayrollDepartmentExpansion(dept.department_id) : undefined}
                                  aria-expanded={hasRows ? isExpanded : undefined}
                                  role="row"
                                >
                                  <td className="table-cell-compact">
                                    {hasRows && (
                                      <input
                                        type="checkbox"
                                        checked={dept.employees.every(e => selectedPayrollIds.has(e.id))}
                                        ref={el => { if (el) { const cnt = dept.employees.filter(e => selectedPayrollIds.has(e.id)).length; el.indeterminate = cnt > 0 && cnt < dept.employees.length; }}}
                                        onChange={(e) => { e.stopPropagation(); toggleDepartmentPayrollSelection(dept.department_id, e.target.checked); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="rounded border-gray-300 text-red-600 accent-red-600"
                                      />
                                    )}
                                  </td>
                                  <td className="table-cell-compact" colSpan={2}>
                                    <div className="flex items-center gap-2">
                                      {hasRows ? (
                                        isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />
                                      ) : (
                                        <span className="w-4 h-4 inline-block" aria-hidden="true" />
                                      )}
                                      {(() => { const DeptIcon = getDepartmentIcon(dept.department_name); return <DeptIcon className={`w-4 h-4 ${getDepartmentColor(dept.department_name).text}`} />; })()}
                                      <span className="font-medium text-gray-900">{dept.department_name}</span>
                                      {expected > 0 ? (
                                        <span
                                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full tabular-nums ${
                                            isCalculated && errors === 0
                                              ? 'bg-green-100 text-green-700'
                                              : isErrored
                                                ? 'bg-amber-100 text-amber-700'
                                                : 'bg-gray-100 text-gray-600'
                                          }`}
                                          title={`${calculated} of ${expected} employees calculated`}
                                        >
                                          {calculated}/{expected}
                                        </span>
                                      ) : (
                                        <CountBadge count={dept.employees.length} theme="admin" size="sm" max={9999} />
                                      )}
                                      {isCalculated && errors === 0 && expected > 0 && (
                                        <CheckCircle className="w-3.5 h-3.5 text-green-500" aria-label="Calculated" />
                                      )}
                                      {isErrored && (
                                        <AlertTriangle
                                          className="w-3.5 h-3.5 text-amber-500"
                                          aria-label={`${errors} error${errors === 1 ? '' : 's'}`}
                                          title={`${errors} error${errors === 1 ? '' : 's'}`}
                                        />
                                      )}
                                      {isPending && expected > 0 && (
                                        <Clock className="w-3.5 h-3.5 text-gray-400" aria-label="Pending calculation" />
                                      )}
                                    </div>
                                  </td>
                                  <td className="table-cell-compact text-sm font-semibold text-gray-700 text-right tabular-nums">
                                    {hasRows ? formatCurrency(deptGross) : ''}
                                  </td>
                                  <td className="table-cell-compact"></td>
                                  <td className="table-cell-compact"></td>
                                  <td className="table-cell-compact text-sm font-semibold text-gray-700 text-right tabular-nums">
                                    {hasRows ? formatCurrency(deptNet) : ''}
                                  </td>
                                  {payrollAggregates.reimbursementTotal > 0 && (
                                    <td className="table-cell-compact text-sm font-semibold text-gray-900 text-right tabular-nums">
                                      {hasRows
                                        ? (deptReimbursed > 0
                                          ? formatCurrency(deptReimbursed)
                                          : <span className="text-gray-300 font-normal">—</span>)
                                        : ''}
                                    </td>
                                  )}
                                  <td className="table-cell-compact text-center">
                                    {expected > 0 && isPending && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          runPayrollMutation.mutate(
                                            { payrollRunId: currentPayrollRunId, departmentId: dept.department_id, forceRecalculate: false },
                                            { onSuccess: (data) => { setCalculationTaskId(data.task_id); setShowCalcModal(true); } }
                                          );
                                        }}
                                        disabled={runPayrollMutation.isPending || !!calculationTaskId}
                                        className="text-xs font-medium text-red-600 hover:text-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1.5 py-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                        title={`Calculate payroll for ${dept.department_name}`}
                                      >
                                        Run
                                      </button>
                                    )}
                                    {expected > 0 && (isCalculated || isErrored) && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          runPayrollMutation.mutate(
                                            { payrollRunId: currentPayrollRunId, departmentId: dept.department_id, forceRecalculate: true },
                                            { onSuccess: (data) => { setCalculationTaskId(data.task_id); setShowCalcModal(true); } }
                                          );
                                        }}
                                        disabled={runPayrollMutation.isPending || !!calculationTaskId}
                                        className="icon-button-admin"
                                        title={`Re-run payroll for ${dept.department_name}`}
                                        aria-label={`Re-run payroll for ${dept.department_name}`}
                                      >
                                        <RefreshCw
                                          className={`w-4 h-4 ${(runPayrollMutation.isPending || !!calculationTaskId) ? 'animate-spin' : ''}`}
                                        />
                                      </button>
                                    )}
                                  </td>
                                </tr>

                                {/* Employee rows */}
                                {isExpanded && dept.employees.map(emp => {
                                  const staffId = emp.employee_staff_id || emp.employee?.staff_id;
                                  const empFlags = flagsByEmployeeId[staffId] || [];
                                  const hasError = empFlags.some(f => f.severity === 'error');
                                  const hasWarning = empFlags.some(f => f.severity === 'warning');
                                  const isRowActive = selectedEmployeePayrollId === emp.id;

                                  return (
                                    <tr
                                      key={emp.id}
                                      className={`table-row-dense cursor-pointer ${isRowActive ? 'bg-gray-100' : ''}`}
                                      onClick={() => { setSelectedEmployeePayrollId(emp.id); setSelectedPayrollDepartmentId(dept.department_id); }}
                                    >
                                      <td className="table-cell-compact">
                                        <input
                                          type="checkbox"
                                          checked={selectedPayrollIds.has(emp.id)}
                                          onChange={(e) => { e.stopPropagation(); togglePayrollSelection(emp.id, e.target.checked); }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="rounded border-gray-300 text-red-600 accent-red-600"
                                        />
                                      </td>
                                      <td className="table-cell-compact">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <div className="min-w-0">
                                            <p className="text-sm text-gray-900 truncate">{emp.employee_first_name || emp.employee?.first_name} {emp.employee_last_name || emp.employee?.last_name}</p>
                                            <div className="flex items-center gap-1.5 flex-wrap min-w-0 mt-0.5">
                                              <span className="text-xs text-gray-500">{staffId}</span>
                                              {/* Dept is the accordion header — only trade
                                                  chips when they disambiguate (esp. Teams). */}
                                              {emp.employee_trade_category &&
                                                !isTradeRedundantWithDepartment(
                                                  emp.employee_department_name || dept.department_name,
                                                  null,
                                                  null,
                                                  emp.employee_trade_category,
                                                ) && (
                                                <TradeBadge trade={emp.employee_trade_category} size="xs" />
                                              )}
                                            </div>
                                          </div>
                                          {hasError && <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                                          {hasWarning && !hasError && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                                        </div>
                                      </td>
                                      <td className="table-cell-compact text-right tabular-nums">{formatCurrency(emp.basic_salary_monthly)}</td>
                                      <td className="table-cell-compact text-right tabular-nums font-medium">{formatCurrency(emp.gross_pay)}</td>
                                      <td className="table-cell-compact text-right tabular-nums text-gray-700">{formatCurrency(emp.cpf_employee_contribution)}</td>
                                      <td className="table-cell-compact text-right tabular-nums text-red-600">-{formatCurrency(emp.total_deductions)}</td>
                                      <td className="table-cell-compact text-right tabular-nums font-medium">{formatCurrency(emp.net_pay)}</td>
                                      {payrollAggregates.reimbursementTotal > 0 && (
                                        <td className="table-cell-compact text-right tabular-nums">
                                          {(parseFloat(emp.total_reimbursements) || 0) > 0
                                            ? <span className="font-medium text-gray-900">{formatCurrency(emp.total_reimbursements)}</span>
                                            : <span className="text-gray-300">—</span>}
                                        </td>
                                      )}
                                      <td className="table-cell-compact text-center whitespace-nowrap">
                                        <StatusBadge status={emp.is_finalized ? 'payroll_locked' : 'payroll_unlocked'} />
                                      </td>
                                    </tr>
                                  );
                                })}
                              </React.Fragment>
                            );
                          })}

                          {/* Grand Totals Row — only when at least one row is calculated */}
                          {employeePayrolls.length > 0 && (
                            <tr className="bg-gray-50 border-t-2 border-gray-300 sticky bottom-0">
                              <td className="table-cell-compact py-2.5" colSpan={2}>
                                <span className="text-xs font-bold text-gray-900 uppercase">{payrollDeptFilter != null ? 'Dept Total' : 'Grand Total'}{payrollFilter !== 'all' ? ` (${payrollFilter === 'staff' ? 'Staff' : 'Workers'})` : ''}</span>
                              </td>
                              <td className="table-cell-compact py-2.5 text-sm font-bold text-gray-900 text-right tabular-nums">{formatCurrency(payrollAggregates.basicTotal)}</td>
                              <td className="table-cell-compact py-2.5 text-sm font-bold text-gray-900 text-right tabular-nums">{formatCurrency(payrollAggregates.grossTotal)}</td>
                              <td className="table-cell-compact py-2.5 text-sm font-bold text-gray-700 text-right tabular-nums">{formatCurrency(payrollAggregates.cpfTotal)}</td>
                              <td className="table-cell-compact py-2.5 text-sm font-bold text-red-600 text-right tabular-nums">-{formatCurrency(payrollAggregates.deductionsTotal)}</td>
                              <td className="table-cell-compact py-2.5 text-sm font-bold text-gray-900 text-right tabular-nums">{formatCurrency(payrollAggregates.netTotal)}</td>
                              {payrollAggregates.reimbursementTotal > 0 && (
                                <td className="table-cell-compact py-2.5 text-sm font-bold text-gray-900 text-right tabular-nums">{formatCurrency(payrollAggregates.reimbursementTotal)}</td>
                              )}
                              <td className="table-cell-compact py-2.5"></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* RIGHT: Employee Detail Panel */}
                  {selectedEmployeePayrollId && selectedEmployeePayroll && (
                  <div className="w-[45%] flex-shrink-0 bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
                    {selectedEmployeePayroll && (
                      <>
                        {/* Employee Header */}
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                          {/* Row 1: Identity + Actions */}
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <h4 className="text-base font-semibold text-gray-900 truncate">
                                {selectedEmployeePayroll.employee_first_name || selectedEmployeePayroll.employee?.first_name} {selectedEmployeePayroll.employee_last_name || selectedEmployeePayroll.employee?.last_name}
                              </h4>
                              {/* Row 2: ID + dept/trade identity chips + Period + Pass Type */}
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className="text-xs text-gray-500">
                                  {selectedEmployeePayroll.employee_staff_id || selectedEmployeePayroll.employee?.staff_id}
                                </span>
                                {(selectedEmployeePayroll.employee_department_name
                                  || selectedEmployeePayroll.employee_trade_category) && (
                                  <DepartmentTradeChips
                                    department={selectedEmployeePayroll.employee_department_name}
                                    trade={selectedEmployeePayroll.employee_trade_category}
                                    layout="inline"
                                    size="xs"
                                  />
                                )}
                                {selectedEmployeePayroll.payroll_run_period_name && (
                                  <InfoBadge color="gray" label={selectedEmployeePayroll.payroll_run_period_name} />
                                )}
                                {selectedEmployeePayroll.employee_employment_pass_type && (
                                  <InfoBadge
                                    color={
                                      ['Singapore Citizen', 'Permanent Resident'].includes(selectedEmployeePayroll.employee_employment_pass_type) ? 'blue' : 'orange'
                                    }
                                    label={selectedEmployeePayroll.employee_employment_pass_type}
                                  />
                                )}
                              </div>
                              {/* Row 3: Flags/alerts (conditional — only when exceptions exist) */}
                              {(selectedEmployeePayroll.employee_employment_status || selectedEmployeePayroll.employee_service_months != null) && (
                                <div className="flex items-center gap-1.5 mt-1.5">
                                  {selectedEmployeePayroll.employee_employment_status && (
                                    <StatusBadge
                                      status={selectedEmployeePayroll.employee_employment_status === 'On Probation' ? 'warning' : 'error'}
                                      label={selectedEmployeePayroll.employee_employment_status}
                                    />
                                  )}
                                  {selectedEmployeePayroll.employee_service_months != null && (
                                    <InfoBadge
                                      color="amber"
                                      label={`Month ${selectedEmployeePayroll.employee_service_months + 1} — no leave entitlement`}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <PayrollLockButton
                                isFinalized={selectedEmployeePayroll.is_finalized}
                                disabled={
                                  selectedEmployeePayroll.is_finalized
                                    ? unfinalizePayrollMutation.isPending
                                    : finalizePayrollMutation.isPending
                                }
                                onLock={() => finalizePayrollMutation.mutate(selectedEmployeePayroll.id)}
                                onUnlock={() => {
                                  setRegUnfinalizeReason('');
                                  setShowRegUnfinalizeModal(true);
                                }}
                              />
                              {/* Action Buttons - Icon-only round buttons per design system */}
                              {(() => {
                                const detailStaffId = selectedEmployeePayroll.employee_staff_id
                                  || selectedEmployeePayroll.employee?.staff_id;
                                const isRecalcing = recalculatingStaffId === detailStaffId;
                                return (
                                  <button
                                    onClick={() => handleRecalculateEmployee(selectedEmployeePayroll)}
                                    disabled={
                                      selectedEmployeePayroll.is_finalized
                                      || isRecalcing
                                      || !!recalculatingStaffId
                                    }
                                    title={
                                      selectedEmployeePayroll.is_finalized
                                        ? 'Unlock before recalculating'
                                        : isRecalcing
                                          ? 'Recalculating…'
                                          : 'Recalculate this employee'
                                    }
                                    aria-label="Recalculate this employee"
                                    className="icon-button-admin"
                                  >
                                    <RefreshCw className={`w-3.5 h-3.5${isRecalcing ? ' animate-spin' : ''}`} />
                                  </button>
                                );
                              })()}
                              <button
                                onClick={() => {
                                  setSelectedPayrollForAdjustment(selectedEmployeePayroll);
                                  setAdjustmentForm({
                                    backpayItems: [],
                                    overtimeItems: [],
                                    allowanceItems: [],
                                    bonusItems: [],
                                    deductionItems: [],
                                    reimbursementItems: [],
                                    adjustmentNotes: '',
                                    bonusPayments: '',
                                    otherPayments: '',
                                    otherDeductions: ''
                                  });
                                  setAdjustmentPreview(null);
                                  setShowAdjustmentModal(true);
                                }}
                                disabled={selectedEmployeePayroll.is_finalized}
                                title="Adjust Payroll"
                                aria-label="Adjust Payroll"
                                className="icon-button-admin"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setSelectedEmployeePayrollId(null)}
                                title="Close detail panel"
                                aria-label="Close detail panel"
                                className="icon-button-admin"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Exception Alerts */}
                          {(() => {
                            const staffId = selectedEmployeePayroll.employee_staff_id || selectedEmployeePayroll.employee?.staff_id;
                            const empFlags = flagsByEmployeeId[staffId] || [];
                            const errors = empFlags.filter(f => f.severity === 'error');
                            const warnings = empFlags.filter(f => f.severity === 'warning');
                            
                            if (errors.length === 0 && warnings.length === 0) return null;
                            
                            return (
                              <div className="mt-3 space-y-2">
                                {errors.map(flag => (
                                  <div key={flag.id} className="flex items-start gap-2 p-2 bg-red-50 rounded-lg border border-red-200">
                                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-red-800">{flag.flag_type_display}</p>
                                      <p className="text-xs text-red-600">{flag.message}</p>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setSelectedFlag(flag);
                                        setExclusionReason('');
                                        setShowFlagReviewModal(true);
                                      }}
                                      className="text-xs text-red-700 hover:text-red-900 font-medium"
                                    >
                                      Resolve
                                    </button>
                                  </div>
                                ))}
                                {warnings.map(flag => (
                                  <div key={flag.id} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-200">
                                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-amber-800">{flag.flag_type_display}</p>
                                      <p className="text-xs text-amber-600">{flag.message}</p>
                                    </div>
                                    <button
                                      onClick={() => {
                                        reviewFlagMutation.mutate({ flagId: flag.id, reviewNotes: '' }, {
                                          onSuccess: () => {
                                            refetchFlagSummary();
                                            refetchFlags();
                                          }
                                        });
                                      }}
                                      className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                                    >
                                      Acknowledge
                                    </button>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Payroll Details - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-6">
                          <EmployeePayrollBreakdown payroll={selectedEmployeePayroll} formatCurrency={formatCurrency} />
                        </div>
                      </>
                    )}
                  </div>
                  )}
                </div>
              )}

              {/* Ready State — no payroll run yet */}
              {!currentPayrollRunId && (
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center">
                  <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">Ready to Process Payroll</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Click <span className="font-semibold text-red-600">"Run Payroll"</span> above to begin calculations for the selected period.
                  </p>
                </div>
              )}

              {/* Adjustment Modal — DS-compliant */}
              <PayrollAdjustmentModal
                isOpen={showAdjustmentModal && !!selectedPayrollForAdjustment}
                onClose={() => {
                  setShowAdjustmentModal(false);
                  setSelectedPayrollForAdjustment(null);
                }}
                payrollData={selectedPayrollForAdjustment}
                formatCurrency={formatCurrency}
                onSuccess={() => refetchEmployeePayrolls()}
              />

              {/* Unfinalize Confirmation Modal */}
              <ConfirmModal
                isOpen={showRegUnfinalizeModal}
                title="Unlock payroll"
                confirmLabel="Unlock"
                variant="danger"
                isLoading={unfinalizePayrollMutation.isPending}
                onCancel={() => setShowRegUnfinalizeModal(false)}
                onConfirm={() => {
                  if (!regUnfinalizeReason.trim()) return;
                  unfinalizePayrollMutation.mutate({ payrollId: selectedEmployeePayroll.id, reason: regUnfinalizeReason.trim() }, {
                    onSuccess: () => {
                      setShowRegUnfinalizeModal(false);
                      refetchEmployeePayrolls();
                    }
                  });
                }}
              >
                <div className="space-y-3">
                  <p className="text-sm text-gray-700">
                    This will unlock the payroll record for <span className="font-semibold">{selectedEmployeePayroll?.employee_first_name} {selectedEmployeePayroll?.employee_last_name}</span> and allow further edits.
                  </p>
                  <div>
                    <label className="form-label-admin">Reason <span className="text-red-500">*</span></label>
                    <textarea
                      value={regUnfinalizeReason}
                      onChange={(e) => setRegUnfinalizeReason(e.target.value)}
                      placeholder="Why is this payroll being unlocked?"
                      className="textarea-admin w-full text-sm"
                      rows={2}
                      autoFocus
                    />
                  </div>
                </div>
              </ConfirmModal>

              {/* Step 3 review-flag triage modal */}
              <ReviewFlagsModal
                isOpen={showReviewAllModal}
                flags={flagSummary?.needs_review || []}
                onAcknowledge={handleAcknowledgeFlags}
                isAcknowledging={bulkReviewMutation.isPending}
                onAutoVerify={handleAutoVerifyFlags}
                isAutoVerifying={autoVerifyMutation.isPending}
                onClose={() => setShowReviewAllModal(false)}
              />

              {/* Step 3 bulk lock confirmation */}
              <ConfirmModal
                isOpen={showLockAllModal}
                title="Lock All Payroll Records"
                message={`This will finalize all ${unfinalizedPayrollCount} remaining payroll record(s) in this run, locking them from recalculation. HR admin can unlock individual records later if corrections are needed. Continue?`}
                confirmLabel="Lock All"
                isLoading={bulkFinalizeRunMutation.isPending}
                onConfirm={handleLockAllPayrolls}
                onCancel={() => setShowLockAllModal(false)}
              />

              {/* REPLACED: inline adjustment modal now uses shared PayrollAdjustmentModal above */}
              {false && selectedPayrollForAdjustment && (
                <Portal>
                  <div className="modal-overlay">
                    <div className="modal-container modal-container-xl">
                      <div className="modal-header">
                        <div>
                          <h3 className="modal-title">Adjust Payroll</h3>
                          <p className="text-sm text-gray-600 mt-0.5 flex items-center gap-2">
                            {selectedPayrollForAdjustment.employee_first_name} {selectedPayrollForAdjustment.employee_last_name}
                            <InfoBadge color="gray" label={selectedPayrollForAdjustment.employee_staff_id} />
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setShowAdjustmentModal(false);
                            setSelectedPayrollForAdjustment(null);
                            setAdjustmentPreview(null);
                          }}
                          className="modal-close-admin"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="modal-content !overflow-hidden !p-0 flex flex-col">
                        <div className="grid grid-cols-5 gap-4 h-full p-5">
                          {/* Left Column — Form (2 cols) */}
                          <div className="col-span-2 overflow-y-auto overscroll-y-contain pr-2 space-y-4">
                            {/* Earnings Section */}
                            <div className="border-b border-gray-200 pb-5">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                  <TrendingUp className="w-3.5 h-3.5 text-green-600" />
                                </div>
                                Earnings Adjustments
                              </h5>

                              {/* Backpay */}
                              <div className="mb-4">
                                <label className="form-label-admin">Backpay</label>
                                {adjustmentForm.backpayItems.map((item, index) => (
                                  <div key={index} className="flex items-center gap-2 mb-2">
                                    <input
                                      type="text"
                                      placeholder="Description"
                                      value={item.description}
                                      onChange={(e) => {
                                        const updated = [...adjustmentForm.backpayItems];
                                        updated[index] = { ...updated[index], description: e.target.value };
                                        setAdjustmentForm(prev => ({ ...prev, backpayItems: updated }));
                                      }}
                                      className="input-admin flex-1 text-sm"
                                    />
                                    <div className="relative w-32">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        onWheel={(e) => e.target.blur()}
                                        value={item.amount}
                                        onChange={(e) => {
                                          const updated = [...adjustmentForm.backpayItems];
                                          updated[index] = { ...updated[index], amount: e.target.value };
                                          setAdjustmentForm(prev => ({ ...prev, backpayItems: updated }));
                                        }}
                                        className="input-admin w-full text-sm pl-7 text-right"
                                      />
                                    </div>
                                    <button
                                      onClick={() => {
                                        const updated = adjustmentForm.backpayItems.filter((_, i) => i !== index);
                                        setAdjustmentForm(prev => ({ ...prev, backpayItems: updated }));
                                      }}
                                      className="icon-button-admin"
                                      title="Remove"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setAdjustmentForm(prev => ({
                                    ...prev,
                                    backpayItems: [...prev.backpayItems, { description: '', amount: '' }]
                                  }))}
                                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Backpay
                                </button>
                              </div>

                              {/* Overtime */}
                              <div className="mb-4">
                                <label className="form-label-admin">Overtime</label>
                                {adjustmentForm.overtimeItems.map((item, index) => (
                                  <div key={index} className="flex items-center gap-2 mb-2">
                                    <input
                                      type="text"
                                      placeholder="Description"
                                      value={item.description}
                                      onChange={(e) => {
                                        const updated = [...adjustmentForm.overtimeItems];
                                        updated[index] = { ...updated[index], description: e.target.value };
                                        setAdjustmentForm(prev => ({ ...prev, overtimeItems: updated }));
                                      }}
                                      className="input-admin flex-1 text-sm"
                                    />
                                    <div className="relative w-32">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        onWheel={(e) => e.target.blur()}
                                        value={item.amount}
                                        onChange={(e) => {
                                          const updated = [...adjustmentForm.overtimeItems];
                                          updated[index] = { ...updated[index], amount: e.target.value };
                                          setAdjustmentForm(prev => ({ ...prev, overtimeItems: updated }));
                                        }}
                                        className="input-admin w-full text-sm pl-7 text-right"
                                      />
                                    </div>
                                    <button
                                      onClick={() => {
                                        const updated = adjustmentForm.overtimeItems.filter((_, i) => i !== index);
                                        setAdjustmentForm(prev => ({ ...prev, overtimeItems: updated }));
                                      }}
                                      className="icon-button-admin"
                                      title="Remove"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setAdjustmentForm(prev => ({
                                    ...prev,
                                    overtimeItems: [...prev.overtimeItems, { description: '', amount: '' }]
                                  }))}
                                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Overtime
                                </button>
                              </div>

                              {/* Allowances */}
                              <div className="mb-4">
                                <label className="form-label-admin">Allowances</label>
                                {adjustmentForm.allowanceItems.map((item, index) => (
                                  <div key={index} className="flex items-center gap-2 mb-2">
                                    <input
                                      type="text"
                                      placeholder="Description"
                                      value={item.description}
                                      onChange={(e) => {
                                        const updated = [...adjustmentForm.allowanceItems];
                                        updated[index] = { ...updated[index], description: e.target.value };
                                        setAdjustmentForm(prev => ({ ...prev, allowanceItems: updated }));
                                      }}
                                      className="input-admin flex-1 text-sm"
                                    />
                                    <div className="relative w-32">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        onWheel={(e) => e.target.blur()}
                                        value={item.amount}
                                        onChange={(e) => {
                                          const updated = [...adjustmentForm.allowanceItems];
                                          updated[index] = { ...updated[index], amount: e.target.value };
                                          setAdjustmentForm(prev => ({ ...prev, allowanceItems: updated }));
                                        }}
                                        className="input-admin w-full text-sm pl-7 text-right"
                                      />
                                    </div>
                                    <button
                                      onClick={() => {
                                        const updated = adjustmentForm.allowanceItems.filter((_, i) => i !== index);
                                        setAdjustmentForm(prev => ({ ...prev, allowanceItems: updated }));
                                      }}
                                      className="icon-button-admin"
                                      title="Remove"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setAdjustmentForm(prev => ({
                                    ...prev,
                                    allowanceItems: [...prev.allowanceItems, { description: '', amount: '' }]
                                  }))}
                                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Allowance
                                </button>
                              </div>

                              {/* Bonus */}
                              <div>
                                <label className="form-label-admin">Bonus (Additional Wages)</label>
                                {adjustmentForm.bonusItems.map((item, index) => (
                                  <div key={index} className="flex items-center gap-2 mb-2">
                                    <input
                                      type="text"
                                      placeholder="Description"
                                      value={item.description}
                                      onChange={(e) => {
                                        const updated = [...adjustmentForm.bonusItems];
                                        updated[index] = { ...updated[index], description: e.target.value };
                                        setAdjustmentForm(prev => ({ ...prev, bonusItems: updated }));
                                      }}
                                      className="input-admin flex-1 text-sm"
                                    />
                                    <div className="relative w-32">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        onWheel={(e) => e.target.blur()}
                                        value={item.amount}
                                        onChange={(e) => {
                                          const updated = [...adjustmentForm.bonusItems];
                                          updated[index] = { ...updated[index], amount: e.target.value };
                                          setAdjustmentForm(prev => ({ ...prev, bonusItems: updated }));
                                        }}
                                        className="input-admin w-full text-sm pl-7 text-right"
                                      />
                                    </div>
                                    <button
                                      onClick={() => {
                                        const updated = adjustmentForm.bonusItems.filter((_, i) => i !== index);
                                        setAdjustmentForm(prev => ({ ...prev, bonusItems: updated }));
                                      }}
                                      className="icon-button-admin"
                                      title="Remove"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => setAdjustmentForm(prev => ({
                                    ...prev,
                                    bonusItems: [...prev.bonusItems, { description: '', amount: '' }]
                                  }))}
                                  className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                                >
                                  <Plus className="w-3 h-3 mr-1" />
                                  Add Bonus
                                </button>
                              </div>
                            </div>

                            {/* Deductions Section — FormSelect dropdown */}
                            <div className="border-b border-gray-200 pb-5">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2">
                                  <TrendingUp className="w-3.5 h-3.5 text-red-600 rotate-180" />
                                </div>
                                Deduction Adjustments
                              </h5>

                              {adjustmentForm.deductionItems.map((item, index) => (
                                <div key={index} className="mb-3">
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1">
                                      <FormSelect
                                        options={DEDUCTION_ADJUSTMENT_TYPES}
                                        value={item.deductionType}
                                        onChange={(val) => {
                                          const updated = [...adjustmentForm.deductionItems];
                                          const label = DEDUCTION_ADJUSTMENT_TYPES.find(t => t.value === val)?.label || val;
                                          updated[index] = { ...updated[index], description: val === 'other' ? '' : label, deductionType: val };
                                          setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                                        }}
                                        placeholder="Select type..."
                                        theme="admin"
                                      />
                                    </div>
                                    <div className="relative w-32">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        onWheel={(e) => e.target.blur()}
                                        value={item.amount}
                                        onChange={(e) => {
                                          const updated = [...adjustmentForm.deductionItems];
                                          updated[index] = { ...updated[index], amount: e.target.value };
                                          setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                                        }}
                                        className="input-admin w-full text-sm pl-7 text-right"
                                      />
                                    </div>
                                    <button
                                      onClick={() => {
                                        const updated = adjustmentForm.deductionItems.filter((_, i) => i !== index);
                                        setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                                      }}
                                      className="icon-button-admin"
                                      title="Remove"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  {item.deductionType === 'other' && (
                                    <input
                                      type="text"
                                      placeholder="Specify deduction..."
                                      value={item.description}
                                      onChange={(e) => {
                                        const updated = [...adjustmentForm.deductionItems];
                                        updated[index] = { ...updated[index], description: e.target.value };
                                        setAdjustmentForm(prev => ({ ...prev, deductionItems: updated }));
                                      }}
                                      className="input-admin w-full text-sm mt-2"
                                    />
                                  )}
                                </div>
                              ))}
                              <button
                                onClick={() => setAdjustmentForm(prev => ({
                                  ...prev,
                                  deductionItems: [...prev.deductionItems, { description: '', amount: '', deductionType: '' }]
                                }))}
                                className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Deduction
                              </button>
                            </div>

                            {/* Reimbursements Section */}
                            <div className="border-b border-gray-200 pb-5">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                                  <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                                </div>
                                Reimbursements
                                <span className="ml-2 text-xs text-gray-400 font-normal">(Non-taxable)</span>
                              </h5>

                              {adjustmentForm.reimbursementItems.map((item, index) => (
                                <div key={index} className="flex items-center gap-2 mb-2">
                                  <input
                                    type="text"
                                    placeholder="Description (e.g., Transport, Medical)"
                                    value={item.description}
                                    onChange={(e) => {
                                      const updated = [...adjustmentForm.reimbursementItems];
                                      updated[index] = { ...updated[index], description: e.target.value };
                                      setAdjustmentForm(prev => ({ ...prev, reimbursementItems: updated }));
                                    }}
                                    className="input-admin flex-1 text-sm"
                                  />
                                  <div className="relative w-32">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      onWheel={(e) => e.target.blur()}
                                      value={item.amount}
                                      onChange={(e) => {
                                        const updated = [...adjustmentForm.reimbursementItems];
                                        updated[index] = { ...updated[index], amount: e.target.value };
                                        setAdjustmentForm(prev => ({ ...prev, reimbursementItems: updated }));
                                      }}
                                      className="input-admin w-full text-sm pl-7 text-right"
                                    />
                                  </div>
                                  <button
                                    onClick={() => {
                                      const updated = adjustmentForm.reimbursementItems.filter((_, i) => i !== index);
                                      setAdjustmentForm(prev => ({ ...prev, reimbursementItems: updated }));
                                    }}
                                    className="icon-button-admin"
                                    title="Remove"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => setAdjustmentForm(prev => ({
                                  ...prev,
                                  reimbursementItems: [...prev.reimbursementItems, { description: '', amount: '' }]
                                }))}
                                className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded px-1"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Reimbursement
                              </button>
                            </div>

                            {/* Notes */}
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                                  <FileText className="w-3.5 h-3.5 text-gray-600" />
                                </div>
                                Notes
                              </h5>

                              <textarea
                                value={adjustmentForm.adjustmentNotes}
                                onChange={(e) => setAdjustmentForm(prev => ({ ...prev, adjustmentNotes: e.target.value }))}
                                placeholder="Optional context for this adjustment..."
                                className="textarea-admin w-full text-sm"
                                rows={2}
                              />
                            </div>
                          </div>

                          {/* Right Column — Impact Preview (3 cols) */}
                          <div className="col-span-3">
                            <div className="sticky top-0 bg-gray-50 rounded-xl p-4">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                                <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                                  <Calculator className="w-3.5 h-3.5 text-purple-600" />
                                </div>
                                Impact Preview
                                {isPreviewLoading ? (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                    Calculating...
                                  </span>
                                ) : adjustmentPreview ? (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Accurate
                                  </span>
                                ) : (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    No changes
                                  </span>
                                )}
                              </h5>

                              <div className="overflow-hidden">
                                {(() => {
                                  const cb = adjustmentPreview?.component_breakdown;
                                  const isWorker = selectedPayrollForAdjustment.employee_classification === 'worker';
                                  const sp = selectedPayrollForAdjustment;

                                  const otherEarningsFallback = parseFloat(sp.commission || 0) + parseFloat(sp.public_holiday_pay || 0)
                                    + parseFloat(sp.rest_day_pay || 0) + parseFloat(sp.other_payments || 0);

                                  const earningsRows = [
                                    { label: 'Basic Salary', key: 'basic_salary', fallback: sp.basic_salary_earned },
                                    { label: 'Overtime', key: 'overtime', fallback: sp.overtime_pay },
                                    { label: 'Allowances', key: 'allowances', fallback: sp.total_allowances },
                                    { label: 'Backpay', key: 'backpay', fallback: 0 },
                                    { label: 'Bonus', key: 'bonus', fallback: sp.bonus_payments },
                                    { label: 'Other Earnings', key: 'other_earnings', fallback: otherEarningsFallback },
                                  ];

                                  const deductionRows = [
                                    { label: 'Leave Deductions', key: 'leave_deductions', fallback: sp.leave_deduction_amount },
                                    ...(!isWorker ? [
                                      { label: 'CPF (Employee)', key: 'cpf_employee', fallback: sp.cpf_employee_contribution },
                                      { label: 'CPF (Employer)', key: 'cpf_employer', fallback: sp.cpf_employer_contribution },
                                    ] : []),
                                    { label: 'Addtl. Deductions', key: 'additional_deductions', fallback: sp.other_deductions },
                                  ];

                                  const getVals = (key, fallback) => {
                                    const c = cb?.[key];
                                    return {
                                      before: c ? c.before : parseFloat(fallback || 0),
                                      after: c ? c.after : parseFloat(fallback || 0),
                                      change: c ? c.change : 0,
                                    };
                                  };
                                  const isVisible = (key, fallback) => {
                                    const v = getVals(key, fallback);
                                    return v.before > 0 || v.after > 0 || v.change !== 0;
                                  };

                                  const otDetail = cb?.overtime?.detail;

                                  const renderRow = (key, label, fallback, positive = 'green') => {
                                    const v = getVals(key, fallback);
                                    const upColor = positive === 'green' ? 'text-green-600' : 'text-red-600';
                                    const downColor = positive === 'green' ? 'text-red-600' : 'text-green-600';
                                    return (
                                      <tr key={key}>
                                        <td className="py-1.5 text-gray-600">
                                          {key === 'overtime' && otDetail && (otDetail.weekday_overtime_pay > 0 || otDetail.rest_day_overtime_pay > 0) ? (
                                            <div className="tooltip-container">
                                              <span className="tooltip-trigger cursor-help border-b border-dashed border-gray-400">{label}</span>
                                              <div className="tooltip-top" style={{ minWidth: '160px' }}>
                                                <div className="tooltip-arrow"></div>
                                                <div className="text-xs space-y-1">
                                                  {otDetail.weekday_overtime_pay > 0 && (
                                                    <div className="flex justify-between gap-3">
                                                      <span className="text-gray-400">Weekday OT</span>
                                                      <span className="text-white tabular-nums">{formatCurrency(otDetail.weekday_overtime_pay)}</span>
                                                    </div>
                                                  )}
                                                  {otDetail.rest_day_overtime_pay > 0 && (
                                                    <div className="flex justify-between gap-3">
                                                      <span className="text-gray-400">Rest Day OT</span>
                                                      <span className="text-white tabular-nums">{formatCurrency(otDetail.rest_day_overtime_pay)}</span>
                                                    </div>
                                                  )}
                                                  {v.change !== 0 && (
                                                    <div className="flex justify-between gap-3 border-t border-gray-600 pt-1 mt-1">
                                                      <span className="text-gray-400">Adjustment</span>
                                                      <span className="text-white tabular-nums">+{formatCurrency(v.change)}</span>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          ) : label}
                                        </td>
                                        <td className="py-1.5 text-right text-gray-500 tabular-nums">{formatCurrency(v.before)}</td>
                                        <td className="py-1.5 text-right font-medium tabular-nums">{formatCurrency(v.after)}</td>
                                        <td className={`py-1.5 text-right font-medium tabular-nums ${v.change > 0 ? upColor : v.change < 0 ? downColor : 'text-gray-400'}`}>
                                          {v.change !== 0 ? <>{v.change > 0 ? '+' : ''}{formatCurrency(v.change)}</> : '—'}
                                        </td>
                                      </tr>
                                    );
                                  };

                                  const renderSubtotal = (label, key, fallback, borderClass = 'border-t border-gray-200') => {
                                    const before = adjustmentPreview?.before?.[key] ?? parseFloat(fallback || 0);
                                    const after = adjustmentPreview?.after?.[key] ?? parseFloat(fallback || 0);
                                    const change = adjustmentPreview?.changes?.[key] ?? 0;
                                    return (
                                      <tr key={key} className={borderClass}>
                                        <td className="py-1.5 font-semibold text-gray-900">{label}</td>
                                        <td className="py-1.5 text-right text-gray-600 tabular-nums">{formatCurrency(before)}</td>
                                        <td className="py-1.5 text-right font-semibold tabular-nums">{formatCurrency(after)}</td>
                                        <td className={`py-1.5 text-right font-semibold tabular-nums ${change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                          {change !== 0 ? <>{change > 0 ? '+' : ''}{formatCurrency(change)}</> : '—'}
                                        </td>
                                      </tr>
                                    );
                                  };

                                  const visibleEarnings = earningsRows.filter(r => isVisible(r.key, r.fallback));
                                  const visibleDeductions = deductionRows.filter(r => isVisible(r.key, r.fallback));

                                  return (
                                    <table className="w-full text-xs">
                                      <thead className="table-header">
                                        <tr className="border-b border-gray-100">
                                          <th className="table-header-cell"></th>
                                          <th className="table-header-cell text-right">Before</th>
                                          <th className="table-header-cell text-right">After</th>
                                          <th className="table-header-cell text-right">Change</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {visibleEarnings.length > 0 && (
                                          <tr><td colSpan={4} className="pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Earnings</td></tr>
                                        )}
                                        {visibleEarnings.map(r => renderRow(r.key, r.label, r.fallback, 'green'))}
                                        {renderSubtotal('Gross Pay', 'gross_pay', sp.gross_pay)}

                                        {visibleDeductions.length > 0 && (
                                          <tr><td colSpan={4} className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Deductions</td></tr>
                                        )}
                                        {visibleDeductions.map(r => renderRow(r.key, r.label, r.fallback, 'red'))}
                                        {renderSubtotal('Net Pay', 'net_pay', sp.net_pay)}

                                        {isVisible('reimbursements_row', sp.total_reimbursements) || parseFloat(sp.total_reimbursements || 0) > 0 || (adjustmentPreview?.changes?.reimbursements ?? 0) !== 0 ? (
                                          <>
                                            <tr><td colSpan={4} className="pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Additions</td></tr>
                                            <tr>
                                              <td className="py-1.5 text-gray-600">Reimbursements</td>
                                              <td className="py-1.5 text-right text-gray-500 tabular-nums">{formatCurrency(adjustmentPreview?.before?.reimbursements ?? parseFloat(sp.total_reimbursements || 0))}</td>
                                              <td className="py-1.5 text-right font-medium tabular-nums">{formatCurrency(adjustmentPreview?.after?.reimbursements ?? parseFloat(sp.total_reimbursements || 0))}</td>
                                              <td className={`py-1.5 text-right font-medium tabular-nums ${(adjustmentPreview?.changes?.reimbursements ?? 0) > 0 ? 'text-green-600' : (adjustmentPreview?.changes?.reimbursements ?? 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                                {(adjustmentPreview?.changes?.reimbursements ?? 0) !== 0 ? <>{(adjustmentPreview?.changes?.reimbursements ?? 0) > 0 ? '+' : ''}{formatCurrency(adjustmentPreview?.changes?.reimbursements ?? 0)}</> : '—'}
                                              </td>
                                            </tr>
                                          </>
                                        ) : null}

                                        <tr className="border-t-2 border-gray-200">
                                          <td className="py-2 font-bold text-gray-900">Final Payment</td>
                                          <td className="py-2 text-right text-gray-600 tabular-nums">{formatCurrency(adjustmentPreview?.before?.final_payment ?? sp.final_payment)}</td>
                                          <td className="py-2 text-right font-bold text-gray-900 tabular-nums">{formatCurrency(adjustmentPreview?.after?.final_payment ?? sp.final_payment)}</td>
                                          <td className={`py-2 text-right font-bold tabular-nums ${(adjustmentPreview?.changes?.final_payment ?? 0) > 0 ? 'text-green-600' : (adjustmentPreview?.changes?.final_payment ?? 0) < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                            {(adjustmentPreview?.changes?.final_payment ?? 0) !== 0 ? (
                                              <>{(adjustmentPreview?.changes?.final_payment ?? 0) > 0 ? '+' : ''}{formatCurrency(adjustmentPreview?.changes?.final_payment ?? 0)}</>
                                            ) : '—'}
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  );
                                })()}
                              </div>

                              {adjustmentPreview?.warnings?.length > 0 && (
                                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                    <div className="text-xs text-amber-700">
                                      {adjustmentPreview.warnings.map((w, i) => (
                                        <p key={i}>{w}</p>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {adjustmentPreview?.will_recalculate_cpf && selectedPayrollForAdjustment.employee_classification !== 'worker' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <p className="text-xs text-blue-700 flex items-center">
                                    <Shield className="w-3.5 h-3.5 mr-1.5" />
                                    CPF contributions will be recalculated automatically
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="modal-footer">
                        <button
                          onClick={() => {
                            setShowAdjustmentModal(false);
                            setSelectedPayrollForAdjustment(null);
                            setAdjustmentPreview(null);
                          }}
                          className="btn btn-admin-outline"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            adjustPayrollMutation.mutate({
                              employeePayrollId: selectedPayrollForAdjustment.id,
                              adjustmentData: adjustmentForm
                            }, {
                              onSuccess: () => {
                                setShowAdjustmentModal(false);
                                setSelectedPayrollForAdjustment(null);
                                setAdjustmentPreview(null);
                                refetchEmployeePayrolls();
                              }
                            });
                          }}
                          disabled={adjustPayrollMutation.isPending}
                          className="btn btn-admin-primary"
                        >
                          {adjustPayrollMutation.isPending ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              Apply Adjustments
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </Portal>
              )}

              {/* Flag Review Modal */}
              {showFlagReviewModal && selectedFlag && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
                    <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {selectedFlag.severity === 'error' ? 'Resolve Error' : 'Review Flag'}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {selectedFlag.employee_name} ({selectedFlag.employee_id})
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setShowFlagReviewModal(false);
                          setSelectedFlag(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="px-6 py-4 space-y-4">
                      <div className={`p-3 rounded-lg ${selectedFlag.severity === 'error' ? 'bg-red-50' : 'bg-amber-50'}`}>
                        <div className="flex items-center mb-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            selectedFlag.severity === 'error' 
                              ? 'bg-red-100 text-red-800' 
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {selectedFlag.flag_type_display}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{selectedFlag.message}</p>
                      </div>
                      
                      {selectedFlag.severity === 'error' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Exclusion Reason (Required)
                          </label>
                          <textarea
                            value={exclusionReason}
                            onChange={(e) => setExclusionReason(e.target.value)}
                            placeholder="Explain why this employee is being excluded..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            rows={3}
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Review Notes (Optional)
                          </label>
                          <textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add any notes..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            rows={2}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                      <button
                        onClick={() => {
                          setShowFlagReviewModal(false);
                          setSelectedFlag(null);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      {selectedFlag.severity === 'error' ? (
                        <button
                          onClick={() => {
                            if (!exclusionReason.trim()) {
                              toast.error('Exclusion reason is required');
                              return;
                            }
                            excludeFromRunMutation.mutate(
                              { flagId: selectedFlag.id, exclusionReason },
                              {
                                onSuccess: () => {
                                  setShowFlagReviewModal(false);
                                  setSelectedFlag(null);
                                  refetchFlagSummary();
                                  refetchFlags();
                                }
                              }
                            );
                          }}
                          disabled={excludeFromRunMutation.isPending || !exclusionReason.trim()}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          {excludeFromRunMutation.isPending ? 'Excluding...' : 'Exclude from Run'}
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            reviewFlagMutation.mutate(
                              { flagId: selectedFlag.id, reviewNotes },
                              {
                                onSuccess: () => {
                                  setShowFlagReviewModal(false);
                                  setSelectedFlag(null);
                                  refetchFlagSummary();
                                  refetchFlags();
                                }
                              }
                            );
                          }}
                          disabled={reviewFlagMutation.isPending}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {reviewFlagMutation.isPending ? 'Saving...' : 'Mark as Reviewed'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

            {/* Footer */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <button
                onClick={() => setActiveStep(2)}
                className="btn btn-admin-outline"
              >
                <ChevronRight className="w-4 h-4 mr-2 rotate-180" />
                Back
              </button>
              <div className="flex items-center gap-3">
                {flagSummary?.review_count > 0 && (
                  <button
                    onClick={() => setShowReviewAllModal(true)}
                    disabled={bulkReviewMutation.isPending}
                    className="btn btn-admin-outline"
                    title="See what needs review, grouped by type, and acknowledge"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span>Review Flags</span>
                    <CountBadge count={flagSummary.review_count} theme="admin" max={9999} className="ml-2" />
                  </button>
                )}
                <button
                  onClick={() => setActiveStep(4)}
                  disabled={!canContinue}
                  className="btn btn-admin-primary"
                  title={!canContinue ? step4BlockedReason : 'Proceed to payment review'}
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
                  </div>
                )}
          </div>
        )}
        
            {/* Step 4: Review & Payment */}
            {activeStep === 4 && (
              <PayrollPaymentStep
                currentRun={payrollRuns.find((run) => run.id === currentPayrollRunId)}
                payrollRuns={payrollRuns}
                employeePayrolls={employeePayrolls}
                departmentProgress={departmentProgress}
                flagSummary={flagSummary}
                flagSummaryLoading={flagSummaryLoading}
                flagSummaryError={flagSummaryError}
                allPayrollsLocked={allPayrollsLocked}
                unfinalizedPayrollCount={unfinalizedPayrollCount}
                selectedPeriod={selectedPeriod}
                formatCurrency={formatCurrency}
                onBack={() => setActiveStep(3)}
                onExportPayrollRegister={handleMasterListExportXLSX}
                payrollRegisterExporting={masterListExporting}
              />
            )}

      </div>

      {/* Adjust & Lock Progress Modal (non-dismissible) */}
      <ProgressModal
        isOpen={progressModal.open}
        title="Adjust & Lock"
        phases={ADJUST_PHASES}
        currentPhase={progressModal.currentPhase}
        progress={progressModal.progress}
        statusMessage={progressModal.statusMessage}
        error={progressModal.error}
        department={progressModal.department}
        currentEmployee={progressModal.currentEmployee}
        subPhase={progressModal.subPhase}
        onDismissError={() => setProgressModal(prev => ({ ...prev, open: false, error: null }))}
      />
      {/* Calculate Payroll Progress Modal (mirrors Step 2 mental model) */}
      <ProgressModal
        isOpen={showCalcModal}
        title="Calculate Payroll"
        phases={CALC_PHASES}
        currentPhase={calcModalProps.currentPhase}
        progress={calcModalProps.progress}
        statusMessage={calcModalProps.statusMessage}
        error={calcModalProps.error}
        onDismissError={() => { setShowCalcModal(false); setCalculationTaskId(null); }}
      />
      <ConfirmModal
        isOpen={!!confirmAction}
        title="Delete Site Session"
        message={`Delete ${confirmAction?.site?.work_site_name ? `the ${confirmAction.site.work_site_name} session` : 'this attendance session'} on ${formatDateDisplay(confirmAction?.rec?.date)}? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleConfirmDeleteSession}
        onCancel={() => setConfirmAction(null)}
        theme="admin"
      />
    </div>
  );
};

const ALLOWANCE_STATUS_MAP = {
  approved: 'approved',
  pending: 'pending',
};

const AllowanceManagementTab = () => {
  const [allowances, setAllowances] = useState([]);
  const [allowanceTypes, setAllowanceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ allowance_type: '' });

  const { data: staffData } = useStaffList(
    { page_size: 500, employment_status: 'active' },
    { enabled: true }
  );
  const staffMap = useMemo(() => {
    const map = {};
    (staffData?.results || []).forEach(s => {
      map[s.staff_id] = s;
    });
    return map;
  }, [staffData]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [allowancesRes, typesRes] = await Promise.all([
        confidentialAPI.getStaffAllowances(),
        configAPI.getAllowanceTypes(),
      ]);
      const allAllowances = allowancesRes.data?.results || allowancesRes.data || [];
      setAllowances(allAllowances.filter(a => !a.staff_allowance_deleted_at));
      setAllowanceTypes(typesRes.data?.results || typesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch allowances:', err);
      toast.error('Failed to load allowances');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTypeName = useCallback((allowance) => {
    const typeId = allowance.staff_allowance_type_id?.id || allowance.staff_allowance_type_id;
    const type = allowanceTypes.find(t => t.id === typeId);
    return type?.allowance_type_name || allowance.staff_allowance_type_id?.allowance_type_name || 'Unknown';
  }, [allowanceTypes]);

  const getEmployeeName = useCallback((staffId) => {
    const emp = staffMap[staffId];
    if (!emp) return staffId;
    return `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || staffId;
  }, [staffMap]);

  const activeFilterCount = [filters.allowance_type].filter(Boolean).length;

  const filteredAllowances = useMemo(() => {
    return allowances.filter(a => {
      if (filters.allowance_type) {
        const typeId = a.staff_allowance_type_id?.id || a.staff_allowance_type_id;
        if (String(typeId) !== String(filters.allowance_type)) return false;
      }
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const staffId = (a.staff_allowance_staff_id || '').toLowerCase();
        const empName = getEmployeeName(a.staff_allowance_staff_id).toLowerCase();
        const typeName = getTypeName(a).toLowerCase();
        if (!staffId.includes(term) && !empName.includes(term) && !typeName.includes(term)) return false;
      }
      return true;
    });
  }, [allowances, filters, searchTerm, getEmployeeName, getTypeName]);

  const isActive = (a) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(a.staff_allowance_effective_start);
    const resolvedEnd = a.effective_end_resolved ? new Date(a.effective_end_resolved) : null;
    return start <= today && (!resolvedEnd || resolvedEnd >= today);
  };

  const summaryStats = useMemo(() => {
    const active = allowances.filter(isActive);
    return {
      total: allowances.length,
      active: active.length,
      monthlyTotal: active.reduce((sum, a) => sum + parseFloat(a.staff_allowance_amount || 0), 0),
      types: allowanceTypes.length,
    };
  }, [allowances, allowanceTypes]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount || 0);

  if (loading && allowances.length === 0) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Award className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.active}</p>
              <p className="text-xs text-gray-500">Active Allowances</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.total}</p>
              <p className="text-xs text-gray-500">Total Records</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summaryStats.types}</p>
              <p className="text-xs text-gray-500">Allowance Types</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryStats.monthlyTotal)}</p>
              <p className="text-xs text-gray-500">Monthly Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 max-w-xl">
          <DebouncedSearchInput
            value={searchTerm}
            onSearch={setSearchTerm}
            placeholder="Search by employee, staff ID, or allowance type..."
            theme="admin"
          />
        </div>
        <div className="flex items-center gap-2">
          <FilterButton
            theme="admin"
            isOpen={showFilters}
            activeCount={activeFilterCount}
            onClick={() => setShowFilters(v => !v)}
          />
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormSelect
            value={filters.allowance_type}
            onChange={(val) => setFilters(f => ({ ...f, allowance_type: val }))}
            options={[
              { value: '', label: 'All Types' },
              ...allowanceTypes.map(t => ({ value: t.id, label: t.allowance_type_name })),
            ]}
            placeholder="Allowance Type"
            theme="admin"
          />
        </div>
      )}

      {/* Allowances Table */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effective Period</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredAllowances.map((a) => {
                const active = isActive(a);
                const approved = !!a.staff_allowance_approved_by_staff_id;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const startDate = new Date(a.staff_allowance_effective_start);
                const resolvedEnd = a.effective_end_resolved ? new Date(a.effective_end_resolved) : null;
                const isFuture = startDate > today;
                const isExpired = resolvedEnd && resolvedEnd < today;

                let statusKey = 'pending';
                if (!approved) statusKey = 'pending';
                else if (isExpired) statusKey = 'expired';
                else if (isFuture) statusKey = 'not_started';
                else if (active) statusKey = 'active';
                else statusKey = 'approved';

                const statusLabel = isFuture && approved ? 'Future' : undefined;

                return (
                  <tr key={a.id} className="table-row-dense">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{getEmployeeName(a.staff_allowance_staff_id)}</span>
                        <span className="text-xs text-gray-400">{a.staff_allowance_staff_id}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-700">{getTypeName(a)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <span className="text-sm font-semibold text-gray-900">{formatCurrency(a.staff_allowance_amount)}/mo</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          {formatDate(a.staff_allowance_effective_start)}
                          {' — '}
                          {a.effective_end_resolved ? formatDate(a.effective_end_resolved) : 'Ongoing'}
                        </span>
                        {a.is_capped_by_employment_end && (
                          <span title="End date derived from employment end date">
                            <InfoBadge color="orange" label="Employment End" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={statusKey} label={statusLabel} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAllowances.length === 0 && !loading && (
          <div className="text-center py-12">
            <Award className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">No allowances found</h3>
            <p className="mt-2 text-sm text-gray-500">
              {searchTerm || activeFilterCount > 0
                ? 'Try adjusting your search or filters.'
                : 'No employee allowances have been assigned yet. Assign allowances from individual employee profiles.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const MOMComplianceTab = ({ getComplianceScoreColor }) => {
  const { data: complianceReports = [], isLoading } = useComplianceReports();

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Compliance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-green-50 rounded-2xl p-6 border border-green-200">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-green-900">
                {complianceReports.filter(r => r.is_fully_compliant).length}
              </p>
              <p className="text-sm text-green-700">Fully Compliant</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-2xl p-6 border border-yellow-200">
          <div className="flex items-center">
            <AlertTriangle className="w-8 h-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-yellow-900">
                {complianceReports.filter(r => r.compliance_score >= 7 && r.compliance_score < 12).length}
              </p>
              <p className="text-sm text-yellow-700">Needs Attention</p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 rounded-2xl p-6 border border-red-200">
          <div className="flex items-center">
            <AlertTriangle className="w-8 h-8 text-red-600" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-red-900">
                {complianceReports.filter(r => r.compliance_score < 7).length}
              </p>
              <p className="text-sm text-red-700">Non-Compliant</p>
            </div>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div className="flex justify-end">
        <button 
          onClick={() => exportMOMComplianceReportPDF(complianceReports)}
          className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors font-medium"
          disabled={complianceReports.length === 0}
        >
          <Download className="w-4 h-4 mr-2" />
          Export Compliance Report (PDF)
        </button>
      </div>

      {/* Compliance Reports */}
      <div className="bg-white rounded-2xl shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">🛡️ MOM Payslip Compliance Reports</h3>
        </div>
        
        <div className="space-y-3">
          {complianceReports.map((report) => (
            <div key={report.id} className="p-6 border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-medium text-gray-900">{report.employee_full_name}</h4>
                    <span className={`text-sm font-medium ${getComplianceScoreColor(report.compliance_score)}`}>
                      {report.compliance_score}/12 Items
                    </span>
                    {report.is_fully_compliant ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                  
                  {report.compliance_issues && report.compliance_issues.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">Issues:</p>
                      <ul className="mt-1 text-sm text-red-600 space-y-1">
                        {report.compliance_issues.slice(0, 3).map((issue, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="w-1.5 h-1.5 bg-red-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                            {issue}
                          </li>
                        ))}
                        {report.compliance_issues.length > 3 && (
                          <li className="text-gray-500">
                            +{report.compliance_issues.length - 3} more issues...
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors">
                    View Details
                  </button>
                  <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors">
                    Generate Payslip
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ReportsAnalyticsTab = ({ formatCurrency, payrollRuns }) => {
  const { data: statistics = {}, isLoading } = usePayrollStatistics();

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Analytics Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="text-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <p className="mt-3 text-lg sm:text-2xl font-bold text-gray-900">
              {formatCurrency(statistics.avg_basic_salary)}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 truncate">Avg Basic Salary</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="text-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <p className="mt-3 text-lg sm:text-2xl font-bold text-gray-900">
              {formatCurrency(statistics.total_allowances)}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 truncate">Total Allowances</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="text-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <p className="mt-3 text-lg sm:text-2xl font-bold text-gray-900">
              {formatCurrency(statistics.total_cpf_employee)}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 truncate">Employee CPF</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="text-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
            <p className="mt-3 text-lg sm:text-2xl font-bold text-gray-900">
              {formatCurrency(statistics.total_sdl)}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 truncate">Skills Dev. Levy</p>
          </div>
        </div>
      </div>

      {/* Export Options */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Export Reports</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <button 
            onClick={() => exportPayrollStatisticsExcel(statistics, payrollRuns)}
            className="flex items-center justify-center px-6 py-4 text-sm font-medium text-blue-700 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors"
          >
            <Download className="w-5 h-5 mr-2" />
            Statistics (Excel)
          </button>
          <button 
            onClick={() => payrollRuns.length > 0 && exportPayrollSummaryPDF(payrollRuns[0], statistics)}
            className="flex items-center justify-center px-6 py-4 text-sm font-medium text-green-700 bg-green-50 rounded-full hover:bg-green-100 transition-colors"
            disabled={payrollRuns.length === 0}
          >
            <FileText className="w-5 h-5 mr-2" />
            Payroll Summary (PDF)
          </button>
          <button 
            onClick={() => payrollRuns.length > 0 && exportCPFSubmissionFile(payrollRuns[0].id)}
            className="flex items-center justify-center px-6 py-4 text-sm font-medium text-purple-700 bg-purple-50 rounded-full hover:bg-purple-100 transition-colors"
            disabled={payrollRuns.length === 0}
          >
            <CreditCard className="w-5 h-5 mr-2" />
            CPF Submission File
          </button>
          <button 
            onClick={() => {
              // This would need compliance reports data - for demo purposes
              toast('Compliance report export - connect to compliance data');
            }}
            className="flex items-center justify-center px-6 py-4 text-sm font-medium text-red-700 bg-red-50 rounded-full hover:bg-red-100 transition-colors"
          >
            <Shield className="w-5 h-5 mr-2" />
            MOM Compliance Report
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentComplianceTab = ({ complianceDashboard, paymentDeadlines, onRefresh, formatCurrency }) => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  
  const filteredDeadlines = paymentDeadlines.filter(deadline => {
    return selectedFilter === 'all' || 
      (selectedFilter === 'overdue' && deadline.is_overdue) ||
      (selectedFilter === 'approaching' && deadline.is_approaching_deadline) ||
      (selectedFilter === 'pending' && deadline.compliance_status === 'pending');
  });

  const getComplianceStatusKey = (status) => {
    const map = { overdue: 'rejected', paid_on_time: 'completed', paid_late: 'on_hold' };
    return map[status] || status;
  };

  const getComplianceStatusLabel = (status) => {
    const labels = {
      overdue: 'Overdue',
      paid_on_time: 'Paid on Time',
      paid_late: 'Paid Late'
    };
    return labels[status];
  };

  const getDeadlineTypeIcon = (type) => {
    const icons = {
      salary: Calculator,
      overtime: Clock,
      termination_salary: AlertTriangle,
      termination_overtime: AlertTriangle,
      leave_encashment: Calendar,
      final_settlement: FileText
    };
    return icons[type] || FileText;
  };

  return (
    <div className="space-y-6">
      {/* Compliance Dashboard */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Timer className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {complianceDashboard.total_pending_payments || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Pending Payments</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {complianceDashboard.overdue_payments || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Overdue Payments</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {complianceDashboard.approaching_deadlines || 0}
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Due Soon (3 days)</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {complianceDashboard.compliance_rate || 100}%
              </p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Compliance Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">🚨 Payment Compliance Monitoring</h3>
          <div className="flex items-center space-x-3">
            <button
              onClick={onRefresh}
              className="icon-button-admin"
              title="Refresh Data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-6">
          {[
            { id: 'all', label: 'All Payments', count: paymentDeadlines.length },
            { id: 'overdue', label: 'Overdue', count: complianceDashboard.overdue_payments || 0 },
            { id: 'approaching', label: 'Due Soon', count: complianceDashboard.approaching_deadlines || 0 },
            { id: 'pending', label: 'Pending', count: complianceDashboard.total_pending_payments || 0 }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setSelectedFilter(filter.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 md:flex-shrink ${
                selectedFilter === filter.id
                  ? 'bg-blue-100 text-blue-700 border-2 border-blue-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-2 border-transparent'
              }`}
            >
              {filter.label}
              {filter.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  selectedFilter === filter.id ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-600'
                }`}>
                  {filter.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Payment Deadlines List */}
        <div className="space-y-4">
          {filteredDeadlines.length === 0 ? (
            <div className="text-center py-12">
              <Timer className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Deadlines</h3>
              <p className="text-gray-500">
                No payment deadlines found for the selected filter.
              </p>
            </div>
          ) : (
            filteredDeadlines.slice(0, 20).map((deadline) => {
              const DeadlineIcon = getDeadlineTypeIcon(deadline.deadline_type);
              return (
                <div key={deadline.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      deadline.is_overdue ? 'bg-red-100' : 
                      deadline.is_approaching_deadline ? 'bg-orange-100' : 'bg-blue-100'
                    }`}>
                      <DeadlineIcon className={`w-5 h-5 ${
                        deadline.is_overdue ? 'text-red-600' : 
                        deadline.is_approaching_deadline ? 'text-orange-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{deadline.employee_name}</p>
                      <p className="text-sm text-gray-600">{deadline.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="font-medium text-gray-900">{formatCurrency(deadline.amount_due)}</p>
                      <p className="text-sm text-gray-600">
                        Due: {formatDate(deadline.payment_due_date)}
                      </p>
                    </div>
                    <StatusBadge
                      status={getComplianceStatusKey(deadline.compliance_status)}
                      label={getComplianceStatusLabel(deadline.compliance_status) || deadline.compliance_status_display || deadline.compliance_status}
                    />
                    {deadline.is_overdue && (
                      <div className="text-red-600 text-sm font-medium">
                        {Math.abs(deadline.days_until_due)} days overdue
                      </div>
                    )}
                    {deadline.is_approaching_deadline && !deadline.is_overdue && (
                      <div className="text-orange-600 text-sm font-medium">
                        Due in {deadline.days_until_due} days
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {filteredDeadlines.length > 20 && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              Showing 20 of {filteredDeadlines.length} payment deadlines
            </p>
          </div>
        )}
      </div>

      {/* MOM Compliance Guidelines */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 MOM Payment Deadlines</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-2xl">
            <div className="flex items-center mb-2">
              <Calculator className="w-5 h-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-900">Regular Salary</h4>
            </div>
            <p className="text-sm text-blue-700">Must be paid within 7 days after end of salary period</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-2xl">
            <div className="flex items-center mb-2">
              <Clock className="w-5 h-5 text-orange-600 mr-2" />
              <h4 className="font-medium text-orange-900">Overtime Pay</h4>
            </div>
            <p className="text-sm text-orange-700">Must be paid within 14 days after end of salary period</p>
          </div>
          <div className="p-4 bg-red-50 rounded-2xl">
            <div className="flex items-center mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              <h4 className="font-medium text-red-900">Termination Pay</h4>
            </div>
            <p className="text-sm text-red-700">Varies by termination type - immediate to 7 days</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PayrollSettingsTab = () => {
  return (
    <div className="space-y-6">
      {/* Settings Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center mb-4">
            <Settings className="w-6 h-6 text-blue-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">General Settings</h3>
          </div>
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Default Currency</span>
              <span className="font-medium">SGD</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Pay Frequency</span>
              <span className="font-medium">Monthly</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Rounding Method</span>
              <span className="font-medium">Round to nearest cent</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center mb-4">
            <Shield className="w-6 h-6 text-green-600 mr-3" />
            <h3 className="text-lg font-semibold text-gray-900">MOM Compliance</h3>
          </div>
          <div className="space-y-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Payslip Format</span>
              <span className="font-medium">MOM Compliant</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">SDL Rate</span>
              <span className="font-medium">0.25%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Auto-validation</span>
              <span className="font-medium text-green-600">Enabled</span>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Links */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">⚙️ Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <button className="flex items-center px-4 py-3 text-left text-sm text-gray-700 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors">
            <DollarSign className="w-5 h-5 mr-3 text-gray-500" />
            Manage Allowance Types
          </button>
          <button className="flex items-center px-4 py-3 text-left text-sm text-gray-700 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors">
            <Calculator className="w-5 h-5 mr-3 text-gray-500" />
            CPF Rate Configuration
          </button>
          <button className="flex items-center px-4 py-3 text-left text-sm text-gray-700 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors">
            <Clock className="w-5 h-5 mr-3 text-gray-500" />
            Work Schedule Templates
          </button>
        </div>
      </div>
    </div>
  );
};

const FinalSettlementsTab = ({ formatCurrency, onCountChange }) => {
  const formatFinalEmploymentBoundary = (employee) => {
    const session = (
      employee?.final_working_session
      || employee?.employment_end_session
      || 'PM'
    ).toUpperCase();
    return `${formatDate(employee?.final_working_day)} · after ${session}`;
  };

  // ── Stepper state ──────────────────────────────────────────────────────────
  const [activeStep, setActiveStep] = usePersistence('final-settlements-step', 1, [1, 2, 3]);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    try {
      const saved = localStorage.getItem('fs-selected-period');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (parsed?.start_date && parsed?.end_date) {
        const periodDate = new Date(parsed.start_date);
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        if (periodDate >= sixMonthsAgo) return parsed;
      }
    } catch { /* ignore corrupt localStorage */ }
    return null;
  });
  const [fsEmployees, setFsEmployees] = useState([]);
  const [loading, setLoading] = useState(false);

  // Step 2 (Calculate) state
  const [calculationResults, setCalculationResults] = useState({});
  const [calculatingIds, setCalculatingIds] = useState(new Set());

  // Step 2/3 detail panel state
  const [selectedFsPayrollId, setSelectedFsPayrollId] = useState(null);

  // Step 2 export state
  const [fsExportingXlsx, setFsExportingXlsx] = useState(false);
  const [fsExportingPdf, setFsExportingPdf] = useState(false);

  // Step 3 (Review & Process) state
  const [reviewEmployee, setReviewEmployee] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [settlementNotes, setSettlementNotes] = useState('');

  const { data: fsPayrollDetail, refetch: refetchFsPayrollDetail } = useEmployeePayrollDetail(selectedFsPayrollId);

  // Adjustment modal state (shared pattern with regular payroll Step 3)
  const [showFsAdjustmentModal, setShowFsAdjustmentModal] = useState(false);

  // Finalize/lock hooks (shared pattern with regular payroll Step 3)
  const fsFinalizePayrollMutation = useFinalizePayroll();
  const fsUnfinalizePayrollMutation = useUnfinalizePayroll();
  const [showUnfinalizeModal, setShowUnfinalizeModal] = useState(false);
  const [unfinalizeReason, setUnfinalizeReason] = useState('');

  // ── Step definitions ───────────────────────────────────────────────────────
  const fsSteps = [
    { id: 1, label: 'Adjust & Lock', icon: Settings },
    { id: 2, label: 'Calculate Settlement', icon: Calculator },
    { id: 3, label: 'Review & Process', icon: CheckCircle },
  ];

  // Derive the period string for the useAdjustAndLock hook (e.g. "2026-03")
  const hookPeriod = useMemo(() => {
    if (!selectedPeriod) return null;
    return selectedPeriod.start_date.slice(0, 7);
  }, [selectedPeriod]);

  // Only show Final Settlement employees in the Adjust & Lock panel
  const fsStaffIds = useMemo(
    () => fsEmployees.map(e => e.staff_id),
    [fsEmployees]
  );

  // Map staff_id → employment end date for inactive date graying
  const employmentEndDates = useMemo(
    () => Object.fromEntries(fsEmployees.map(e => [e.staff_id, e.final_working_day])),
    [fsEmployees]
  );

  // ── Adjust & Lock Hook ─────────────────────────────────────────────────────
  const adjustAndLock = useAdjustAndLock({
    period: hookPeriod,
    departmentIds: [],
    enabled: activeStep === 1 && !!selectedPeriod && fsEmployees.length > 0,
    persistencePrefix: 'final-settlements',
    staffIdFilter: fsStaffIds.length > 0 ? fsStaffIds : null,
    employmentEndDates,
  });

  // ── Gating logic ───────────────────────────────────────────────────────────
  const lockedEmployeeCount = useMemo(() => {
    if (!adjustAndLock.employeeList || adjustAndLock.employeeList.length === 0) return 0;
    return adjustAndLock.employeeList.filter(e => e.lock_status === 'locked').length;
  }, [adjustAndLock.employeeList]);

  const allFsEmployeesLocked = useMemo(() => {
    return fsEmployees.length > 0 && lockedEmployeeCount >= fsEmployees.length;
  }, [fsEmployees.length, lockedEmployeeCount]);

  const canProceedToStep2 = lockedEmployeeCount > 0;
  const canProceedToStep3 = Object.keys(calculationResults).length > 0;

  // ── Last-normalized tracking ────────────────────────────────────────────
  const LS_NORMALIZE_KEY = 'fs-last-normalized';
  const [lastNormalizedAt, setLastNormalizedAt] = useState(null);

  useEffect(() => {
    if (!hookPeriod) { setLastNormalizedAt(null); return; }
    try {
      const map = JSON.parse(localStorage.getItem(LS_NORMALIZE_KEY) || '{}');
      setLastNormalizedAt(map[hookPeriod] || null);
    } catch { setLastNormalizedAt(null); }
  }, [hookPeriod]);

  const recordNormalization = useCallback(() => {
    if (!hookPeriod) return;
    const now = new Date().toISOString();
    try {
      const map = JSON.parse(localStorage.getItem(LS_NORMALIZE_KEY) || '{}');
      map[hookPeriod] = now;
      localStorage.setItem(LS_NORMALIZE_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
    setLastNormalizedAt(now);
  }, [hookPeriod]);

  // ── URL sync ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('tab') === 'final-settlements') {
      url.searchParams.set('step', activeStep);
      window.history.replaceState({}, '', url);
    }
  }, [activeStep]);

  // ── Fetch Final Settlement employees ───────────────────────────────────────
  useEffect(() => {
    if (selectedPeriod) {
      fetchFinalSettlementEmployees();
    } else {
      setFsEmployees([]);
      onCountChange?.(null);
    }
  }, [selectedPeriod]);

  // Clear badge when this tab unmounts (e.g. user navigates to another tab)
  useEffect(() => () => onCountChange?.(null), []);

  // ── Period persistence ────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedPeriod) {
      localStorage.setItem('fs-selected-period', JSON.stringify(selectedPeriod));
    } else {
      localStorage.removeItem('fs-selected-period');
    }
  }, [selectedPeriod]);

  // ── Smart resume: auto-load attendance when progress exists ─────────────
  const autoResumeRef = useRef(null);
  const stepResolvedRef = useRef(false);

  useEffect(() => {
    const periodKey = selectedPeriod
      ? `${selectedPeriod.start_date}_${selectedPeriod.end_date}`
      : null;
    if (!periodKey || !fsEmployees.length) return;
    if (autoResumeRef.current === periodKey) return;
    if (adjustAndLock.adjustedReady || adjustAndLock.adjusting) return;

    const hasExistingProgress = fsEmployees.some(e => e.progress !== 'not_started');
    if (hasExistingProgress) {
      autoResumeRef.current = periodKey;
      setActiveStep(1);
      adjustAndLock.handleLoadOnly();
    }
  }, [fsEmployees, selectedPeriod, adjustAndLock.adjustedReady, adjustAndLock.adjusting]);

  // After auto-load completes, advance to the step that matches server state
  useEffect(() => {
    if (stepResolvedRef.current || !autoResumeRef.current) return;
    if (!adjustAndLock.employeeList?.length) return;

    stepResolvedRef.current = true;

    const hasCalculations = Object.keys(calculationResults).length > 0;
    if (hasCalculations && lockedEmployeeCount > 0) {
      setActiveStep(2);
    }
  }, [adjustAndLock.employeeList, calculationResults, lockedEmployeeCount]);

  const fetchFinalSettlementEmployees = async () => {
    setLoading(true);
    try {
      const response = await api.getFinalSettlementEmployees({
        start_date: selectedPeriod.start_date,
        end_date: selectedPeriod.end_date,
      });
      const emps = response.data.employees || [];
      setFsEmployees(emps);
      onCountChange?.(emps.length || null);

      // Pre-populate calculationResults from existing backend calculations
      const restoredResults = {};
      emps.forEach(emp => {
        if (emp.existing_calculation) {
          restoredResults[emp.staff_id] = {
            success: true,
            data: emp.existing_calculation,
            employee: emp,
          };
        }
      });
      if (Object.keys(restoredResults).length > 0) {
        setCalculationResults(prev => ({ ...restoredResults, ...prev }));
      }

      if (emps.length > 0) {
        const willAutoResume = emps.some(e => e.progress !== 'not_started');
        if (!willAutoResume) {
          toast.success(`Found ${emps.length} employee(s) for final settlement`, { id: 'fs-employees-loaded' });
        }
      } else {
        toast('No employees with final working day in this period', { id: 'fs-employees-loaded' });
      }
    } catch (error) {
      console.error('Error fetching final settlement employees:', error);
      toast.error('Failed to load employees for final settlement');
      setFsEmployees([]);
      onCountChange?.(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Export handlers ──────────────────────────────────────────────

  const getFsPayrollRunId = () => {
    const firstCalc = Object.values(calculationResults).find(r => r.success && r.data?.payroll_run_id);
    return firstCalc?.data?.payroll_run_id || null;
  };

  const handleFsExportXlsx = async () => {
    const runId = getFsPayrollRunId();
    if (!runId) return;
    setFsExportingXlsx(true);
    try {
      const res = await api.get(
        `/confidential/payroll-runs/${runId}/final-settlement-export/`,
        { responseType: 'blob', timeout: 120000 }
      );
      const disposition = res.headers['content-disposition'];
      const filenameMatch = disposition?.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch?.[1] || 'Final_Settlement_Report.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Final Settlement Report exported');
    } catch (err) {
      console.error('FS XLSX export error:', err);
      toast.error(err.response?.data?.error || 'Export failed');
    } finally {
      setFsExportingXlsx(false);
    }
  };

  const handleFsExportPdf = async () => {
    const runId = getFsPayrollRunId();
    if (!runId) return;
    setFsExportingPdf(true);
    try {
      const res = await api.get(
        `/confidential/payroll-runs/${runId}/final-settlement-report-data/`,
        { timeout: 120000 }
      );
      await exportFinalSettlementPDF(res.data);
    } catch (err) {
      console.error('FS PDF export error:', err);
      toast.error(err.response?.data?.error || 'PDF export failed');
    } finally {
      setFsExportingPdf(false);
    }
  };

  // ── Step 2: Per-employee calculation ──────────────────────────────────────
  const handleCalculateOne = async (emp) => {
    setCalculatingIds(prev => new Set(prev).add(emp.staff_id));
    try {
      const response = await api.calculateFinalSettlement({
        staff_id: emp.staff_id,
        final_working_day: emp.final_working_day,
      });
      setCalculationResults(prev => ({
        ...prev,
        [emp.staff_id]: { success: true, data: response.data, employee: emp },
      }));
      toast.success(`Settlement calculated for ${emp.full_name}`, { id: `fs-calc-${emp.staff_id}` });
      if (response.data?.employee_payroll_id) {
        setSelectedFsPayrollId(response.data.employee_payroll_id);
      }
    } catch (error) {
      setCalculationResults(prev => ({
        ...prev,
        [emp.staff_id]: {
          success: false,
          error: error.response?.data?.error || 'Calculation failed',
          employee: emp,
        },
      }));
      toast.error(`Calculation failed for ${emp.full_name}`, { id: `fs-calc-${emp.staff_id}` });
    } finally {
      setCalculatingIds(prev => {
        const next = new Set(prev);
        next.delete(emp.staff_id);
        return next;
      });
    }
  };

  const handleBatchCalculatePending = async () => {
    const lockedEmps = fsEmployees.filter(emp => {
      const hookEmp = adjustAndLock.employeeList.find(e => e.staff_id === emp.staff_id);
      return hookEmp?.lock_status === 'locked' && !calculationResults[emp.staff_id]?.success;
    });
    if (lockedEmps.length === 0) {
      toast('All employees already calculated', { id: 'fs-batch-calc' });
      return;
    }
    for (const emp of lockedEmps) {
      await handleCalculateOne(emp);
    }
  };

  // ── Step 3: Process settlement ─────────────────────────────────────────────
  const openProcessModal = (emp) => {
    if (emp.offboarding?.ir21_hold_active) {
      toast.warning('IR21 is holding this payment. Record the IRAS clearance outcome in Offboarding first.');
      return;
    }
    const result = calculationResults[emp.staff_id];
    if (!result?.success) {
      toast.error('Settlement has not been calculated for this employee');
      return;
    }
    setReviewEmployee(emp);
    const dueDate = new Date(result.data.payment_due_date);
    setPaymentDate(formatDateForInput(dueDate));
    setPaymentReference('');
    setSettlementNotes('');
    setShowProcessModal(true);
  };

  const processSettlement = async () => {
    if (!paymentDate || !paymentReference.trim()) {
      toast.error('Please provide payment date and reference');
      return;
    }
    setProcessing(true);
    try {
      const calcResult = calculationResults[reviewEmployee.staff_id];
      await api.processFinalSettlement({
        staff_id: reviewEmployee.staff_id,
        final_working_day: reviewEmployee.final_working_day,
        payment_date: paymentDate,
        payment_reference: paymentReference,
        settlement_notes: settlementNotes,
        employee_payroll_id: calcResult?.data?.employee_payroll_id || undefined,
      });
      toast.success('Final settlement processed successfully');
      setShowProcessModal(false);
      await fetchFinalSettlementEmployees();
      setReviewEmployee(null);
      setCalculationResults(prev => {
        const next = { ...prev };
        delete next[reviewEmployee.staff_id];
        return next;
      });
    } catch (error) {
      if (error.response?.data?.code === 'ir21_hold') {
        toast.warning(error.response.data.error);
      } else {
        toast.error(error.response?.data?.error || 'Failed to process settlement');
      }
    } finally {
      setProcessing(false);
    }
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  // ── Step indicator (reuses Processing tab pattern) ─────────────────────────
  const renderStepIndicator = () => {
    const handleStepClick = (stepId) => {
      if (stepId === 1) {
        setActiveStep(1);
      } else if (stepId === 2 && canProceedToStep2) {
        setActiveStep(2);
      } else if (stepId === 3 && canProceedToStep3) {
        setActiveStep(3);
      } else if (stepId < activeStep) {
        setActiveStep(stepId);
      }
    };

    return (
      <div className="mb-4">
        <div className="flex items-center justify-center space-x-4">
          {fsSteps.map((step, index) => {
            const Icon = step.icon;
            const isActive = activeStep >= step.id;
            const isCompleted = activeStep > step.id;
            const isCurrent = activeStep === step.id;
            const isClickable = step.id === 1
              || (step.id === 2 && canProceedToStep2)
              || (step.id === 3 && canProceedToStep3)
              || step.id < activeStep;

            return (
              <React.Fragment key={step.id}>
                <div
                  className={`flex flex-col items-center flex-1 ${
                    isClickable ? 'cursor-pointer group' : 'cursor-not-allowed'
                  }`}
                  onClick={() => isClickable && handleStepClick(step.id)}
                  title={isClickable
                    ? (isCurrent ? 'Current step' : `Navigate to ${step.label}`)
                    : step.id === 2 ? 'At least 1 employee must be locked' : 'Complete previous steps first'
                  }
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 ${
                    isCompleted
                      ? 'bg-red-600 text-white shadow-sm'
                      : isActive
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'bg-gray-200 text-gray-500'
                  } ${isClickable && !isCurrent ? 'group-hover:ring-2 group-hover:ring-red-300 group-hover:ring-offset-2' : ''}`}>
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="mt-1.5 text-center">
                    <div className={`text-xs font-medium transition-colors duration-200 ${
                      activeStep >= step.id ? 'text-red-600' : 'text-gray-500'
                    } ${isClickable && !isCurrent ? 'group-hover:text-red-700' : ''}`}>
                      {step.label}
                    </div>
                  </div>
                </div>
                {index < fsSteps.length - 1 && (
                  <div className={`flex-1 h-1 rounded-full transition-all duration-200 self-start mt-4 ${
                    activeStep > step.id ? 'bg-red-600' : 'bg-gray-200'
                  }`} style={{ minWidth: '60px', maxWidth: '150px' }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header with period selector */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Final Settlements</h3>
          <PillSelect
            options={Array.from({ length: 6 }, (_, i) => {
              const date = new Date();
              date.setMonth(date.getMonth() - i);
              const year = date.getFullYear();
              const month = date.getMonth() + 1;
              const start = `${year}-${String(month).padStart(2, '0')}-01`;
              const lastDay = new Date(year, month, 0).getDate();
              const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
              const monthName = date.toLocaleDateString('en-SG', { year: 'numeric', month: 'long' });
              return { value: `${start}_${end}`, label: monthName };
            })}
            value={selectedPeriod ? `${selectedPeriod.start_date}_${selectedPeriod.end_date}` : ''}
            onChange={(val) => {
              if (!val) {
                setSelectedPeriod(null);
                return;
              }
              const [start, end] = val.split('_');
              setSelectedPeriod({ start_date: start, end_date: end });
              setActiveStep(1);
              setCalculationResults({});
              autoResumeRef.current = null;
              stepResolvedRef.current = false;
            }}
            placeholder="Select a period..."
            icon={Calendar}
            iconColor="text-red-600"
            theme="admin"
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 flex flex-col items-center gap-3">
          <LoadingSpinner />
          <p className="text-sm text-gray-500">Loading employees…</p>
        </div>
      )}

      {/* No period selected */}
      {!selectedPeriod && !loading && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Select a period to view employees for final settlement</p>
        </div>
      )}

      {/* No employees */}
      {selectedPeriod && !loading && fsEmployees.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <UserMinus className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No employees with final working day in this period</p>
        </div>
      )}

      {/* Stepper + step content (only when we have employees) */}
      {selectedPeriod && !loading && fsEmployees.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border pt-4 px-6 pb-6">
          {renderStepIndicator()}

          {/* ── Step 1: Adjust & Lock ──────────────────────────────────── */}
          {activeStep === 1 && (
            <div>
              {/* Attendance data loaded: show Adjust & Lock table */}
              {adjustAndLock.adjustedReady && (
                <>
                  <AdjustAndLockTable {...adjustAndLock.getTableProps()} />

                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <span>{lockedEmployeeCount}/{fsEmployees.length} employee(s) locked</span>
                      {lastNormalizedAt && (
                        <span className="ml-3 text-xs text-gray-400">
                          Last normalized {new Date(lastNormalizedAt).toLocaleString('en-SG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => adjustAndLock.handleAdjust(recordNormalization)}
                        disabled={adjustAndLock.adjusting}
                        className="btn btn-sm btn-admin-outline"
                        title="Re-normalize attendance from source clock events"
                      >
                        <RefreshCw className={`w-4 h-4 ${adjustAndLock.adjusting ? 'animate-spin' : ''}`} />
                        Re-normalize
                      </button>
                      <button
                        onClick={() => setActiveStep(2)}
                        disabled={!canProceedToStep2}
                        className="btn btn-admin-primary"
                      >
                        Continue to Calculate
                        <ChevronRight className="w-4 h-4 ml-1.5" />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* First-time: no attendance data loaded yet */}
              {!adjustAndLock.adjustedReady && !adjustAndLock.adjusting && (
                <div className="text-center py-12 space-y-4">
                  <Settings className="w-16 h-16 text-gray-300 mx-auto" />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      {fsEmployees.length} employee(s) require attendance review
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Normalize and load attendance data before adjusting and locking
                    </p>
                  </div>
                  <div className="max-w-lg mx-auto text-left bg-gray-50 rounded-xl p-4 space-y-2">
                    {fsEmployees.map(emp => (
                      <div key={emp.staff_id} className="flex items-center justify-between gap-3 text-sm min-w-0">
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          <span className="font-medium text-gray-900 truncate">{emp.full_name}</span>
                          <span className="text-gray-400 flex-shrink-0">·</span>
                          <span className="text-gray-500 flex-shrink-0">{emp.staff_id}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span className="text-gray-400 text-xs whitespace-nowrap">Ends</span>
                          <span className="text-gray-500 text-xs whitespace-nowrap">
                            {formatFinalEmploymentBoundary(emp)}
                          </span>
                          <DaysUntilBadge dateStr={emp.final_working_day} isProcessed={emp.settlement_processed} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => adjustAndLock.handleAdjust(recordNormalization)}
                    className="btn btn-admin-primary"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Normalize & Load Attendance
                  </button>
                </div>
              )}

              {/* Modals for Adjust & Lock */}
              <ProgressModal
                isOpen={adjustAndLock.progressModal?.open}
                onClose={() => adjustAndLock.setProgressModal(prev => ({ ...prev, open: false }))}
                title={adjustAndLock.progressModal?.currentPhase === 0 ? 'Normalizing Attendance' : 'Loading Attendance Data'}
                phases={adjustAndLock.ADJUST_PHASES}
                currentPhase={adjustAndLock.progressModal?.phase}
                progress={adjustAndLock.progressModal?.progress}
                statusMessage={adjustAndLock.progressModal?.statusMessage}
                error={adjustAndLock.progressModal?.error}
                department={adjustAndLock.progressModal?.department}
                currentEmployee={adjustAndLock.progressModal?.currentEmployee}
                subPhase={adjustAndLock.progressModal?.subPhase}
              />
              {adjustAndLock.confirmAction && (
                <ConfirmModal
                  isOpen={true}
                  onCancel={() => adjustAndLock.setConfirmAction(null)}
                  onConfirm={adjustAndLock.handleConfirmDeleteSession}
                  title="Delete Session"
                  message={`Delete ${adjustAndLock.confirmAction.site?.work_site_name ? `the ${adjustAndLock.confirmAction.site.work_site_name} session` : 'this attendance session'} on ${adjustAndLock.formatDateDisplay(adjustAndLock.confirmAction.rec?.date)}? This cannot be undone.`}
                  confirmLabel="Delete"
                  variant="danger"
                />
              )}
            </div>
          )}

          {/* ── Step 2: Calculate Settlement ───────────────────────────── */}
          {activeStep === 2 && (() => {
            const lockedEmps = fsEmployees.filter(emp => {
              const hookEmp = adjustAndLock.employeeList.find(e => e.staff_id === emp.staff_id);
              return hookEmp?.lock_status === 'locked';
            });
            const pendingCount = lockedEmps.filter(e => !calculationResults[e.staff_id]?.success).length;
            const calculatedCount = lockedEmps.length - pendingCount;
            return (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Calculate Settlements</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {calculatedCount > 0
                      ? `${calculatedCount}/${lockedEmps.length} calculated`
                      : `${lockedEmps.length} employee(s) ready for calculation`
                    }
                    {pendingCount > 0 && calculatedCount > 0 && ` · ${pendingCount} pending`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {pendingCount > 1 && (
                    <button
                      onClick={handleBatchCalculatePending}
                      disabled={calculatingIds.size > 0}
                      className="btn btn-sm btn-admin-outline"
                    >
                      <Calculator className="w-4 h-4" />
                      Calculate All Pending ({pendingCount})
                    </button>
                  )}
                  {calculatedCount > 0 && (
                    <>
                      <button
                        onClick={handleFsExportXlsx}
                        disabled={fsExportingXlsx || !getFsPayrollRunId()}
                        className="btn btn-sm btn-admin-outline"
                        title="Export Final Settlement Report (.xlsx)"
                      >
                        {fsExportingXlsx ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileSpreadsheet className="w-4 h-4" />
                        )}
                        Excel
                      </button>
                      <button
                        onClick={handleFsExportPdf}
                        disabled={fsExportingPdf || !getFsPayrollRunId()}
                        className="btn btn-sm btn-admin-outline"
                        title="Export Final Settlement Report (.pdf)"
                      >
                        {fsExportingPdf ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <FileText className="w-4 h-4" />
                        )}
                        PDF
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Master-Detail layout */}
              <div className="flex gap-4">
                {/* LEFT: Employee list */}
                <div className={`bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden ${selectedFsPayrollId ? 'w-[55%] flex-shrink-0' : 'flex-1'}`}>
                  {/* Mobile card list */}
                  <div className="md:hidden p-4 space-y-3">
                    {lockedEmps.map(emp => {
                      const result = calculationResults[emp.staff_id];
                      const d = result?.data;
                      const isSelected = d?.employee_payroll_id && selectedFsPayrollId === d.employee_payroll_id;
                      const isCalcInProgress = calculatingIds.has(emp.staff_id);
                      return (
                        <div
                          key={emp.staff_id}
                          className={`bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full ${d?.employee_payroll_id ? 'cursor-pointer' : ''} ${isSelected ? 'bg-gray-100' : ''}`}
                          onClick={() => {
                            if (d?.employee_payroll_id) {
                              setSelectedFsPayrollId(isSelected ? null : d.employee_payroll_id);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{emp.full_name}</p>
                              <p className="text-xs text-gray-500">{emp.staff_id}</p>
                            </div>
                            <div className="flex-shrink-0">
                              {isCalcInProgress && <StatusBadge status="info" label="Calculating…" />}
                              {!isCalcInProgress && !result && <StatusBadge status="pending" label="Pending" />}
                              {!isCalcInProgress && result?.success && <StatusBadge status="completed" label="Calculated" />}
                              {!isCalcInProgress && result && !result.success && <StatusBadge status="error" label="Failed" />}
                            </div>
                          </div>
                          <div className="mt-3 space-y-2 flex-1">
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Department</span>
                              <DepartmentTradeChips
                                department={emp.department_name}
                                trade={emp.trade_display}
                                className="items-end"
                              />
                            </div>
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Employment Ends</span>
                              <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">
                                {formatFinalEmploymentBoundary(emp)}
                              </span>
                            </div>
                            <div className="flex items-start justify-between gap-3 text-sm">
                              <span className="text-gray-500 flex-shrink-0">Final Payout</span>
                              <span className="text-gray-900 text-right truncate min-w-0 font-semibold tabular-nums">
                                {d ? formatCurrency(d.final_payment) : '—'}
                              </span>
                            </div>
                          </div>
                          <div className="card-action-footer" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => handleCalculateOne(emp)}
                              disabled={isCalcInProgress}
                              className={`btn btn-sm ${
                                result?.success
                                  ? 'btn-admin-outline'
                                  : 'btn-admin-primary'
                              }`}
                            >
                              {isCalcInProgress ? (
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              ) : result?.success ? (
                                <RefreshCw className="w-3.5 h-3.5" />
                              ) : (
                                <Calculator className="w-3.5 h-3.5" />
                              )}
                              {result?.success ? 'Recalculate' : 'Calculate'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Employee</th>
                          <th className="table-header-cell">Department</th>
                          <th className="table-header-cell text-right">Employment Ends</th>
                          <th className="table-header-cell text-center">Status</th>
                          <th className="table-header-cell text-right">Final Payout</th>
                          <th className="table-header-cell text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {lockedEmps.map(emp => {
                            const result = calculationResults[emp.staff_id];
                            const d = result?.data;
                            const isSelected = d?.employee_payroll_id && selectedFsPayrollId === d.employee_payroll_id;
                            const isCalcInProgress = calculatingIds.has(emp.staff_id);
                            return (
                              <tr
                                key={emp.staff_id}
                                className={`table-row-dense ${d?.employee_payroll_id ? 'cursor-pointer' : ''} ${isSelected ? 'bg-gray-100' : ''}`}
                                onClick={() => {
                                  if (d?.employee_payroll_id) {
                                    setSelectedFsPayrollId(isSelected ? null : d.employee_payroll_id);
                                  }
                                }}
                              >
                                <td className="table-cell">
                                  <div className="text-sm font-medium text-gray-900">{emp.full_name}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{emp.staff_id}</div>
                                </td>
                                <td className="table-cell">
                                  <DepartmentTradeChips
                                    department={emp.department_name}
                                    trade={emp.trade_display}
                                  />
                                </td>
                                <td className="table-cell text-right">
                                  <div className="text-sm text-gray-900">
                                    {formatFinalEmploymentBoundary(emp)}
                                  </div>
                                </td>
                                <td className="table-cell text-center">
                                  {isCalcInProgress && <StatusBadge status="info" label="Calculating…" />}
                                  {!isCalcInProgress && !result && <StatusBadge status="pending" label="Pending" />}
                                  {!isCalcInProgress && result?.success && <StatusBadge status="completed" label="Calculated" />}
                                  {!isCalcInProgress && result && !result.success && <StatusBadge status="error" label="Failed" />}
                                </td>
                                <td className="table-cell text-right">
                                  {d ? (
                                    <span className="text-sm font-semibold text-gray-900">
                                      {formatCurrency(d.final_payment)}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-400">—</span>
                                  )}
                                </td>
                                <td className="table-cell text-center" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleCalculateOne(emp)}
                                    disabled={isCalcInProgress}
                                    className={`btn btn-sm ${
                                      result?.success
                                        ? 'btn-admin-outline'
                                        : 'btn-admin-primary'
                                    }`}
                                  >
                                    {isCalcInProgress ? (
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                    ) : result?.success ? (
                                      <RefreshCw className="w-3.5 h-3.5" />
                                    ) : (
                                      <Calculator className="w-3.5 h-3.5" />
                                    )}
                                    {result?.success ? 'Recalculate' : 'Calculate'}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* RIGHT: Detail Panel */}
                {selectedFsPayrollId && (
                  <div className="w-[45%] flex-shrink-0 bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
                    {!fsPayrollDetail ? (
                      <div className="flex-1 flex items-center justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        {/* Employee Header */}
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <h4 className="text-base font-semibold text-gray-900 truncate">
                                {fsPayrollDetail.employee_first_name} {fsPayrollDetail.employee_last_name}
                              </h4>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className="text-xs text-gray-500">
                                  {fsPayrollDetail.employee_staff_id}
                                </span>
                                {(fsPayrollDetail.employee_department_name
                                  || fsPayrollDetail.employee_trade_category) && (
                                  <DepartmentTradeChips
                                    department={fsPayrollDetail.employee_department_name}
                                    trade={fsPayrollDetail.employee_trade_category}
                                    layout="inline"
                                    size="xs"
                                  />
                                )}
                                {fsPayrollDetail.employee_employment_pass_type && (
                                  <InfoBadge
                                    color={['Singapore Citizen', 'Permanent Resident'].includes(fsPayrollDetail.employee_employment_pass_type) ? 'blue' : 'orange'}
                                    label={fsPayrollDetail.employee_employment_pass_type}
                                  />
                                )}
                                <InfoBadge color="red" label="Final Settlement" />
                              </div>
                              {(fsPayrollDetail.payroll_run?.period_start_date && (fsPayrollDetail.employee_employment_end_date || fsPayrollDetail.payroll_run?.period_end_date)) && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {formatDateRange(fsPayrollDetail.payroll_run.period_start_date, fsPayrollDetail.employee_employment_end_date || fsPayrollDetail.payroll_run.period_end_date)}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <PayrollLockButton
                                isFinalized={fsPayrollDetail.is_finalized}
                                disabled={
                                  fsPayrollDetail.is_finalized
                                    ? fsUnfinalizePayrollMutation.isPending
                                    : fsFinalizePayrollMutation.isPending
                                }
                                onLock={() => fsFinalizePayrollMutation.mutate(fsPayrollDetail.id, {
                                  onSuccess: () => refetchFsPayrollDetail()
                                })}
                                onUnlock={() => {
                                  setUnfinalizeReason('');
                                  setShowUnfinalizeModal(true);
                                }}
                              />
                              <button
                                onClick={() => setShowFsAdjustmentModal(true)}
                                disabled={fsPayrollDetail.is_finalized}
                                title="Adjust Payroll"
                                aria-label="Adjust Payroll"
                                className="icon-button-admin"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setSelectedFsPayrollId(null)}
                                title="Close detail panel"
                                aria-label="Close detail panel"
                                className="icon-button-admin"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Payroll Details - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-6">
                          <EmployeePayrollBreakdown payroll={fsPayrollDetail} formatCurrency={formatCurrency} />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Adjustment Modal */}
                <PayrollAdjustmentModal
                  isOpen={showFsAdjustmentModal}
                  onClose={() => setShowFsAdjustmentModal(false)}
                  payrollData={fsPayrollDetail}
                  formatCurrency={formatCurrency}
                  onSuccess={() => refetchFsPayrollDetail()}
                />

                {/* Unfinalize Confirmation Modal */}
                <ConfirmModal
                  isOpen={showUnfinalizeModal}
                  title="Unlock payroll"
                  confirmLabel="Unlock"
                  variant="danger"
                  isLoading={fsUnfinalizePayrollMutation.isPending}
                  onCancel={() => setShowUnfinalizeModal(false)}
                  onConfirm={() => {
                    if (!unfinalizeReason.trim()) return;
                    fsUnfinalizePayrollMutation.mutate({ payrollId: fsPayrollDetail.id, reason: unfinalizeReason.trim() }, {
                      onSuccess: () => {
                        setShowUnfinalizeModal(false);
                        refetchFsPayrollDetail();
                      }
                    });
                  }}
                >
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                      This will unlock the payroll record for <span className="font-semibold">{fsPayrollDetail?.employee_first_name} {fsPayrollDetail?.employee_last_name}</span> and allow further edits.
                    </p>
                    <div>
                      <label className="form-label-admin">Reason <span className="text-red-500">*</span></label>
                      <textarea
                        value={unfinalizeReason}
                        onChange={(e) => setUnfinalizeReason(e.target.value)}
                        placeholder="Why is this payroll being unlocked?"
                        className="textarea-admin w-full text-sm"
                        rows={2}
                        autoFocus
                      />
                    </div>
                  </div>
                </ConfirmModal>
              </div>

              {/* Gate button: proceed to Step 3 */}
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setActiveStep(1)}
                  className="btn btn-sm btn-admin-outline"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Adjust & Lock
                </button>
                <button
                  onClick={() => setActiveStep(3)}
                  disabled={!canProceedToStep3}
                  className="btn btn-admin-primary"
                >
                  Continue to Review
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
            );
          })()}

          {/* ── Step 3: Review & Process ───────────────────────────────── */}
          {activeStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Review & Process</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Review settlement breakdowns and process payments
                </p>
              </div>

              {/* Master-Detail layout */}
              <div className="flex gap-4">
                {/* LEFT: Employee list */}
                <div className={`bg-white rounded-2xl border border-gray-200 flex flex-col overflow-hidden ${selectedFsPayrollId ? 'w-[55%] flex-shrink-0' : 'flex-1'}`}>
                  {/* Mobile card list */}
                  <div className="md:hidden p-4 space-y-3">
                    {Object.entries(calculationResults)
                      .filter(([, r]) => r.success)
                      .map(([staffId, result]) => {
                        const emp = result.employee;
                        const calc = result.data;
                        const isSelected = calc.employee_payroll_id && selectedFsPayrollId === calc.employee_payroll_id;
                        const daysLeft = getDaysUntilDue(calc.payment_due_date);
                        return (
                          <div
                            key={staffId}
                            className={`bg-white border border-gray-200 rounded-xl p-4 flex flex-col h-full cursor-pointer ${isSelected ? 'bg-gray-100' : ''}`}
                            onClick={() => {
                              if (calc.employee_payroll_id) {
                                setSelectedFsPayrollId(isSelected ? null : calc.employee_payroll_id);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{emp.full_name}</p>
                                <p className="text-xs text-gray-500">{emp.staff_id}</p>
                              </div>
                              {calc.payment_due_date && (
                                <div className="flex-shrink-0">
                                  <StatusBadge
                                    status={daysLeft <= 0 ? 'error' : daysLeft <= 3 ? 'warning' : 'info'}
                                    label={daysLeft <= 0 ? 'Overdue' : `${daysLeft}d`}
                                  />
                                </div>
                              )}
                            </div>
                            <div className="mt-3 space-y-2 flex-1">
                              <div className="flex items-start justify-between gap-3 text-sm">
                                <span className="text-gray-500 flex-shrink-0">Department</span>
                                <DepartmentTradeChips
                                  department={emp.department_name}
                                  trade={emp.trade_display}
                                  className="items-end"
                                />
                              </div>
                              <div className="flex items-start justify-between gap-3 text-sm">
                                <span className="text-gray-500 flex-shrink-0">Employment Ends</span>
                                <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">
                                  {formatFinalEmploymentBoundary(emp)}
                                </span>
                              </div>
                              <div className="flex items-start justify-between gap-3 text-sm">
                                <span className="text-gray-500 flex-shrink-0">Final Payout</span>
                                <span className="text-gray-900 text-right truncate min-w-0 font-semibold tabular-nums">
                                  {formatCurrency(calc.final_payment)}
                                </span>
                              </div>
                              {calc.payment_due_date && (
                                <div className="flex items-start justify-between gap-3 text-sm">
                                  <span className="text-gray-500 flex-shrink-0">MOM Deadline</span>
                                  <span className="text-gray-900 text-right truncate min-w-0 tabular-nums">{formatDate(calc.payment_due_date)}</span>
                                </div>
                              )}
                            </div>
                            <div className="card-action-footer" onClick={e => e.stopPropagation()}>
                              <button
                                onClick={() => openProcessModal(emp)}
                                className="btn btn-sm btn-admin-primary"
                              >
                                Process
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Employee</th>
                          <th className="table-header-cell">Department</th>
                          <th className="table-header-cell text-right">Employment Ends</th>
                          <th className="table-header-cell text-right">Final Payout</th>
                          <th className="table-header-cell text-right">MOM Deadline</th>
                          <th className="table-header-cell text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody className="table-body">
                        {Object.entries(calculationResults)
                          .filter(([, r]) => r.success)
                          .map(([staffId, result]) => {
                            const emp = result.employee;
                            const calc = result.data;
                            const isSelected = calc.employee_payroll_id && selectedFsPayrollId === calc.employee_payroll_id;
                            const daysLeft = getDaysUntilDue(calc.payment_due_date);
                            return (
                              <tr
                                key={staffId}
                                className={`table-row-dense cursor-pointer ${isSelected ? 'bg-gray-100' : ''}`}
                                onClick={() => {
                                  if (calc.employee_payroll_id) {
                                    setSelectedFsPayrollId(isSelected ? null : calc.employee_payroll_id);
                                  }
                                }}
                              >
                                <td className="table-cell">
                                  <div className="text-sm font-medium text-gray-900">{emp.full_name}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{emp.staff_id}</div>
                                </td>
                                <td className="table-cell">
                                  <DepartmentTradeChips
                                    department={emp.department_name}
                                    trade={emp.trade_display}
                                  />
                                </td>
                                <td className="table-cell text-right">
                                  <div className="text-sm text-gray-900">
                                    {formatFinalEmploymentBoundary(emp)}
                                  </div>
                                </td>
                                <td className="table-cell text-right">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {formatCurrency(calc.final_payment)}
                                  </span>
                                </td>
                                <td className="table-cell text-right">
                                  {calc.payment_due_date && (
                                    <div className="flex items-center justify-end gap-1.5">
                                      <span className="text-xs text-gray-500">{formatDate(calc.payment_due_date)}</span>
                                      <StatusBadge
                                        status={daysLeft <= 0 ? 'error' : daysLeft <= 3 ? 'warning' : 'info'}
                                        label={daysLeft <= 0 ? 'Overdue' : `${daysLeft}d`}
                                      />
                                    </div>
                                  )}
                                </td>
                                <td className="table-cell text-center" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => openProcessModal(emp)}
                                    className="btn btn-sm btn-admin-primary"
                                  >
                                    Process
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* RIGHT: Detail Panel */}
                {selectedFsPayrollId && (
                  <div className="w-[45%] flex-shrink-0 bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col">
                    {!fsPayrollDetail ? (
                      <div className="flex-1 flex items-center justify-center py-12">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <>
                        {/* Employee Header */}
                        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="min-w-0 flex-1">
                              <h4 className="text-base font-semibold text-gray-900 truncate">
                                {fsPayrollDetail.employee_first_name} {fsPayrollDetail.employee_last_name}
                              </h4>
                              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                <span className="text-xs text-gray-500">
                                  {fsPayrollDetail.employee_staff_id}
                                </span>
                                {(fsPayrollDetail.employee_department_name
                                  || fsPayrollDetail.employee_trade_category) && (
                                  <DepartmentTradeChips
                                    department={fsPayrollDetail.employee_department_name}
                                    trade={fsPayrollDetail.employee_trade_category}
                                    layout="inline"
                                    size="xs"
                                  />
                                )}
                                {fsPayrollDetail.employee_employment_pass_type && (
                                  <InfoBadge
                                    color={['Singapore Citizen', 'Permanent Resident'].includes(fsPayrollDetail.employee_employment_pass_type) ? 'blue' : 'orange'}
                                    label={fsPayrollDetail.employee_employment_pass_type}
                                  />
                                )}
                                <InfoBadge color="red" label="Final Settlement" />
                              </div>
                              {(fsPayrollDetail.payroll_run?.period_start_date && (fsPayrollDetail.employee_employment_end_date || fsPayrollDetail.payroll_run?.period_end_date)) && (
                                <div className="text-xs text-gray-400 mt-1">
                                  {formatDateRange(fsPayrollDetail.payroll_run.period_start_date, fsPayrollDetail.employee_employment_end_date || fsPayrollDetail.payroll_run.period_end_date)}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <PayrollLockButton
                                isFinalized={fsPayrollDetail.is_finalized}
                                disabled={
                                  fsPayrollDetail.is_finalized
                                    ? fsUnfinalizePayrollMutation.isPending
                                    : fsFinalizePayrollMutation.isPending
                                }
                                onLock={() => fsFinalizePayrollMutation.mutate(fsPayrollDetail.id, {
                                  onSuccess: () => refetchFsPayrollDetail()
                                })}
                                onUnlock={() => {
                                  setUnfinalizeReason('');
                                  setShowUnfinalizeModal(true);
                                }}
                              />
                              <button
                                onClick={() => setShowFsAdjustmentModal(true)}
                                disabled={fsPayrollDetail.is_finalized}
                                title="Adjust Payroll"
                                aria-label="Adjust Payroll"
                                className="icon-button-admin"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setSelectedFsPayrollId(null)}
                                title="Close detail panel"
                                aria-label="Close detail panel"
                                className="icon-button-admin"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Payroll Details - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-6">
                          <EmployeePayrollBreakdown payroll={fsPayrollDetail} formatCurrency={formatCurrency} />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Back button */}
              <div className="flex items-center">
                <button
                  onClick={() => setActiveStep(2)}
                  className="btn btn-sm btn-admin-outline"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Calculation
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Processing Modal */}
      {showProcessModal && reviewEmployee && (
        <Portal>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Process Final Settlement</h3>
                  <button
                    onClick={() => setShowProcessModal(false)}
                    disabled={processing}
                    className="icon-button-admin"
                    title="Close"
                    aria-label="Close"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {reviewEmployee.full_name} · {reviewEmployee.staff_id}
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date *</label>
                  <DatePickerField
                    theme="admin"
                    selected={parseLocalDate(paymentDate)}
                    onChange={(date) => setPaymentDate(date ? toLocalISODate(date) : '')}
                    maxDate={new Date()}
                    isClearable
                    placeholderText="Select date"
                    disabled={processing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Reference *</label>
                  <input
                    type="text"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="e.g., Bank Transfer REF123456"
                    className="input-admin w-full"
                    disabled={processing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Settlement Notes (Optional)</label>
                  <textarea
                    value={settlementNotes}
                    onChange={(e) => setSettlementNotes(e.target.value)}
                    placeholder="Additional notes about the settlement..."
                    rows={3}
                    className="textarea-admin w-full"
                    disabled={processing}
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">Confirm Settlement Processing</p>
                      <p className="mt-1">
                        This will create a final settlement record and update the offboarding workflow.
                        Ensure payment has been made before processing.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t bg-gray-50 flex gap-3 btn-row-spread">
                <button
                  onClick={() => setShowProcessModal(false)}
                  disabled={processing}
                  className="btn btn-admin-outline flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={processSettlement}
                  disabled={processing}
                  className="btn btn-admin-primary flex-1"
                >
                  {processing ? 'Processing...' : 'Confirm & Process'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
};

const OvertimeManagementTab = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/employees/');
      const employeesWithHR = await Promise.all(
        response.data.results.map(async (employee) => {
          try {
            const hrResponse = await api.get(`/confidential/hr-profiles/${employee.staff_id}/`);
            return {
              ...employee,
              hrProfile: hrResponse.data
            };
          } catch (error) {
            return {
              ...employee,
              hrProfile: null
            };
          }
        })
      );
      setEmployees(employeesWithHR);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const getOvertimeEligibilityStatus = (employee) => {
    const hrProfile = employee.hrProfile;
    if (!hrProfile) return 'Unknown';
    
    const classification = hrProfile.hrprofile_mom_employment_classification;
    if (classification === 'workman' || classification === 'non_workman') {
      return 'Eligible';
    }
    return 'Not Eligible';
  };

  const getOvertimeEligibilityColor = (status) => {
    switch (status) {
      case 'Eligible':
        return 'bg-green-100 text-green-800';
      case 'Not Eligible':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSalaryThreshold = (employee) => {
    const hrProfile = employee.hrProfile;
    if (!hrProfile) return 'N/A';
    
    const classification = hrProfile.hrprofile_mom_employment_classification;
    if (classification === 'workman') return '$4,500';
    if (classification === 'non_workman') return '$2,600';
    return 'N/A';
  };

  const getOvertimeRateCap = (employee) => {
    const hrProfile = employee.hrProfile;
    if (!hrProfile) return 'N/A';
    
    const classification = hrProfile.hrprofile_mom_employment_classification;
    if (classification === 'workman') return 'No cap';
    if (classification === 'non_workman') return '$13.60/hour';
    return 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Overtime Management</h2>
            <p className="text-gray-600 mt-1">Manage employee overtime eligibility and MOM compliance</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Overtime Eligible</p>
              <p className="text-2xl font-bold text-green-600">
                {employees.filter(emp => getOvertimeEligibilityStatus(emp) === 'Eligible').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MOM Regulations Summary */}
      <div className="bg-blue-50 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">📋 MOM Overtime Regulations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-4">
            <h4 className="font-medium text-gray-900 mb-2">Non-workman</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Eligible if monthly basic salary ≤ $2,600</li>
              <li>• Overtime rate capped at $13.60/hour</li>
              <li>• Minimum 1.5x hourly basic rate</li>
            </ul>
          </div>
          <div className="bg-white rounded-xl p-4">
            <h4 className="font-medium text-gray-900 mb-2">Workman</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Eligible if monthly basic salary ≤ $4,500</li>
              <li>• No overtime rate cap</li>
              <li>• Minimum 1.5x hourly basic rate</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Employee List */}
      <div className="bg-white rounded-2xl shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">👥 Employee Overtime Eligibility</h3>
        </div>
        
        <div className="space-y-3">
          {employees.map((employee) => (
            <div key={employee.staff_id} className="p-6 table-row-dense border border-gray-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="font-medium text-gray-900">
                      {employee.first_name} {employee.last_name}
                    </h4>
                    <span className="text-sm text-gray-500">({employee.staff_id})</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getOvertimeEligibilityColor(getOvertimeEligibilityStatus(employee))}`}>
                      {getOvertimeEligibilityStatus(employee)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>Department: {employee.department?.department_name || 'Not assigned'}</div>
                    <div>Classification: {employee.employment_classification || 'Not specified'}</div>
                    <div>Salary Threshold: {getSalaryThreshold(employee)}</div>
                    <div>Rate Cap: {getOvertimeRateCap(employee)}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button className="icon-button-admin">
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="icon-button-admin">
                    <Edit className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PayrollManagement; 